import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import QRCode from 'react-qr-code';
import { 
  getAllEntries, clearEntries, getSettings, saveSettings, 
  getOfficialWinners, saveOfficialWinners, updateOfficialWinner, deleteOfficialWinner, clearOfficialWinners,
  saveAdminCredentials 
} from '../utils/db';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { Link, useNavigate } from 'react-router-dom';
import AdminAnalytics from '../components/AdminAnalytics';
import ScratchToRevealCard from '../components/ScratchToRevealCard';
import SpinWheel from '../components/SpinWheel';
import PeelToRevealCard from '../components/PeelToRevealCard';
import SlotMachine from '../components/SlotMachine';
import FlipToRevealCard from '../components/FlipToRevealCard';
import { 
  playRollSound, playWinFanfare, playTickSound, playReelStopSound, 
  playWheelSpinSound, playJackpotSound, startBackgroundMusic, stopBackgroundMusic, 
  setAudioVolumes, MUSIC_THEMES 
} from '../utils/soundEffects';
import { 
  getQuizQuestions, saveQuizQuestions, getHouseScores, saveHouseScores, 
  SCHOOL_HOUSE_TEAMS, SCHOOL_REWARD_BADGES,
  getSchoolAwards, saveSchoolAwards, DEFAULT_SCHOOL_AWARDS,
  getEmojiRiddles, saveEmojiRiddles, DEFAULT_EMOJI_RIDDLES,
  getInterHouseChampions, saveInterHouseChampions, deleteInterHouseChampion,
  announceInterHouseChampion, getCustomHouses
} from '../utils/quizData';
import QuizQuestionManagerModal, { downloadQuizExcelSample } from '../components/QuizQuestionManagerModal';
import TokenSerialManager from '../components/TokenSerialManager';
import SchoolSportsBarometerManager from '../components/SchoolSportsBarometerManager';
import StationRoutingManager, { EVENT_STATIONS } from '../components/StationRoutingManager';
import GlassPotTokenDraw from '../components/GlassPotTokenDraw';
import { getStaffUsers } from '../utils/db';

const { 
  FiUsers, FiAward, FiTrash2, FiArrowLeft, FiGift, FiStar, FiActivity, FiSettings, 
  FiLayout, FiClock, FiTarget, FiMonitor, FiDownload, FiBarChart2, FiEdit3, FiPlus, 
  FiX, FiLayers, FiColumns, FiSmartphone, FiCopy, FiArrowUpRight, FiImage, FiPlayCircle, 
  FiPauseCircle, FiMessageCircle, FiEdit2, FiShield, FiKey, FiLogOut, FiUser, FiLock,
  FiVolume2, FiVolumeX, FiShuffle, FiRefreshCw, FiPrinter, FiFilter, FiCheck, FiExternalLink, 
  FiEye, FiGrid, FiMusic, FiPlay, FiSquare, FiSliders, FiBookOpen, FiZap, FiHelpCircle, FiUpload,
  FiChevronDown, FiChevronUp
} = FiIcons;

class AdminTabErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    console.error('Admin Tab Error caught:', error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="p-8 rounded-2xl bg-amber-500/10 border border-amber-500/30 text-center space-y-4 my-4">
          <div className="w-14 h-14 mx-auto rounded-full bg-amber-500/20 text-amber-500 flex items-center justify-center text-2xl font-bold">⚠️</div>
          <h3 className="text-lg font-black text-slate-900 dark:text-white">
            {this.props.moduleName || 'Module'} encountered a temporary display issue
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md mx-auto">
            {this.state.error?.message || 'A data sync error occurred. Click below to safely reset and reload.'}
          </p>
          <div className="flex justify-center gap-3">
            <button
              type="button"
              onClick={() => this.setState({ hasError: false, error: null })}
              className="px-5 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-slate-950 font-black text-sm shadow-md transition-all cursor-pointer"
            >
              🔄 Retry Module
            </button>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="px-5 py-2.5 rounded-xl bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-800 dark:text-white font-bold text-sm transition-all cursor-pointer"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('campaign');
  const [entries, setEntries] = useState([]);
  const [settings, setSettings] = useState(getSettings());
  const [officialWinners, setOfficialWinners] = useState([]);
  const [winners, setWinners] = useState(settings?.winners || []);
  const [isSpinning, setIsSpinning] = useState(false);
  const [winnerCount, setWinnerCount] = useState(2);
  const [newPrize, setNewPrize] = useState('');
  const [bulkText, setBulkText] = useState('');
  const [isBulkMode, setIsBulkMode] = useState(false);
  
  // Generator State
  const [serialPrefix, setSerialPrefix] = useState('EVENT-');
  const [serialCount, setSerialCount] = useState(50);

  // School Modules Sub-Nav / Filter State
  const [schoolGamesFilter, setSchoolGamesFilter] = useState('all'); // 'all' | 'projector' | 'stall' | 'junior' | 'stem'
  const [schoolMenuExpanded, setSchoolMenuExpanded] = useState(true); // Minimize / Expand School Submenu
  const [stationMenuExpanded, setStationMenuExpanded] = useState(true); // Live Mode & Station Routing Submenu
  const [staffCount, setStaffCount] = useState(getStaffUsers().length);
  const [activeEmojiCategory, setActiveEmojiCategory] = useState('space'); // 'space' | 'heritage' | 'animals' | 'sports' | 'mystery'

  // Edit Winner Modal State
  const [editingWinner, setEditingWinner] = useState(null);

  // NEW: Credentials Form State
  const [credForm, setCredForm] = useState({ username: '', password: '' });

  // NEW: Draw & Mode Settings Enhanced States
  const [adminTimeLeft, setAdminTimeLeft] = useState(null);
  const [showStationHub, setShowStationHub] = useState(false);
  const [previewGameMode, setPreviewGameMode] = useState(null);
  const [previewResult, setPreviewResult] = useState('');
  const [previewSelectedPrize, setPreviewSelectedPrize] = useState('');
  const [previewResetKey, setPreviewResetKey] = useState(0);
  const [isTestingBgm, setIsTestingBgm] = useState(false);
  const [customWinnerCount, setCustomWinnerCount] = useState('');

  const openGamePreview = (modeId, specificPrize = null) => {
    const activePrizes = settings?.prizes?.length 
      ? settings.prizes 
      : ["₹50 Discount", "Free Premium Gift", "Better Luck Next Time", "Free Product Sample", "10% Cashback"];
    const prizeToUse = specificPrize || activePrizes[Math.floor(Math.random() * activePrizes.length)];
    setPreviewSelectedPrize(prizeToUse);
    setPreviewResult('');
    setPreviewResetKey(Date.now());
    setPreviewGameMode(modeId);
  };

  // School & Live Quiz Arena States
  const [quizQuestions, setQuizQuestions] = useState(getQuizQuestions());
  const [adminHouseScores, setAdminHouseScores] = useState(getHouseScores());
  const [showAddQuizModal, setShowAddQuizModal] = useState(false);
  const [quizModalTab, setQuizModalTab] = useState('upload'); // 'upload' | 'manual' | 'bank'
  const [newQuizForm, setNewQuizForm] = useState({
    category: 'Science & Space',
    type: 'trivia',
    question: '',
    options: ['', '', '', ''],
    correctIndex: 0,
    fact: '',
    title: '',
    clues: ['', '', ''],
    answer: ''
  });

  // School Awards & Physical Merchandise State
  const [schoolAwards, setSchoolAwards] = useState(getSchoolAwards());
  const [awardTitleInput, setAwardTitleInput] = useState('');
  const [awardItemInput, setAwardItemInput] = useState('');
  const [awardEmojiInput, setAwardEmojiInput] = useState('🎖️');

  const handleAddSchoolAward = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!awardTitleInput.trim()) return alert("Please enter an award title!");
    const newAward = {
      id: 'award_' + Date.now(),
      emoji: awardEmojiInput || '🎖️',
      title: awardTitleInput.trim(),
      item: awardItemInput.trim() || awardTitleInput.trim()
    };
    const updated = [...schoolAwards, newAward];
    setSchoolAwards(updated);
    saveSchoolAwards(updated);
    setAwardTitleInput('');
    setAwardItemInput('');
    playCorrectChime();
  };

  const handleQuickAddAward = (preset) => {
    const newAward = {
      id: 'award_' + Date.now() + '_' + Math.random().toString(36).substr(2, 4),
      emoji: preset.emoji,
      title: preset.title,
      item: preset.item
    };
    const updated = [...schoolAwards, newAward];
    setSchoolAwards(updated);
    saveSchoolAwards(updated);
    playTickSound(800, 0.05);
  };

  const handleDeleteSchoolAward = (id) => {
    if (window.confirm("Remove this award from the school physical reward list?")) {
      const updated = schoolAwards.filter(a => a.id !== id);
      setSchoolAwards(updated);
      saveSchoolAwards(updated);
    }
  };

  const handleResetSchoolAwards = () => {
    if (window.confirm("Reset all physical awards to default school badges & caps?")) {
      setSchoolAwards(DEFAULT_SCHOOL_AWARDS);
      saveSchoolAwards(DEFAULT_SCHOOL_AWARDS);
    }
  };

  // Inter-House Champions Roll of Honor State
  const [interHouseChampions, setInterHouseChampions] = useState(getInterHouseChampions());
  const [showManualChampModal, setShowManualChampModal] = useState(false);
  const [champFilterHouse, setChampFilterHouse] = useState('ALL');
  const [manualChampForm, setManualChampForm] = useState({
    houseId: 'red',
    awardType: '🏆 Overall Annual Inter-House Championship Trophy',
    customAward: '',
    points: 100,
    eventSource: 'Official Inter-House Championship',
    prizeDistributionInfo: 'Trophy, Gold Medals & Certificates presented to winning House on Stage',
    announcedBy: 'Chief Sports Referee / Principal'
  });

  const handleManualAnnounceChampion = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const housesList = getCustomHouses();
    const targetHouse = housesList.find(h => h.id === manualChampForm.houseId) || housesList[0];
    const finalAward = manualChampForm.awardType === 'CUSTOM' ? (manualChampForm.customAward.trim() || 'Official Championship Trophy') : manualChampForm.awardType;

    announceInterHouseChampion({
      houseId: targetHouse.id,
      houseName: targetHouse.name,
      badge: targetHouse.badge,
      points: Number(manualChampForm.points) || 100,
      awardType: finalAward,
      eventSource: manualChampForm.eventSource.trim() || 'Official Inter-House Championship',
      prizeDistributionInfo: manualChampForm.prizeDistributionInfo.trim() || 'Trophy, Medals & Certificates awarded to winning House on Stage',
      announcedBy: manualChampForm.announcedBy.trim() || 'Chief Sports Referee / Principal'
    });

    setInterHouseChampions(getInterHouseChampions());
    setShowManualChampModal(false);
    playWinFanfare();
  };

  const handleDeleteChampionRecord = (id) => {
    if (window.confirm("Remove this champion announcement from the Inter-House Champions Roll of Honor?")) {
      deleteInterHouseChampion(id);
      setInterHouseChampions(getInterHouseChampions());
      playTickSound(450, 0.08);
    }
  };

  // Live Emoji & Visual Riddle Quest State
  const [emojiRiddles, setEmojiRiddles] = useState(getEmojiRiddles());
  const [newRiddleForm, setNewRiddleForm] = useState({
    category: 'Science & Space',
    emojis: '🚀 🌕 🇮🇳 🛰️',
    image: '',
    answer: '',
    optionA: '',
    optionB: '',
    optionC: '',
    optionD: '',
    correctIndex: 0,
    hint: '',
    funFact: ''
  });

  const handleRiddleImageFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      alert("Please upload an image smaller than 2MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = (uploadEvent) => {
      setNewRiddleForm(prev => ({ ...prev, image: uploadEvent.target.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleAddEmojiRiddle = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!newRiddleForm.answer.trim()) return alert("Please enter the answer for the riddle!");
    if (!newRiddleForm.optionA.trim() || !newRiddleForm.optionB.trim()) {
      return alert("Please enter at least Option A and Option B!");
    }

    const rawTrimmed = (newRiddleForm.emojis || '').trim();
    let parsedEmojis = [];
    if (rawTrimmed.includes(' ')) {
      parsedEmojis = rawTrimmed.split(/\s+/).filter(Boolean);
    } else if (rawTrimmed.length > 0) {
      if (typeof Intl !== 'undefined' && Intl.Segmenter) {
        const segmenter = new Intl.Segmenter('en', { granularity: 'grapheme' });
        parsedEmojis = Array.from(segmenter.segment(rawTrimmed), s => s.segment).filter(s => s.trim().length > 0);
      } else {
        parsedEmojis = Array.from(rawTrimmed).filter(s => s.trim().length > 0);
      }
    }
    if (parsedEmojis.length === 0) parsedEmojis = ['🧩', '❓', '✨', '💡'];

    const newRiddle = {
      id: 'custom_riddle_' + Date.now(),
      category: newRiddleForm.category || 'General Knowledge',
      emojis: parsedEmojis,
      emojiClues: parsedEmojis.map((em, idx) => `Clue ${idx + 1}`),
      image: newRiddleForm.image.trim(),
      answer: newRiddleForm.answer.trim(),
      options: [
        { text: newRiddleForm.optionA.trim(), emoji: parsedEmojis[0] || '🎯' },
        { text: newRiddleForm.optionB.trim(), emoji: parsedEmojis[1] || '🎯' },
        { text: (newRiddleForm.optionC.trim() || 'Other Choice'), emoji: parsedEmojis[2] || '🎯' },
        { text: (newRiddleForm.optionD.trim() || 'Alternative'), emoji: parsedEmojis[3] || '🎯' }
      ],
      correctIndex: Number(newRiddleForm.correctIndex) || 0,
      hint: newRiddleForm.hint.trim() || 'Look closely at the clue emojis to guess the answer!',
      funFact: newRiddleForm.funFact.trim() || 'Great job discovering this visual answer!'
    };

    const updated = [...emojiRiddles, newRiddle];
    setEmojiRiddles(updated);
    saveEmojiRiddles(updated);
    setNewRiddleForm({
      category: 'Science & Space',
      emojis: '🚀 🌕 🇮🇳 🛰️',
      image: '',
      answer: '',
      optionA: '',
      optionB: '',
      optionC: '',
      optionD: '',
      correctIndex: 0,
      hint: '',
      funFact: ''
    });
    playCorrectChime();
    alert("New Emoji & Visual Riddle created successfully!");
  };

  const handleDeleteEmojiRiddle = (id) => {
    if (window.confirm("Delete this riddle from Live Emoji Riddle Quest?")) {
      const updated = emojiRiddles.filter(r => r.id !== id);
      setEmojiRiddles(updated);
      saveEmojiRiddles(updated);
    }
  };

  const handleResetEmojiRiddles = () => {
    if (window.confirm("Reset all riddles to default curated Indian heritage & science riddles?")) {
      setEmojiRiddles(DEFAULT_EMOJI_RIDDLES);
      saveEmojiRiddles(DEFAULT_EMOJI_RIDDLES);
    }
  };

  const navigate = useNavigate();
  const baseUrl = window.location.origin + window.location.pathname;
  const isRandomMode = settings?.modeBehavior === 'random';
  const gameUrl = isRandomMode ? `${baseUrl}#/play` : `${baseUrl}#/${settings?.activeGame || 'scratch'}`;

  // Live in-admin countdown timer ticker
  useEffect(() => {
    if (!settings?.drawTime) {
      setAdminTimeLeft(null);
      return;
    }
    const updateTime = () => {
      const diff = +new Date(settings.drawTime) - +new Date();
      if (diff > 0) {
        setAdminTimeLeft({
          hours: Math.floor((diff / (1000 * 60 * 60)) % 24) + Math.floor(diff / (1000 * 60 * 60 * 24)) * 24,
          minutes: Math.floor((diff / 1000 / 60) % 60),
          seconds: Math.floor((diff / 1000) % 60),
          isExpired: false
        });
      } else {
        setAdminTimeLeft({ hours: 0, minutes: 0, seconds: 0, isExpired: true });
      }
    };
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, [settings?.drawTime]);

  useEffect(() => {
    const handleSync = () => {
      loadEntries();
      loadOfficialWinners();
      setSettings(getSettings());
      setInterHouseChampions(getInterHouseChampions());
    };
    handleSync();
    window.addEventListener('settingsUpdated', handleSync);
    window.addEventListener('interHouseChampionsUpdated', handleSync);
    window.addEventListener('storage', handleSync);
    return () => {
      window.removeEventListener('settingsUpdated', handleSync);
      window.removeEventListener('interHouseChampionsUpdated', handleSync);
      window.removeEventListener('storage', handleSync);
    };
  }, []);

  const loadEntries = () => {
    const data = getAllEntries();
    data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    setEntries(data);
  };

  const loadOfficialWinners = () => {
    setOfficialWinners(getOfficialWinners());
  };

  const handleSettingsUpdate = (key, value) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    saveSettings(newSettings);
  };

  const addPrize = () => {
    if (newPrize.trim() === '') return;
    const currentPrizes = settings?.prizes || [];
    handleSettingsUpdate('prizes', [...currentPrizes, newPrize.trim()]);
    setNewPrize('');
  };

  const handleBulkAdd = () => {
    if (!bulkText.trim()) return;
    const prizesToAdd = bulkText
      .split(/,|\n/)
      .map(p => p.trim())
      .filter(p => p.length > 0);
    if (prizesToAdd.length > 0) {
      const currentPrizes = settings?.prizes || [];
      handleSettingsUpdate('prizes', [...currentPrizes, ...prizesToAdd]);
      setBulkText('');
      setIsBulkMode(false);
    }
  };

  const addQuickTemplate = (prize) => {
    const currentPrizes = settings?.prizes || [];
    if (!currentPrizes.includes(prize)) {
      handleSettingsUpdate('prizes', [...currentPrizes, prize]);
    }
  };

  const removePrize = (index) => {
    const updated = (settings?.prizes || []).filter((_, i) => i !== index);
    handleSettingsUpdate('prizes', updated);
  };

  const handleClear = () => {
    if (window.confirm("Are you sure you want to clear all entries? This cannot be undone.")) {
      clearEntries();
      setEntries([]);
      setWinners([]);
      handleSettingsUpdate('winners', []);
      handleSettingsUpdate('resultsPublished', false);
    }
  };

  const exportCSV = () => {
    if (entries.length === 0) return alert("No data to export.");
    const headers = ['Name', 'Mobile', 'Receipt/Ticket', 'Game Type', 'Reward Won', 'Date'];
    const rows = entries.map(e => 
      `"${e.name}", "${e.mobile}", "${e.receiptNumber || ''}", "${e.gameType}", "${e.reward}", "${new Date(e.createdAt).toLocaleString()}"`
    );
    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `campaign_leads_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const exportToCSV = (dataList, filenamePrefix = 'export') => {
    if (!dataList || dataList.length === 0) return alert("No data to export.");
    const headers = ['Student Name', 'Parent Mobile', 'Class/Roll', 'Reward Won', 'Claim Code', 'Date'];
    const rows = dataList.map(e => 
      `"${e.name}", "${e.mobile}", "${e.receiptNumber || ''}", "${e.reward}", "${e.couponCode || ''}", "${new Date(e.createdAt).toLocaleString()}"`
    );
    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filenamePrefix}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // Filter eligible entries based on active Mega Draw rule
  const getEligibleEntries = () => {
    const filter = settings?.megaDrawFilter || 'all';
    if (filter === 'unique') {
      const seen = new Set();
      return entries.filter(e => {
        const cleanPhone = (e.mobile || '').replace(/[^0-9]/g, '');
        if (!cleanPhone || seen.has(cleanPhone)) return false;
        seen.add(cleanPhone);
        return true;
      });
    }
    if (filter === 'scratch') return entries.filter(e => e.gameType === 'Scratch Card');
    if (filter === 'spin') return entries.filter(e => e.gameType === 'Spin Wheel');
    if (filter === 'peel') return entries.filter(e => e.gameType === 'Peel & Reveal');
    if (filter === 'slot') return entries.filter(e => e.gameType === 'Slot Machine');
    if (filter === 'flip') return entries.filter(e => e.gameType === 'Flip Card');
    return entries;
  };

  const pickRandomWinners = () => {
    const eligible = getEligibleEntries();
    const countToPick = Number(winnerCount) || 1;

    if (eligible.length < countToPick) {
      return alert(`Not enough eligible entries! Current filter has ${eligible.length} participants, but ${countToPick} requested.`);
    }
    
    setIsSpinning(true);
    setWinners([]);
    handleSettingsUpdate('resultsPublished', false);

    if (settings?.soundEnabled !== false) {
      playRollSound(2400);
    }

    let count = 0;
    const maxSpins = 20;

    const interval = setInterval(() => {
      const tempWinners = [];
      const tempEntries = [...eligible];
      
      for(let i = 0; i < countToPick; i++) {
        const randomIdx = Math.floor(Math.random() * tempEntries.length);
        const picked = tempEntries[randomIdx];
        const assignedPrize = (settings?.megaDrawPrizes && settings.megaDrawPrizes[i]) || picked.reward || 'Mega Reward';
        tempWinners.push({ ...picked, megaPrize: assignedPrize });
        tempEntries.splice(randomIdx, 1);
      }
      
      setWinners(tempWinners);
      count++;

      if (count >= maxSpins) {
        clearInterval(interval);
        setIsSpinning(false);
        if (settings?.soundEnabled !== false) {
          playWinFanfare();
        }
      }
    }, 120);
  };

  const reRollWinner = (indexToReplace) => {
    const eligible = getEligibleEntries();
    const currentWinnerKeys = winners.map(w => w.id || (w.name + w.mobile));
    const remainingPool = eligible.filter(e => !currentWinnerKeys.includes(e.id || (e.name + e.mobile)));

    if (remainingPool.length === 0) {
      return alert("No remaining eligible entries in the pool to re-roll from!");
    }

    if (settings?.soundEnabled !== false) {
      playRollSound(800);
    }

    const replacement = remainingPool[Math.floor(Math.random() * remainingPool.length)];
    const assignedPrize = (settings?.megaDrawPrizes && settings.megaDrawPrizes[indexToReplace]) || replacement.reward || 'Mega Reward';
    const updatedWinners = [...winners];
    updatedWinners[indexToReplace] = { ...replacement, megaPrize: assignedPrize };
    setWinners(updatedWinners);

    if (settings?.resultsPublished) {
      handleSettingsUpdate('winners', updatedWinners);
    }
  };

  const printWinnerSheet = () => {
    if (winners.length === 0) return alert("Please draw winners first!");
    const printWindow = window.open('', '_blank');
    if (!printWindow) return alert("Popup blocked. Please allow popups to print winner sheet.");

    const rowsHtml = winners.map((w, idx) => {
      const prizeName = w.megaPrize || (settings?.megaDrawPrizes && settings.megaDrawPrizes[idx]) || w.reward || 'Special Prize';
      return `
        <tr>
          <td style="padding: 12px; border-bottom: 1px solid #e2e8f0; font-weight: bold; color: #d97706;">#${idx + 1}</td>
          <td style="padding: 12px; border-bottom: 1px solid #e2e8f0; font-weight: bold;">${w.name}</td>
          <td style="padding: 12px; border-bottom: 1px solid #e2e8f0;">${w.mobile}</td>
          <td style="padding: 12px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #059669;">${prizeName}</td>
          <td style="padding: 12px; border-bottom: 1px solid #e2e8f0; font-family: monospace;">${w.receiptNumber || 'N/A'}</td>
          <td style="padding: 12px; border-bottom: 1px solid #e2e8f0;">${w.gameType || 'Mega Draw'}</td>
        </tr>
      `;
    }).join('');

    const content = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${settings?.campaignName || 'Campaign'} - Official Winners Sheet</title>
          <style>
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 40px; color: #0f172a; }
            .header { text-align: center; border-bottom: 2px solid #e2e8f0; padding-bottom: 20px; margin-bottom: 24px; }
            h1 { margin: 0 0 8px 0; font-size: 26px; }
            p { margin: 0; color: #64748b; font-size: 14px; }
            table { width: 100%; border-collapse: collapse; margin-top: 16px; }
            th { background: #f8fafc; text-align: left; padding: 12px; border-bottom: 2px solid #cbd5e1; font-size: 12px; text-transform: uppercase; color: #475569; }
            .footer { margin-top: 40px; text-align: center; font-size: 12px; color: #94a3b8; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>${settings?.campaignName || 'Mega Festive Bonanza'}</h1>
            <p>Official Mega Draw Announced Winners &bull; Generated on ${new Date().toLocaleString()}</p>
          </div>
          <table>
            <thead>
              <tr>
                <th>Rank</th>
                <th>Winner Name</th>
                <th>Mobile Number</th>
                <th>Prize</th>
                <th>Ticket / Receipt</th>
                <th>Game Mode</th>
              </tr>
            </thead>
            <tbody>
              ${rowsHtml}
            </tbody>
          </table>
          <div class="footer">
            <p>GamifyCenter Event Control System &bull; Verified Draw Report</p>
          </div>
          <script>
            window.onload = function() { window.print(); }
          </script>
        </body>
      </html>
    `;
    printWindow.document.write(content);
    printWindow.document.close();
  };

  const setTimerPreset = (minutes) => {
    const target = new Date(Date.now() + minutes * 60 * 1000).toISOString();
    handleSettingsUpdate('drawTime', target);
  };

  const setTimerTonight8PM = () => {
    const target = new Date();
    target.setHours(20, 0, 0, 0);
    if (target.getTime() <= Date.now()) {
      target.setDate(target.getDate() + 1);
    }
    handleSettingsUpdate('drawTime', target.toISOString());
  };

  const publishResults = () => {
    if (winners.length === 0) return alert("Please draw winners first!");
    const newSettings = { ...settings, winners, resultsPublished: true };
    setSettings(newSettings);
    saveSettings(newSettings);

    // Save to the 7-day official winners log
    saveOfficialWinners(winners);
    loadOfficialWinners();

    alert("Results published to the Main Display Screen successfully!");
  };

  const resetDisplay = () => {
    const newSettings = { ...settings, resultsPublished: false, winners: [] };
    setSettings(newSettings);
    saveSettings(newSettings);
    setWinners([]);
  };

  // --- Official Winners Actions ---
  const handleDeleteOfficialWinner = (id) => {
    if(window.confirm("Are you sure you want to delete this winner from the official history?")) {
      deleteOfficialWinner(id);
      loadOfficialWinners();
    }
  };

  const handleClearOfficialWinners = () => {
    if(window.confirm("Are you sure you want to clear all announced winners? This will reset the Official Winners Directory so participants can be drawn again.")) {
      clearOfficialWinners();
      loadOfficialWinners();
    }
  };

  const handleSaveWinnerEdit = () => {
    if (!editingWinner) return;
    updateOfficialWinner(editingWinner.announcedId, {
      name: editingWinner.name,
      mobile: editingWinner.mobile,
      reward: editingWinner.reward
    });
    setEditingWinner(null);
    loadOfficialWinners();
  };

  const sendWhatsApp = (winner) => {
    const phone = winner.mobile.replace(/[^0-9]/g, ''); // strip non-numeric
    const text = encodeURIComponent(`Hi ${winner.name}! Congratulations, you have won "${winner.reward}" in our ${settings?.campaignName || 'event'}. Please contact us to claim your prize!`);
    window.open(`https://wa.me/${phone}?text=${text}`, '_blank');
  };

  // --- Fraud & Security Tools ---
  const executeGenerateSerials = () => {
    if(!serialPrefix || serialCount < 1) return;
    const generated = [];
    for(let i=1; i<=serialCount; i++) {
      generated.push(`${serialPrefix}${i.toString().padStart(4, '0')}`);
    }
    const merged = [...new Set([...(settings.validSerialNumbers || []), ...generated])];
    handleSettingsUpdate('validSerialNumbers', merged);
    alert(`Successfully generated ${serialCount} serial numbers!`);
  };

  // --- NEW: Update Admin Credentials ---
  const handleUpdateCredentials = () => {
    if (!credForm.username.trim() || !credForm.password.trim()) {
      return alert("Username and Password cannot be empty.");
    }
    if (window.confirm("Are you sure you want to change the Admin Login Credentials?")) {
      saveAdminCredentials({
        username: credForm.username.trim(),
        password: credForm.password.trim()
      });
      alert("Admin credentials updated successfully! You can use these on your next login.");
      setCredForm({ username: '', password: '' });
    }
  };

  // Logout Handler
  const handleLogout = () => {
    localStorage.removeItem('admin_auth');
    navigate('/admin-login');
  };

  const scratchEntries = entries.filter(e => e.gameType === 'Scratch Card');
  const spinEntries = entries.filter(e => e.gameType === 'Spin Wheel');
  const peelEntries = entries.filter(e => e.gameType === 'Peel & Reveal');
  const slotEntries = entries.filter(e => e.gameType === 'Slot Machine');
  const flipEntries = entries.filter(e => e.gameType === 'Flip Card');
  const balloonEntries = entries.filter(e => e.gameType === 'Balloon Pop');
  const treasureEntries = entries.filter(e => e.gameType === 'Magic Treasure Box' || e.gameType === 'Treasure Box');
  const pinataEntries = entries.filter(e => e.gameType === 'Candy Piñata');
  const juniorEntries = entries.filter(e => e.gameType === 'Animal Safari Match' || e.gameType === 'Rainbow Bubble Pop');
  const stemEntries = entries.filter(e => e.gameType === 'Astro-Engineer Rocket' || e.gameType === 'Cosmic Gravity Lab' || e.gameType === 'Magic Color Lab');

  // Modular Sub-Navigation Bar for School & Event modules
  const renderSchoolModuleNav = () => (
    <div className="p-2 rounded-2xl bg-white dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700/80 shadow-sm flex flex-wrap items-center justify-between gap-2 mb-6">
      <div className="flex flex-wrap items-center gap-1.5">
        <button
          type="button"
          onClick={() => setActiveTab('school_quiz')}
          className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer ${
            activeTab === 'school_quiz'
              ? 'bg-amber-500 text-slate-950 font-black shadow-sm'
              : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
          }`}
        >
          <span>🎮</span>
          <span>Games & Kiosks (14)</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('school_barometer')}
          className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer ${
            activeTab === 'school_barometer'
              ? 'bg-amber-500 text-slate-950 font-black shadow-sm'
              : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
          }`}
        >
          <SafeIcon icon={FiUsers} className="text-sm text-indigo-500" />
          <span>House Championship Barometer</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('school_champions')}
          className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer ${
            activeTab === 'school_champions'
              ? 'bg-amber-500 text-slate-950 font-black shadow-sm'
              : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
          }`}
        >
          <span>🏆</span>
          <span>Inter-House Champions ({(interHouseChampions || []).length})</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('school_awards')}
          className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer ${
            activeTab === 'school_awards'
              ? 'bg-amber-500 text-slate-950 font-black shadow-sm'
              : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
          }`}
        >
          <SafeIcon icon={FiAward} className="text-sm text-amber-500" />
          <span>Physical Awards & Tokens ({(schoolAwards || []).length})</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('school_qbank')}
          className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer ${
            activeTab === 'school_qbank'
              ? 'bg-amber-500 text-slate-950 font-black shadow-sm'
              : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
          }`}
        >
          <SafeIcon icon={FiHelpCircle} className="text-sm text-indigo-500" />
          <span>Curated Q&A Bank ({(quizQuestions || []).length})</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('school_riddles')}
          className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer ${
            activeTab === 'school_riddles'
              ? 'bg-purple-600 text-white font-black shadow-sm'
              : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
          }`}
        >
          <span className="text-base">🧩</span>
          <span>Emoji Riddle Quest ({(emojiRiddles || []).length})</span>
        </button>
      </div>

      <div className="hidden xl:flex items-center gap-2 pr-2">
        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Module:</span>
        <span className="px-2.5 py-1 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20 text-[10px] font-black uppercase">
          School & Event Suite
        </span>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200 font-sans flex flex-col transition-colors duration-300">
      
      {/* Edit Winner Modal */}
      <AnimatePresence>
        {editingWinner && (
          <motion.div 
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm"
          >
            <motion.div 
              initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
              className="bg-white dark:bg-slate-800 rounded-2xl p-6 w-full max-w-md shadow-2xl border border-slate-200 dark:border-slate-700"
            >
              <h3 className="text-xl font-bold mb-6 text-slate-800 dark:text-white flex items-center gap-2">
                <SafeIcon icon={FiEdit2} className="text-indigo-500" /> Edit Winner Details
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Name</label>
                  <input type="text" value={editingWinner.name} onChange={e => setEditingWinner({...editingWinner, name: e.target.value})} className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 outline-none text-slate-800 dark:text-white" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Mobile Contact</label>
                  <input type="text" value={editingWinner.mobile} onChange={e => setEditingWinner({...editingWinner, mobile: e.target.value})} className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 outline-none text-slate-800 dark:text-white" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Reward Won</label>
                  <input type="text" value={editingWinner.reward} onChange={e => setEditingWinner({...editingWinner, reward: e.target.value})} className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 outline-none text-slate-800 dark:text-white" />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-8">
                <button onClick={() => setEditingWinner(null)} className="px-4 py-2 rounded-lg text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors font-medium">
                  Cancel
                </button>
                <button onClick={handleSaveWinnerEdit} className="px-6 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white transition-colors font-medium">
                  Save Changes
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <div className="bg-white dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800 px-6 py-4 flex items-center justify-between sticky top-0 z-50 transition-colors">
        <div className="flex items-center gap-6">
          <Link to="/" className="text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white transition-colors bg-slate-100 dark:bg-slate-800/50 p-2 rounded-lg border border-slate-200 dark:border-slate-700">
            <SafeIcon icon={FiArrowLeft} />
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-500/20">
              <SafeIcon icon={FiLayout} className="text-white text-xl" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900 dark:text-white leading-none">Event Control Center</h1>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Manage campaigns & live displays</p>
            </div>
          </div>
        </div>
        
        <div className="flex gap-3">
          <button 
            onClick={() => handleSettingsUpdate('campaignStatus', settings?.campaignStatus === 'active' ? 'paused' : 'active')}
            className={`px-4 py-2 rounded-lg border transition-colors flex items-center gap-2 text-sm font-medium ${settings?.campaignStatus === 'active' ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/20 hover:bg-emerald-100 dark:hover:bg-emerald-500/20' : 'bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-200 dark:border-amber-500/20 hover:bg-amber-100 dark:hover:bg-amber-500/20'}`}
          >
            <SafeIcon icon={settings?.campaignStatus === 'active' ? FiPauseCircle : FiPlayCircle} />
            {settings?.campaignStatus === 'active' ? 'Pause Campaign' : 'Resume Campaign'}
          </button>
          <button onClick={exportCSV} className="px-4 py-2 rounded-lg bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-500/20 hover:bg-indigo-100 dark:hover:bg-indigo-500/20 transition-colors flex items-center gap-2 text-sm font-medium">
            <SafeIcon icon={FiDownload} /> Export CSV
          </button>
          <button onClick={handleClear} className="px-4 py-2 rounded-lg bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-500/20 hover:bg-red-100 dark:hover:bg-red-500/20 transition-colors flex items-center gap-2 text-sm font-medium">
            <SafeIcon icon={FiTrash2} /> Clear Data
          </button>
          <button onClick={handleLogout} className="px-4 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 transition-colors flex items-center gap-2 text-sm font-medium">
            <SafeIcon icon={FiLogOut} /> Logout
          </button>
        </div>
      </div>

      {/* Executive Quick Stats Strip */}
      <div className="bg-white/80 dark:bg-slate-950/80 backdrop-blur-md border-b border-slate-200 dark:border-slate-800 px-6 py-2.5 transition-colors">
        <div className="max-w-[1400px] mx-auto flex flex-wrap items-center justify-between gap-3 text-xs">
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-bold text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-full ${settings?.campaignStatus === 'active' ? 'bg-emerald-500 animate-pulse' : 'bg-amber-500'}`}></span>
              {settings?.campaignName || 'Event Gamify Center'}
            </span>
            <span className="text-slate-300 dark:text-slate-700">|</span>
            <span className="text-slate-500 dark:text-slate-400">
              Status: <strong className={settings?.campaignStatus === 'active' ? 'text-emerald-600 dark:text-emerald-400 font-bold' : 'text-amber-500 font-bold'}>
                {settings?.campaignStatus === 'active' ? 'LIVE & ACTIVE' : 'PAUSED'}
              </strong>
            </span>
            <span className="text-slate-300 dark:text-slate-700">|</span>
            <span className="text-slate-500 dark:text-slate-400">
              Total Visitors / Leads: <strong className="text-indigo-600 dark:text-indigo-400 font-mono font-bold">{entries.length}</strong>
            </span>
            <span className="text-slate-300 dark:text-slate-700">|</span>
            <span className="text-slate-500 dark:text-slate-400">
              Dynamic Pool: <strong className="text-purple-600 dark:text-purple-400 font-mono font-bold">{(settings?.prizes || []).length} Prizes</strong>
            </span>
            <span className="text-slate-300 dark:text-slate-700">|</span>
            <span className="text-slate-500 dark:text-slate-400">
              Token Gate: {settings?.requireSerialNumber ? (
                <strong className="text-emerald-600 dark:text-emerald-400 font-bold">ENFORCED ({(settings?.validSerialNumbers || []).length} Tokens)</strong>
              ) : (
                <strong className="text-slate-400 font-bold">OPEN ACCESS</strong>
              )}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <Link 
              to="/game/spin" 
              target="_blank"
              className="text-[11px] font-bold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 flex items-center gap-1 bg-indigo-50 dark:bg-indigo-950/40 px-2 py-1 rounded-md border border-indigo-200 dark:border-indigo-800 transition-colors"
            >
              <span>Launch Live Game</span>
              <SafeIcon icon={FiExternalLink} className="text-xs" />
            </Link>
          </div>
        </div>
      </div>

      <div className="flex-1 max-w-[1400px] w-full mx-auto p-6 md:p-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        {/* Left Sidebar */}
        <div className="lg:col-span-3 space-y-2">
          <h3 className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-4 px-3">Menu</h3>
          
          <button onClick={() => setActiveTab('campaign')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all font-medium ${activeTab === 'campaign' ? 'bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-500/20' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800/50 border border-transparent dark:hover:text-white'}`}>
            <SafeIcon icon={FiEdit3} /> Campaign Builder
          </button>
          
          <button onClick={() => setActiveTab('branding')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all font-medium ${activeTab === 'branding' ? 'bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-500/20' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800/50 border border-transparent dark:hover:text-white'}`}>
            <SafeIcon icon={FiImage} /> Branding & Display
          </button>
          
          <button onClick={() => setActiveTab('allocation')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all font-medium ${activeTab === 'allocation' ? 'bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-500/20' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800/50 border border-transparent dark:hover:text-white'}`}>
            <SafeIcon icon={FiSettings} /> Draw & Mode Settings
          </button>

          <button onClick={() => setActiveTab('security')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all font-medium ${activeTab === 'security' ? 'bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-500/20' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800/50 border border-transparent dark:hover:text-white'}`}>
            <SafeIcon icon={FiShield} /> Security & Fraud
          </button>

          <button onClick={() => setActiveTab('analytics')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all font-medium ${activeTab === 'analytics' ? 'bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-500/20' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800/50 border border-transparent dark:hover:text-white'}`}>
            <SafeIcon icon={FiBarChart2} /> Analytics Dashboard
          </button>

          {/* Live Mode & Station Routing Hub (Segregated Operations) */}
          <div className="space-y-1">
            <div className={`w-full flex items-center justify-between px-3 py-2 rounded-xl transition-all font-bold ${
              ['station_routing', 'staff_stations'].includes(activeTab) 
                ? 'bg-blue-600/10 text-blue-600 dark:text-blue-400 border border-blue-500/30' 
                : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800/50 border border-transparent dark:hover:text-white'
            }`}>
              <button 
                type="button"
                onClick={() => {
                  setActiveTab('station_routing');
                  setStationMenuExpanded(true);
                }} 
                className="flex-1 flex items-center justify-between text-left pr-2 cursor-pointer"
              >
                <div className="flex items-center gap-2.5">
                  <span className="text-base">🎪</span>
                  <span className="text-xs sm:text-sm font-bold">Live Mode & Station Routing</span>
                </div>
                <span className="bg-blue-600 text-white font-black text-[9px] px-1.5 py-0.5 rounded-full">LIVE</span>
              </button>

              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setStationMenuExpanded(!stationMenuExpanded);
                }}
                className="p-1 rounded-lg text-slate-400 hover:text-blue-500 hover:bg-blue-500/20 transition-all cursor-pointer"
                title={stationMenuExpanded ? "Minimize Station Menu" : "Expand Station Menu"}
              >
                <SafeIcon icon={stationMenuExpanded ? FiChevronUp : FiChevronDown} className="text-base" />
              </button>
            </div>

            <AnimatePresence initial={false}>
              {stationMenuExpanded && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden pl-4 pr-1 py-1 space-y-1 border-l-2 border-blue-500/30 ml-4"
                >
                  <button
                    onClick={() => setActiveTab('station_routing')}
                    className={`w-full flex items-center justify-between px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                      activeTab === 'station_routing'
                        ? 'bg-blue-600 text-white font-black shadow-sm'
                        : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800/60 dark:hover:text-white'
                    }`}
                  >
                    <span className="flex items-center gap-2"><span>📍</span> Event Stations</span>
                    <span className="text-[10px] opacity-75">{EVENT_STATIONS.length}</span>
                  </button>

                  <button
                    onClick={() => setActiveTab('staff_stations')}
                    className={`w-full flex items-center justify-between px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                      activeTab === 'staff_stations'
                        ? 'bg-blue-600 text-white font-black shadow-sm'
                        : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800/60 dark:hover:text-white'
                    }`}
                  >
                    <span className="flex items-center gap-2"><span>👥</span> Staff Allocations</span>
                    <span className="text-[10px] opacity-75 font-mono">{staffCount}</span>
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Transparent Glass Pot Token Mega Draw */}
          <button 
            onClick={() => setActiveTab('glass_token_draw')} 
            className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all font-medium cursor-pointer ${
              activeTab === 'glass_token_draw' 
                ? 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/40 shadow-sm' 
                : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800/50 border border-transparent dark:hover:text-white'
            }`}
          >
            <div className="flex items-center gap-3">
              <span className="text-lg">🏺</span>
              <span className="text-xs sm:text-sm font-bold">Glass Pot Token Draw</span>
            </div>
            <span className="bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-950 font-black text-[9px] px-2 py-0.5 rounded-full uppercase tracking-wider">
              3rd ➔ 1st
            </span>
          </button>

          <button onClick={() => setActiveTab('official_winners')} className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all font-medium ${activeTab === 'official_winners' ? 'bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-500/20' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800/50 border border-transparent dark:hover:text-white'}`}>
            <div className="flex items-center gap-3"><SafeIcon icon={FiAward} /> Announced Winners</div>
            <span className="bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-[10px] px-2 py-1 rounded-full">{officialWinners.length}</span>
          </button>

          {/* School Quiz & Games Arena Menu with Separated Sub-Modules */}
          <div className="space-y-1">
            <div className={`w-full flex items-center justify-between px-3 py-2 rounded-xl transition-all font-bold ${
              ['school_quiz', 'school_barometer', 'school_champions', 'school_awards', 'school_qbank', 'school_riddles'].includes(activeTab) 
                ? 'bg-amber-500/10 text-amber-500 border border-amber-500/30' 
                : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800/50 border border-transparent dark:hover:text-white'
            }`}>
              <button 
                type="button"
                onClick={() => {
                  setActiveTab('school_quiz');
                  setSchoolMenuExpanded(true);
                }} 
                className="flex-1 flex items-center justify-between text-left pr-2 cursor-pointer"
              >
                <div className="flex items-center gap-2.5">
                  <SafeIcon icon={FiBookOpen} /> 
                  <span className="text-xs sm:text-sm font-bold">School Quiz & Games</span>
                </div>
                <span className="bg-amber-500 text-slate-950 font-black text-[9px] px-1.5 py-0.5 rounded-full">ARENA</span>
              </button>

              {/* Minimize / Expand Toggle Button */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setSchoolMenuExpanded(!schoolMenuExpanded);
                }}
                className="p-1 rounded-lg text-slate-400 hover:text-amber-500 hover:bg-amber-500/20 transition-all cursor-pointer"
                title={schoolMenuExpanded ? "Minimize School Submodules" : "Expand School Submodules"}
                aria-label={schoolMenuExpanded ? "Minimize School Submodules" : "Expand School Submodules"}
              >
                <SafeIcon icon={schoolMenuExpanded ? FiChevronUp : FiChevronDown} className="text-base" />
              </button>
            </div>

            {/* Sub-menu items for instant access to each section */}
            <AnimatePresence initial={false}>
              {schoolMenuExpanded && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden pl-4 pr-1 py-1 space-y-1 border-l-2 border-amber-500/30 ml-4"
                >
                  <button
                    onClick={() => setActiveTab('school_quiz')}
                    className={`w-full flex items-center justify-between px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                      activeTab === 'school_quiz'
                        ? 'bg-amber-500 text-slate-950 font-black shadow-sm'
                        : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800/60 dark:hover:text-white'
                    }`}
                  >
                    <span className="flex items-center gap-2"><span>🎮</span> Games & Kiosks</span>
                    <span className="text-[10px] opacity-75">14</span>
                  </button>

                  <button
                    onClick={() => setActiveTab('school_barometer')}
                    className={`w-full flex items-center justify-between px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                      activeTab === 'school_barometer'
                        ? 'bg-amber-500 text-slate-950 font-black shadow-sm'
                        : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800/60 dark:hover:text-white'
                    }`}
                  >
                    <span className="flex items-center gap-2"><span>🏆</span> House Barometer</span>
                    <span className="text-[10px] opacity-75 font-mono">Live</span>
                  </button>

                  <button
                    onClick={() => setActiveTab('school_champions')}
                    className={`w-full flex items-center justify-between px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                      activeTab === 'school_champions'
                        ? 'bg-amber-500 text-slate-950 font-black shadow-sm'
                        : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800/60 dark:hover:text-white'
                    }`}
                  >
                    <span className="flex items-center gap-2"><span>🏆</span> Inter-House Champions</span>
                    <span className="text-[10px] opacity-75 font-mono">{(interHouseChampions || []).length}</span>
                  </button>

                  <button
                    onClick={() => setActiveTab('school_awards')}
                    className={`w-full flex items-center justify-between px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                      activeTab === 'school_awards'
                        ? 'bg-amber-500 text-slate-950 font-black shadow-sm'
                        : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800/60 dark:hover:text-white'
                    }`}
                  >
                    <span className="flex items-center gap-2"><span>🎁</span> Physical Awards</span>
                    <span className="text-[10px] opacity-75">{(schoolAwards || []).length}</span>
                  </button>

                  <button
                    onClick={() => setActiveTab('school_qbank')}
                    className={`w-full flex items-center justify-between px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                      activeTab === 'school_qbank'
                        ? 'bg-amber-500 text-slate-950 font-black shadow-sm'
                        : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800/60 dark:hover:text-white'
                    }`}
                  >
                    <span className="flex items-center gap-2"><span>📚</span> Q&A Bank (Excel)</span>
                    <span className="text-[10px] opacity-75">{(quizQuestions || []).length}</span>
                  </button>

                  <button
                    onClick={() => setActiveTab('school_riddles')}
                    className={`w-full flex items-center justify-between px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                      activeTab === 'school_riddles'
                        ? 'bg-purple-600 text-white font-black shadow-sm'
                        : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800/60 dark:hover:text-white'
                    }`}
                  >
                    <span className="flex items-center gap-2"><span>🧩</span> Emoji Riddle Quest</span>
                    <span className="text-[10px] opacity-75">{(emojiRiddles || []).length}</span>
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="my-4 border-t border-slate-200 dark:border-slate-800"></div>
          
          <button onClick={() => setActiveTab('scratch_entries')} className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all font-medium ${activeTab === 'scratch_entries' ? 'bg-orange-50 dark:bg-orange-500/10 text-orange-600 dark:text-orange-400 border border-orange-200 dark:border-orange-500/20' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800/50 border border-transparent dark:hover:text-white'}`}>
            <div className="flex items-center gap-3"><SafeIcon icon={FiGift} /> Scratch Cards</div>
            <span className="bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-[10px] px-2 py-1 rounded-full">{scratchEntries.length}</span>
          </button>
          
          <button onClick={() => setActiveTab('spin_entries')} className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all font-medium ${activeTab === 'spin_entries' ? 'bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-500/20' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800/50 border border-transparent dark:hover:text-white'}`}>
            <div className="flex items-center gap-3"><SafeIcon icon={FiTarget} /> Spin Wheels</div>
            <span className="bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-[10px] px-2 py-1 rounded-full">{spinEntries.length}</span>
          </button>
          
          <button onClick={() => setActiveTab('peel_entries')} className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all font-medium ${activeTab === 'peel_entries' ? 'bg-purple-50 dark:bg-purple-500/10 text-purple-600 dark:text-purple-400 border border-purple-200 dark:border-purple-500/20' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800/50 border border-transparent dark:hover:text-white'}`}>
            <div className="flex items-center gap-3"><SafeIcon icon={FiLayers} /> Peel & Reveal</div>
            <span className="bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-[10px] px-2 py-1 rounded-full">{peelEntries.length}</span>
          </button>

          <button onClick={() => setActiveTab('slot_entries')} className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all font-medium ${activeTab === 'slot_entries' ? 'bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-500/20' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800/50 border border-transparent dark:hover:text-white'}`}>
            <div className="flex items-center gap-3"><SafeIcon icon={FiColumns} /> Slot Machine</div>
            <span className="bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-[10px] px-2 py-1 rounded-full">{slotEntries.length}</span>
          </button>

          <button onClick={() => setActiveTab('flip_entries')} className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all font-medium ${activeTab === 'flip_entries' ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/20' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800/50 border border-transparent dark:hover:text-white'}`}>
            <div className="flex items-center gap-3"><SafeIcon icon={FiAward} /> Mystery Flip Card</div>
            <span className="bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-[10px] px-2 py-1 rounded-full">{flipEntries.length}</span>
          </button>

          <button onClick={() => setActiveTab('balloon_entries')} className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all font-medium ${activeTab === 'balloon_entries' ? 'bg-pink-50 dark:bg-pink-500/10 text-pink-600 dark:text-pink-400 border border-pink-200 dark:border-pink-500/20' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800/50 border border-transparent dark:hover:text-white'}`}>
            <div className="flex items-center gap-3"><span className="text-base">🎈</span> Balloon Pop Carnival</div>
            <span className="bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-[10px] px-2 py-1 rounded-full">{balloonEntries.length}</span>
          </button>

          <button onClick={() => setActiveTab('treasure_entries')} className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all font-medium ${activeTab === 'treasure_entries' ? 'bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-500/20' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800/50 border border-transparent dark:hover:text-white'}`}>
            <div className="flex items-center gap-3"><span className="text-base">🎁</span> Magic Treasure Box</div>
            <span className="bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-[10px] px-2 py-1 rounded-full">{treasureEntries.length}</span>
          </button>

          <button onClick={() => setActiveTab('pinata_entries')} className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all font-medium ${activeTab === 'pinata_entries' ? 'bg-purple-50 dark:bg-purple-500/10 text-purple-600 dark:text-purple-400 border border-purple-200 dark:border-purple-500/20' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800/50 border border-transparent dark:hover:text-white'}`}>
            <div className="flex items-center gap-3"><span className="text-base">🪅</span> Candy Piñata Bash</div>
            <span className="bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-[10px] px-2 py-1 rounded-full">{pinataEntries.length}</span>
          </button>

          <button onClick={() => setActiveTab('junior_entries')} className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all font-medium ${activeTab === 'junior_entries' ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/20' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800/50 border border-transparent dark:hover:text-white'}`}>
            <div className="flex items-center gap-3"><span className="text-base">🧸</span> Junior Champs (Ages 3-7)</div>
            <span className="bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-[10px] px-2 py-1 rounded-full">{juniorEntries.length}</span>
          </button>

          <button onClick={() => setActiveTab('stem_entries')} className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all font-medium ${activeTab === 'stem_entries' ? 'bg-cyan-50 dark:bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border border-cyan-200 dark:border-cyan-500/20' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800/50 border border-transparent dark:hover:text-white'}`}>
            <div className="flex items-center gap-3"><span className="text-base">🚀</span> Space & STEM Lab</div>
            <span className="bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-[10px] px-2 py-1 rounded-full">{stemEntries.length}</span>
          </button>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-9">
          
          {/* TAB: CAMPAIGN BUILDER (Advanced Editing) */}
          {activeTab === 'campaign' && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
              <div className="glass-panel p-8 rounded-2xl bg-white dark:bg-slate-800/40 border-slate-200 dark:border-slate-700">
                <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2 flex items-center gap-2">
                  <SafeIcon icon={FiEdit3} className="text-indigo-500 dark:text-indigo-400" /> Advanced Campaign Editor
                </h2>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-8">Customize the event name and the dynamic rewards available across all game modes.</p>
                
                <div className="space-y-8">
                  {/* Campaign Name */}
                  <div>
                    <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">Campaign Title</label>
                    <input 
                      type="text" 
                      value={settings?.campaignName || ''} 
                      onChange={(e) => handleSettingsUpdate('campaignName', e.target.value)}
                      className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all text-lg"
                      placeholder="e.g. Mega Festive Bonanza"
                    />
                  </div>

                  {/* Rewards Editor */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">Dynamic Reward Pool</label>
                      <button 
                        type="button" 
                        onClick={() => setIsBulkMode(!isBulkMode)} 
                        className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1"
                      >
                        <SafeIcon icon={isBulkMode ? FiEdit3 : FiLayers} /> {isBulkMode ? "Switch to Single Add" : "Bulk Import List"}
                      </button>
                    </div>
                    <p className="text-xs text-slate-500 mb-4">Add the prizes that users can randomly win. Items containing "Luck" or "Try Again" are categorized as losing options.</p>
                    
                    {isBulkMode ? (
                      <div className="space-y-3 mb-6 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
                        <textarea
                          rows={4}
                          value={bulkText}
                          onChange={(e) => setBulkText(e.target.value)}
                          className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white rounded-xl p-3 outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                          placeholder="Paste prizes here, separated by commas or new lines (e.g. Prize 1, Prize 2, Try Again)"
                        />
                        <div className="flex justify-end gap-2">
                          <button 
                            type="button" 
                            onClick={() => setIsBulkMode(false)} 
                            className="px-4 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg text-xs font-medium hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                          >
                            Cancel
                          </button>
                          <button 
                            type="button" 
                            onClick={handleBulkAdd} 
                            className="px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg text-xs font-medium transition-colors"
                          >
                            Import List
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex gap-2 mb-4">
                        <input 
                          type="text" 
                          value={newPrize} 
                          onChange={(e) => setNewPrize(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && addPrize()}
                          className="flex-1 bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500 outline-none"
                          placeholder="e.g. 50% Off Coupon"
                        />
                        <button 
                          onClick={addPrize}
                          className="bg-indigo-500 hover:bg-indigo-600 text-white px-5 py-2 rounded-xl transition-colors font-medium flex items-center gap-2"
                        >
                          <SafeIcon icon={FiPlus} /> Add
                        </button>
                      </div>
                    )}

                    {/* Quick Templates Row */}
                    <div className="mb-6 flex flex-wrap items-center gap-2">
                      <span className="text-[11px] font-bold text-slate-400 uppercase mr-1">Quick Add:</span>
                      <button type="button" onClick={() => addQuickTemplate("Better Luck Next Time")} className="text-xs px-2.5 py-1 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-750 text-slate-600 dark:text-slate-300 rounded-lg border border-slate-200 dark:border-slate-700 transition-all font-medium">
                        Better Luck
                      </button>
                      <button type="button" onClick={() => addQuickTemplate("Try Again Tomorrow")} className="text-xs px-2.5 py-1 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-750 text-slate-600 dark:text-slate-300 rounded-lg border border-slate-200 dark:border-slate-700 transition-all font-medium">
                        Try Again
                      </button>
                      <button type="button" onClick={() => addQuickTemplate("10% Discount Coupon")} className="text-xs px-2.5 py-1 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-750 text-slate-600 dark:text-slate-300 rounded-lg border border-slate-200 dark:border-slate-700 transition-all font-medium">
                        10% Off
                      </button>
                      <button type="button" onClick={() => addQuickTemplate("Free Premium Gift")} className="text-xs px-2.5 py-1 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-750 text-slate-600 dark:text-slate-300 rounded-lg border border-slate-200 dark:border-slate-700 transition-all font-medium">
                        Free Gift
                      </button>
                    </div>

                    <div className="bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-xl p-4 flex flex-wrap gap-3 min-h-[100px]">
                      {(!settings?.prizes || settings.prizes.length === 0) ? (
                        <div className="w-full text-center text-slate-500 py-4 text-sm">No prizes added. Users will see a default message.</div>
                      ) : (
                        settings.prizes.map((prize, idx) => {
                          const isLoss = prize.toLowerCase().includes('luck') || prize.toLowerCase().includes('try') || prize.toLowerCase().includes('miss');
                          const pct = ((1 / settings.prizes.length) * 100).toFixed(0);
                          return (
                            <div 
                              key={idx} 
                              className={`border px-3 py-2 rounded-xl flex items-center gap-3 text-sm shadow-sm transition-all ${
                                isLoss 
                                  ? 'bg-slate-100/80 border-slate-300 text-slate-700 dark:bg-slate-800/80 dark:border-slate-700 dark:text-slate-300' 
                                  : 'bg-emerald-50/80 border-emerald-300 text-emerald-800 dark:bg-emerald-950/20 dark:border-emerald-900/40 dark:text-emerald-300'
                              }`}
                            >
                              <span className="flex items-center gap-1.5 font-semibold">
                                <span className={`w-1.5 h-1.5 rounded-full ${isLoss ? 'bg-slate-400' : 'bg-emerald-500'}`}></span>
                                {prize}
                              </span>
                              
                              <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono font-bold ${
                                isLoss ? 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-400' : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400'
                              }`}>
                                {pct}% chance
                              </span>

                              <button onClick={() => removePrize(idx)} className="text-slate-400 hover:text-red-500 dark:hover:text-red-400 transition-colors ml-1">
                                <SafeIcon icon={FiX} />
                              </button>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>

                  {/* Active Event Game Engines Synchronization Status */}
                  <div className="pt-6 border-t border-slate-200 dark:border-slate-700/80">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                      <div>
                        <h3 className="text-sm font-bold text-slate-800 dark:text-white flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse"></span>
                          Dynamic Reward Pool Synchronized Across All 5 Game Modes
                        </h3>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                          Prizes added above automatically populate and power outcomes for all active interactive games with mathematically distributed odds.
                        </p>
                      </div>
                      <span className="text-[11px] font-bold px-3 py-1 rounded-full bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-400 border border-emerald-300 dark:border-emerald-800 self-start sm:self-auto">
                        5 / 5 Engines Live Synced
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                      {[
                        { id: 'scratch', name: 'Scratch & Win', desc: 'Hidden foil scratch reveal', icon: FiGift, color: 'text-orange-500', bg: 'bg-orange-500/10 border-orange-300 dark:border-orange-500/30' },
                        { id: 'spin', name: 'Spin the Wheel', desc: 'Dynamic wheel slices & odds', icon: FiTarget, color: 'text-blue-500', bg: 'bg-blue-500/10 border-blue-300 dark:border-blue-500/30' },
                        { id: 'peel', name: 'Peel & Reveal', desc: 'Interactive sticker peeling', icon: FiLayers, color: 'text-purple-500', bg: 'bg-purple-500/10 border-purple-300 dark:border-purple-500/30' },
                        { id: 'slot', name: 'Slot Machine', desc: '3-reel casino jackpot match', icon: FiColumns, color: 'text-red-500', bg: 'bg-red-500/10 border-red-300 dark:border-red-500/30' },
                        { id: 'flip', name: 'Mystery Flip Card', desc: '3D perspective card flip', icon: FiAward, color: 'text-emerald-500', bg: 'bg-emerald-500/10 border-emerald-300 dark:border-emerald-500/30' }
                      ].map((gm) => (
                        <div 
                          key={gm.id}
                          className={`p-3 rounded-xl border flex flex-col justify-between transition-all ${gm.bg}`}
                        >
                          <div>
                            <div className="flex items-center justify-between mb-1.5">
                              <SafeIcon icon={gm.icon} className={`text-base ${gm.color}`} />
                              <span className="text-[9px] font-black uppercase tracking-wider text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/80 px-1.5 py-0.5 rounded border border-emerald-200 dark:border-emerald-900">
                                Synced
                              </span>
                            </div>
                            <h4 className="font-bold text-xs text-slate-800 dark:text-slate-100">{gm.name}</h4>
                            <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5 leading-tight">{gm.desc}</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => openGamePreview(gm.id)}
                            className="mt-3 w-full py-1.5 text-[10px] font-bold bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-750 text-slate-700 dark:text-slate-300 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm transition-all flex items-center justify-center gap-1"
                          >
                            <SafeIcon icon={FiEye} /> Test Pool
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                </div>
              </div>
            </motion.div>
          )}

          {/* TAB: BRANDING & DISPLAY (Customization Params) */}
          {activeTab === 'branding' && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
              <div className="glass-panel p-8 rounded-2xl bg-white dark:bg-slate-800/40 border-slate-200 dark:border-slate-700">
                <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2 flex items-center gap-2">
                  <SafeIcon icon={FiImage} className="text-indigo-500 dark:text-indigo-400" /> Branding & Display Parameters
                </h2>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-8">Customize images, logos, and messages across the entire experience to align with your brand.</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Media Parameters */}
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">Brand Logo URL</label>
                      <p className="text-xs text-slate-500 mb-2">Transparent PNG recommended. Rendered on the Home screen and top of all games.</p>
                      <input 
                        type="url" 
                        value={settings?.brandLogo || ''} 
                        onChange={(e) => handleSettingsUpdate('brandLogo', e.target.value)}
                        className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all text-sm"
                        placeholder="https://example.com/logo.png"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">Background Image URL</label>
                      <p className="text-xs text-slate-500 mb-2">High-quality image for the main display and game backgrounds.</p>
                      <input 
                        type="url" 
                        value={settings?.bgImage || ''} 
                        onChange={(e) => handleSettingsUpdate('bgImage', e.target.value)}
                        className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all text-sm"
                        placeholder="https://images.unsplash.com/..."
                      />
                    </div>
                  </div>

                  {/* Text Parameters */}
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">Home Display Subtitle</label>
                      <p className="text-xs text-slate-500 mb-2">The instruction text shown on the main display screen below the title.</p>
                      <textarea 
                        value={settings?.displaySubtitle || ''} 
                        onChange={(e) => handleSettingsUpdate('displaySubtitle', e.target.value)}
                        rows={3}
                        className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all text-sm resize-none"
                        placeholder="Scan the QR code with your smartphone..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">Victory Success Message</label>
                      <p className="text-xs text-slate-500 mb-2">Message shown to the user on their phone after they win a prize.</p>
                      <input 
                        type="text" 
                        value={settings?.successMessage || ''} 
                        onChange={(e) => handleSettingsUpdate('successMessage', e.target.value)}
                        className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all text-sm"
                        placeholder="You're officially in the Mega Draw."
                      />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* TAB: DISPLAY & ALLOCATION (REFINED DRAW & MODE SETTINGS) */}
          {activeTab === 'allocation' && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-8">
              
              {/* 1. Live Game Link, Mode Routing & Multi-Station Hub */}
              <div className="glass-panel p-6 md:p-8 rounded-2xl bg-white dark:bg-slate-800/40 border-slate-200 dark:border-slate-700 shadow-sm relative overflow-hidden">
                {settings?.campaignStatus === 'paused' && (
                  <div className="absolute inset-0 bg-white/70 dark:bg-slate-900/80 backdrop-blur-sm z-10 flex flex-col items-center justify-center text-center p-4">
                    <SafeIcon icon={FiPauseCircle} className="text-4xl text-amber-500 mb-2" />
                    <h3 className="font-bold text-slate-800 dark:text-white text-lg">Campaign is Paused</h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400 max-w-md">Resume the campaign from the top bar to enable live QR participation.</p>
                  </div>
                )}
                
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 pb-6 border-b border-slate-100 dark:border-slate-700/50">
                  <div>
                    <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                      <SafeIcon icon={FiSmartphone} className="text-indigo-500 dark:text-indigo-400" /> Live Mode & Station Routing
                    </h2>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Configure participant routing, test live games, and generate station-specific QR codes.</p>
                  </div>

                  {/* Mode Behavior Switch */}
                  <div className="flex items-center gap-2 bg-slate-100 dark:bg-slate-900 p-1 rounded-xl border border-slate-200 dark:border-slate-700 self-start md:self-auto">
                    <button
                      type="button"
                      onClick={() => handleSettingsUpdate('modeBehavior', 'fixed')}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${
                        (settings?.modeBehavior || 'fixed') === 'fixed'
                          ? 'bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 shadow-sm'
                          : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
                      }`}
                    >
                      <SafeIcon icon={FiTarget} /> Single Active Mode
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSettingsUpdate('modeBehavior', 'random')}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${
                        settings?.modeBehavior === 'random'
                          ? 'bg-gradient-to-r from-amber-500 to-pink-500 text-white shadow-sm'
                          : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
                      }`}
                    >
                      <SafeIcon icon={FiShuffle} /> Surprise Shuffle Mode
                    </button>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-center gap-6">
                  <div className={`bg-white p-3 border-2 border-slate-200 dark:border-slate-600 rounded-2xl shadow-sm flex-shrink-0 transition-opacity ${settings?.campaignStatus === 'paused' ? 'opacity-30 grayscale' : ''}`}>
                    <QRCode value={gameUrl} size={110} fgColor="#0f172a" level="M" />
                  </div>
                  
                  <div className="flex-1 w-full space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                        {settings?.modeBehavior === 'random' ? 'Dynamic Shuffle Link (Random Game)' : `Active Mode: ${settings?.activeGame?.toUpperCase() || 'SCRATCH'}`}
                      </span>
                      <span className="text-[11px] text-slate-500 font-mono">Scan on mobile</span>
                    </div>

                    <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 text-xs font-mono text-slate-600 dark:text-slate-300 break-all overflow-hidden text-ellipsis shadow-inner">
                      {gameUrl}
                    </div>

                    <div className="flex flex-wrap gap-2 pt-1">
                      <button 
                        onClick={() => {
                          navigator.clipboard.writeText(gameUrl);
                          alert('Live game link copied securely!');
                        }}
                        className="px-4 py-2.5 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 rounded-xl text-xs font-bold border border-indigo-200 dark:border-indigo-500/20 hover:bg-indigo-100 dark:hover:bg-indigo-500/20 transition-all flex items-center gap-1.5"
                      >
                        <SafeIcon icon={FiCopy} /> Copy Live Link
                      </button>

                      <a 
                        href={gameUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="px-4 py-2.5 bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600 text-white rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 shadow-sm"
                      >
                        <SafeIcon icon={FiExternalLink} /> Open Play Screen
                      </a>

                      <button 
                        onClick={() => setShowStationHub(!showStationHub)}
                        className={`px-4 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 border ${
                          showStationHub
                            ? 'bg-amber-500 text-white border-amber-600'
                            : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:bg-slate-200'
                        }`}
                      >
                        <SafeIcon icon={FiGrid} /> {showStationHub ? 'Hide Station Hub' : 'Multi-Station QR Codes (5 Stations)'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Collapsible Multi-Station QR Hub */}
                <AnimatePresence>
                  {showStationHub && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-700/60 overflow-hidden">
                      <div className="flex justify-between items-center mb-4">
                        <div>
                          <h4 className="text-sm font-bold text-slate-800 dark:text-white flex items-center gap-2">
                            <SafeIcon icon={FiGrid} className="text-indigo-500" /> Multi-Station Kiosk & Tablet Hub
                          </h4>
                          <p className="text-xs text-slate-500 dark:text-slate-400">Deploy specific game modes at individual tablets or event booths without altering the main screen mode.</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {[
                          { id: 'scratch', name: 'Scratch & Win', path: '#/scratch', badge: 'Station 1', color: 'border-orange-200 dark:border-orange-500/30' },
                          { id: 'spin', name: 'Spin the Wheel', path: '#/spin', badge: 'Station 2', color: 'border-blue-200 dark:border-blue-500/30' },
                          { id: 'peel', name: 'Peel & Reveal', path: '#/peel', badge: 'Station 3', color: 'border-purple-200 dark:border-purple-500/30' },
                          { id: 'slot', name: 'Slot Machine', path: '#/slot', badge: 'Station 4', color: 'border-red-200 dark:border-red-500/30' },
                          { id: 'flip', name: 'Mystery Flip Card', path: '#/flip', badge: 'Station 5', color: 'border-emerald-200 dark:border-emerald-500/30' },
                          { id: 'play', name: 'Surprise Shuffle Kiosk', path: '#/play', badge: 'Smart Shuffle', color: 'border-amber-200 dark:border-amber-500/30' }
                        ].map(st => {
                          const stUrl = `${baseUrl}${st.path}`;
                          return (
                            <div key={st.id} className={`p-4 rounded-xl border ${st.color} bg-slate-50/70 dark:bg-slate-900/50 flex flex-col justify-between`}>
                              <div className="flex items-center gap-3 mb-3">
                                <div className="bg-white p-1 rounded-lg border border-slate-200 shadow-sm flex-shrink-0">
                                  <QRCode value={stUrl} size={64} fgColor="#0f172a" level="L" />
                                </div>
                                <div className="overflow-hidden">
                                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{st.badge}</span>
                                  <h5 className="font-bold text-xs text-slate-800 dark:text-white truncate">{st.name}</h5>
                                  <span className="text-[10px] text-slate-500 font-mono block truncate">{st.path}</span>
                                </div>
                              </div>
                              <div className="flex gap-2 mt-2">
                                <button
                                  type="button"
                                  onClick={() => {
                                    navigator.clipboard.writeText(stUrl);
                                    alert(`${st.name} link copied!`);
                                  }}
                                  className="flex-1 py-1.5 text-[11px] font-semibold bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-slate-100 rounded-lg text-slate-700 dark:text-slate-300 transition-colors"
                                >
                                  Copy Link
                                </button>
                                <a
                                  href={stUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="px-2.5 py-1.5 text-[11px] font-semibold bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-500/20 hover:bg-indigo-100 rounded-lg transition-colors flex items-center"
                                >
                                  <SafeIcon icon={FiExternalLink} />
                                </a>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* 2. Game Mode Selector Cards & Interactive Previews */}
              <div className="glass-panel p-6 md:p-8 rounded-2xl bg-white dark:bg-slate-800/40 border-slate-200 dark:border-slate-700">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
                  <div>
                    <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                      <SafeIcon icon={FiMonitor} className="text-slate-500 dark:text-slate-400" /> Active Event Game Mode
                    </h2>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Select which game is activated on the primary live display and mobile QR flow.</p>
                  </div>
                  <span className="text-xs font-semibold px-3 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 self-start sm:self-auto border border-slate-200 dark:border-slate-700">
                    5 Interactive Game Engines
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[
                    {
                      id: 'scratch',
                      name: 'Scratch & Win',
                      desc: 'Realistic digital canvas scratch card with cursor/touch physics.',
                      icon: FiGift,
                      color: 'text-orange-500',
                      bg: 'bg-orange-500',
                      activeBorder: 'border-orange-500 ring-2 ring-orange-500/20',
                      entriesCount: scratchEntries.length
                    },
                    {
                      id: 'spin',
                      name: 'Spin the Wheel',
                      desc: 'High-tension multi-color roulette wheel with sound feedback.',
                      icon: FiTarget,
                      color: 'text-blue-500',
                      bg: 'bg-blue-500',
                      activeBorder: 'border-blue-500 ring-2 ring-blue-500/20',
                      entriesCount: spinEntries.length
                    },
                    {
                      id: 'peel',
                      name: 'Peel & Reveal',
                      desc: 'Tactile sticker-peeling physics simulation revealing secret gifts.',
                      icon: FiLayers,
                      color: 'text-purple-500',
                      bg: 'bg-purple-500',
                      activeBorder: 'border-purple-500 ring-2 ring-purple-500/20',
                      entriesCount: peelEntries.length
                    },
                    {
                      id: 'slot',
                      name: 'Slot Machine',
                      desc: 'Classic casino 3-reel spinner with spinning chimes and icons.',
                      icon: FiColumns,
                      color: 'text-red-500',
                      bg: 'bg-red-500',
                      activeBorder: 'border-red-500 ring-2 ring-red-500/20',
                      entriesCount: slotEntries.length
                    },
                    {
                      id: 'flip',
                      name: 'Mystery Flip Card',
                      desc: '3D perspective card flip with grand golden reveal animations.',
                      icon: FiAward,
                      color: 'text-emerald-500',
                      bg: 'bg-emerald-500',
                      activeBorder: 'border-emerald-500 ring-2 ring-emerald-500/20',
                      entriesCount: flipEntries.length
                    }
                  ].map(game => {
                    const isSelected = (settings?.activeGame || 'scratch') === game.id;
                    return (
                      <div
                        key={game.id}
                        onClick={() => handleSettingsUpdate('activeGame', game.id)}
                        className={`p-5 rounded-2xl border transition-all cursor-pointer relative flex flex-col justify-between ${
                          isSelected
                            ? `bg-white dark:bg-slate-800 ${game.activeBorder} shadow-md`
                            : 'bg-slate-50 dark:bg-slate-900/60 border-slate-200 dark:border-slate-700/80 hover:border-slate-300 dark:hover:border-slate-600'
                        }`}
                      >
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2.5">
                              <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-lg ${isSelected ? `${game.bg} text-white shadow-sm` : 'bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-400'}`}>
                                <SafeIcon icon={game.icon} />
                              </div>
                              <div>
                                <h4 className={`font-bold text-sm leading-tight ${isSelected ? 'text-slate-900 dark:text-white' : 'text-slate-700 dark:text-slate-300'}`}>
                                  {game.name}
                                </h4>
                                <span className="text-[10px] text-slate-500">{game.entriesCount} participants</span>
                              </div>
                            </div>
                            <input
                              type="radio"
                              name="activeGameRadio"
                              checked={isSelected}
                              onChange={() => handleSettingsUpdate('activeGame', game.id)}
                              className="w-4 h-4 accent-indigo-600"
                            />
                          </div>
                          <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed mb-4">{game.desc}</p>
                        </div>

                        <div className="pt-3 border-t border-slate-100 dark:border-slate-700/60 flex items-center justify-between">
                          <span className={`text-[10px] font-bold uppercase tracking-wider ${isSelected ? game.color : 'text-slate-400'}`}>
                            {isSelected ? 'Live Mode Active' : 'Click to Activate'}
                          </span>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              openGamePreview(game.id);
                            }}
                            className="px-2.5 py-1 text-[11px] font-semibold bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg transition-colors flex items-center gap-1 shadow-sm"
                            title="Test Play in Modal"
                          >
                            <SafeIcon icon={FiEye} /> Preview
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* 3. Draw Countdown & Audio Tension Engine */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                
                {/* Countdown Timer Controller */}
                <div className="lg:col-span-8 glass-panel p-6 md:p-8 rounded-2xl bg-white dark:bg-slate-800/40 border-slate-200 dark:border-slate-700 flex flex-col justify-between">
                  <div>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
                      <div>
                        <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                          <SafeIcon icon={FiClock} className="text-indigo-500 dark:text-indigo-400" /> Mega Draw Countdown Controller
                        </h2>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Powers the real-time countdown display on the main event projection screen.</p>
                      </div>

                      {/* Live Status Badge */}
                      <div>
                        {adminTimeLeft ? (
                          adminTimeLeft.isExpired ? (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-500 text-xs font-bold animate-pulse">
                              <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                              Target Reached &bull; Ready for Draw
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-500 text-xs font-bold">
                              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-ping"></span>
                              Live Countdown Active
                            </span>
                          )
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 text-xs font-semibold">
                            Timer Off
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Real-time Ticker Readout in Admin */}
                    {adminTimeLeft && !adminTimeLeft.isExpired ? (
                      <div className="mb-6 p-4 rounded-xl bg-slate-950 text-white border border-slate-800 flex items-center justify-around text-center font-mono">
                        <div>
                          <div className="text-3xl font-black text-white">{String(adminTimeLeft.hours).padStart(2, '0')}</div>
                          <div className="text-[10px] text-slate-400 uppercase tracking-widest mt-0.5">Hours</div>
                        </div>
                        <span className="text-2xl font-bold text-slate-600">:</span>
                        <div>
                          <div className="text-3xl font-black text-white">{String(adminTimeLeft.minutes).padStart(2, '0')}</div>
                          <div className="text-[10px] text-slate-400 uppercase tracking-widest mt-0.5">Minutes</div>
                        </div>
                        <span className="text-2xl font-bold text-slate-600">:</span>
                        <div>
                          <div className={`text-3xl font-black ${adminTimeLeft.hours === 0 && adminTimeLeft.minutes === 0 && adminTimeLeft.seconds <= 10 ? 'text-red-400 animate-pulse' : 'text-emerald-400'}`}>
                            {String(adminTimeLeft.seconds).padStart(2, '0')}
                          </div>
                          <div className="text-[10px] text-slate-400 uppercase tracking-widest mt-0.5">Seconds</div>
                        </div>
                      </div>
                    ) : null}

                    {/* Custom Datetime Input */}
                    <div className="bg-slate-50 dark:bg-slate-900/80 p-4 rounded-xl border border-slate-200 dark:border-slate-700 mb-4">
                      <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Target Date & Time</label>
                      <input 
                        type="datetime-local" 
                        value={settings?.drawTime ? new Date(new Date(settings.drawTime).getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0,16) : ''} 
                        onChange={(e) => handleSettingsUpdate('drawTime', e.target.value ? new Date(e.target.value).toISOString() : null)}
                        className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all text-sm font-medium"
                      />
                    </div>

                    {/* Quick Presets */}
                    <div>
                      <span className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Quick Duration Presets</span>
                      <div className="flex flex-wrap gap-2">
                        <button 
                          type="button"
                          onClick={() => setTimerPreset(15)}
                          className="px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 text-xs font-semibold transition-colors"
                        >
                          +15 Mins
                        </button>
                        <button 
                          type="button"
                          onClick={() => setTimerPreset(30)}
                          className="px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 text-xs font-semibold transition-colors"
                        >
                          +30 Mins
                        </button>
                        <button 
                          type="button"
                          onClick={() => setTimerPreset(60)}
                          className="px-3 py-1.5 rounded-lg border border-indigo-200 dark:border-indigo-500/30 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-500/20 text-xs font-semibold transition-colors"
                        >
                          +1 Hour
                        </button>
                        <button 
                          type="button"
                          onClick={() => setTimerPreset(180)}
                          className="px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 text-xs font-semibold transition-colors"
                        >
                          +3 Hours
                        </button>
                        <button 
                          type="button"
                          onClick={setTimerTonight8PM}
                          className="px-3 py-1.5 rounded-lg border border-purple-200 dark:border-purple-500/30 bg-purple-50 dark:bg-purple-500/10 text-purple-600 dark:text-purple-400 hover:bg-purple-100 dark:hover:bg-purple-500/20 text-xs font-semibold transition-colors"
                        >
                          Tonight 8 PM
                        </button>
                        <button 
                          type="button"
                          onClick={() => handleSettingsUpdate('drawTime', null)}
                          className="px-3 py-1.5 rounded-lg border border-red-200 dark:border-red-500/20 bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 hover:bg-red-100 text-xs font-semibold transition-colors ml-auto"
                        >
                          Clear Timer
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sound & Background Music Studio Card */}
                <div className="lg:col-span-4 glass-panel p-6 rounded-2xl bg-white dark:bg-slate-800/40 border-slate-200 dark:border-slate-700 flex flex-col justify-between">
                  <div className="space-y-5">
                    <div>
                      <h3 className="text-base font-bold text-slate-900 dark:text-white mb-1 flex items-center gap-2">
                        <SafeIcon icon={FiMusic} className="text-amber-500" /> Sound & Music Studio
                      </h3>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Configure arcade game sound effects and background music for participant games and big-screen displays.
                      </p>
                    </div>

                    {/* Section 1: Sound Effects (SFX) */}
                    <div className="p-3.5 bg-slate-50 dark:bg-slate-900/70 rounded-xl border border-slate-200 dark:border-slate-700 space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <strong className="text-xs font-bold text-slate-800 dark:text-white block">Game Sound FX</strong>
                          <span className="text-[10px] text-slate-500">Reels, wheels, scratches & wins</span>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input 
                            type="checkbox" 
                            className="sr-only peer"
                            checked={settings?.soundEnabled !== false}
                            onChange={e => handleSettingsUpdate('soundEnabled', e.target.checked)}
                          />
                          <div className="w-10 h-5 bg-slate-300 dark:bg-slate-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-500"></div>
                        </label>
                      </div>

                      {/* Quick SFX Audition Buttons */}
                      <div className="grid grid-cols-2 gap-1.5 pt-1">
                        <button
                          type="button"
                          onClick={() => playWinFanfare()}
                          className="py-1 px-2 rounded-lg bg-white dark:bg-slate-800 hover:bg-slate-100 text-[10px] font-semibold text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 transition-colors flex items-center justify-center gap-1"
                        >
                          <SafeIcon icon={FiVolume2} /> Fanfare
                        </button>
                        <button
                          type="button"
                          onClick={() => playJackpotSound()}
                          className="py-1 px-2 rounded-lg bg-white dark:bg-slate-800 hover:bg-slate-100 text-[10px] font-semibold text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 transition-colors flex items-center justify-center gap-1"
                        >
                          <SafeIcon icon={FiVolume2} /> Jackpot
                        </button>
                        <button
                          type="button"
                          onClick={() => playReelStopSound(3)}
                          className="py-1 px-2 rounded-lg bg-white dark:bg-slate-800 hover:bg-slate-100 text-[10px] font-semibold text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 transition-colors flex items-center justify-center gap-1"
                        >
                          <SafeIcon icon={FiVolume2} /> Slot Reel
                        </button>
                        <button
                          type="button"
                          onClick={() => playWheelSpinSound(1500)}
                          className="py-1 px-2 rounded-lg bg-white dark:bg-slate-800 hover:bg-slate-100 text-[10px] font-semibold text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 transition-colors flex items-center justify-center gap-1"
                        >
                          <SafeIcon icon={FiVolume2} /> Wheel Ticks
                        </button>
                      </div>
                    </div>

                    {/* Section 2: Background Music (BGM) */}
                    <div className="p-3.5 bg-slate-50 dark:bg-slate-900/70 rounded-xl border border-slate-200 dark:border-slate-700 space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <strong className="text-xs font-bold text-slate-800 dark:text-white block">Background Music</strong>
                          <span className="text-[10px] text-slate-500">Audio theme on games & screen</span>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input 
                            type="checkbox" 
                            className="sr-only peer"
                            checked={settings?.musicEnabled !== false}
                            onChange={e => {
                              const val = e.target.checked;
                              handleSettingsUpdate('musicEnabled', val);
                              if (!val && isTestingBgm) {
                                stopBackgroundMusic();
                                setIsTestingBgm(false);
                              }
                            }}
                          />
                          <div className="w-10 h-5 bg-slate-300 dark:bg-slate-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-amber-500"></div>
                        </label>
                      </div>

                      {/* Music Theme Selector */}
                      <div>
                        <label className="block text-[10px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1">
                          Select Music Theme
                        </label>
                        <select
                          value={settings?.musicTheme || 'arcade_casino'}
                          onChange={e => {
                            const newTheme = e.target.value;
                            handleSettingsUpdate('musicTheme', newTheme);
                            if (isTestingBgm) {
                              startBackgroundMusic(newTheme, settings?.musicVolume || 0.35, settings?.musicCustomUrl);
                            }
                          }}
                          className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 text-slate-800 dark:text-white text-xs font-semibold rounded-lg px-2.5 py-2 focus:ring-2 focus:ring-amber-500 outline-none"
                        >
                          {MUSIC_THEMES.map(t => (
                            <option key={t.id} value={t.id}>{t.name}</option>
                          ))}
                        </select>
                      </div>

                      {/* Custom Audio URL (if chosen) */}
                      {settings?.musicTheme === 'custom' && (
                        <div>
                          <label className="block text-[10px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1">
                            Audio Stream / MP3 Link
                          </label>
                          <input 
                            type="url"
                            value={settings?.musicCustomUrl || ''}
                            onChange={e => handleSettingsUpdate('musicCustomUrl', e.target.value)}
                            placeholder="https://example.com/track.mp3"
                            className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 text-slate-800 dark:text-white text-xs rounded-lg px-2.5 py-1.5 focus:ring-2 focus:ring-amber-500 outline-none"
                          />
                        </div>
                      )}

                      {/* Volume Slider */}
                      <div>
                        <div className="flex justify-between items-center text-[10px] text-slate-500 mb-1">
                          <span className="font-semibold uppercase">Music Volume</span>
                          <span className="font-mono">{Math.round((settings?.musicVolume ?? 0.35) * 100)}%</span>
                        </div>
                        <input 
                          type="range" 
                          min="0.05" 
                          max="1" 
                          step="0.05"
                          value={settings?.musicVolume ?? 0.35}
                          onChange={e => {
                            const vol = parseFloat(e.target.value);
                            handleSettingsUpdate('musicVolume', vol);
                            setAudioVolumes({ musicVolume: vol });
                          }}
                          className="w-full accent-amber-500 cursor-pointer h-1.5 bg-slate-200 dark:bg-slate-700 rounded-lg"
                        />
                      </div>

                      {/* Live Audition Button */}
                      <button
                        type="button"
                        onClick={() => {
                          if (isTestingBgm) {
                            stopBackgroundMusic();
                            setIsTestingBgm(false);
                          } else {
                            startBackgroundMusic(
                              settings?.musicTheme || 'arcade_casino', 
                              settings?.musicVolume || 0.35, 
                              settings?.musicCustomUrl
                            );
                            setIsTestingBgm(true);
                          }
                        }}
                        className={`w-full py-2 px-3 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-2 ${
                          isTestingBgm 
                            ? 'bg-amber-500 text-white shadow-md' 
                            : 'bg-white dark:bg-slate-800 hover:bg-slate-100 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700'
                        }`}
                      >
                        <SafeIcon icon={isTestingBgm ? FiSquare : FiPlay} />
                        {isTestingBgm ? "Stop Audition Track" : "Audition Selected Theme"}
                      </button>
                    </div>

                  </div>
                </div>

              </div>

              {/* Home Page Live Draw Display Options Quick Controller */}
              <div className="glass-panel p-6 rounded-2xl bg-white dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 shadow-md">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                  <div className="flex items-center gap-2.5">
                    <span className="text-2xl">🏺</span>
                    <div>
                      <h3 className="text-base font-black text-slate-800 dark:text-white flex items-center gap-2">
                        Home Page Live Draw Display Mode
                        <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-full border ${
                          settings?.homeDisplayMode === 'live_pot_draw'
                            ? 'bg-amber-500/20 text-amber-700 dark:text-amber-400 border-amber-500/40 animate-pulse'
                            : 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border-emerald-500/40'
                        }`}>
                          {settings?.homeDisplayMode === 'live_pot_draw' ? 'Live Ceremony (QR Code Hidden)' : 'Visitor QR Active'}
                        </span>
                      </h3>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        When drawing tokens, switch to <strong>Live Pot Draw Only</strong> to hide the QR code and focus spectators on the transparent glass pot.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleSettingsUpdate('homeDisplayMode', settings?.homeDisplayMode === 'live_pot_draw' ? 'qr_participate' : 'live_pot_draw')}
                      className={`px-4 py-2 rounded-xl text-xs font-black transition-all shadow-md flex items-center gap-2 cursor-pointer ${
                        settings?.homeDisplayMode === 'live_pot_draw'
                          ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                          : 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-slate-950'
                      }`}
                    >
                      <span>{settings?.homeDisplayMode === 'live_pot_draw' ? '📱 Restore Visitor QR Code on Home' : '⚡ Switch to Live Pot Draw (Hide QR)'}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveTab('glass_token_draw')}
                      className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 rounded-xl text-xs font-bold transition-all shadow-sm flex items-center gap-1.5"
                    >
                      <span>Open Pot Controls ➔</span>
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
                  <div
                    onClick={() => handleSettingsUpdate('homeDisplayMode', 'live_pot_draw')}
                    className={`p-3 rounded-xl border-2 cursor-pointer transition-all ${
                      settings?.homeDisplayMode === 'live_pot_draw'
                        ? 'border-amber-500 bg-amber-500/10 dark:bg-amber-500/20 shadow-sm'
                        : 'border-slate-200 dark:border-slate-700 hover:border-amber-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-xs text-slate-800 dark:text-white">🏺 Live Pot Draw Only</span>
                      {settings?.homeDisplayMode === 'live_pot_draw' && <span className="text-amber-500 font-bold text-xs">✓ Active</span>}
                    </div>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">Hides QR code & entry forms; displays only the glass pot draw.</p>
                  </div>

                  <div
                    onClick={() => handleSettingsUpdate('homeDisplayMode', 'qr_participate')}
                    className={`p-3 rounded-xl border-2 cursor-pointer transition-all ${
                      settings?.homeDisplayMode === 'qr_participate' || !settings?.homeDisplayMode
                        ? 'border-emerald-500 bg-emerald-500/10 dark:bg-emerald-500/20 shadow-sm'
                        : 'border-slate-200 dark:border-slate-700 hover:border-emerald-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-xs text-slate-800 dark:text-white">📱 Visitor QR Code Mode</span>
                      {(settings?.homeDisplayMode === 'qr_participate' || !settings?.homeDisplayMode) && <span className="text-emerald-500 font-bold text-xs">✓ Active</span>}
                    </div>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">Full participation page with large QR code and game links.</p>
                  </div>

                  <div
                    onClick={() => handleSettingsUpdate('homeDisplayMode', 'both')}
                    className={`p-3 rounded-xl border-2 cursor-pointer transition-all ${
                      settings?.homeDisplayMode === 'both'
                        ? 'border-indigo-500 bg-indigo-500/10 dark:bg-indigo-500/20 shadow-sm'
                        : 'border-slate-200 dark:border-slate-700 hover:border-indigo-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-xs text-slate-800 dark:text-white">🔄 Dual Showcase Mode</span>
                      {settings?.homeDisplayMode === 'both' && <span className="text-indigo-500 font-bold text-xs">✓ Active</span>}
                    </div>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">Dual layout with both the glass pot and visitor QR code.</p>
                  </div>
                </div>
              </div>

              {/* 4. Supercharged Mega Draw Command Center */}
              <div className="glass-panel p-6 md:p-8 rounded-2xl bg-gradient-to-br from-amber-500/10 via-amber-500/5 to-slate-900/10 dark:from-slate-800/90 dark:to-slate-900 border border-amber-300 dark:border-amber-500/30 shadow-xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-80 h-80 bg-amber-400/10 blur-3xl rounded-full pointer-events-none"></div>

                <div className="relative z-10 space-y-6">
                  {/* Top Bar with Participant Counts */}
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 border-b border-amber-200/60 dark:border-slate-700/60">
                    <div>
                      <h2 className="text-2xl font-black text-slate-900 dark:text-white flex items-center gap-2">
                        <SafeIcon icon={FiStar} className="text-amber-500" /> Mega Draw Command Center
                      </h2>
                      <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                        Select and announce official event winners with live high-tension simulation, automated prize tier allocation, and printable certificates.
                      </p>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="bg-white dark:bg-slate-900 px-3.5 py-1.5 rounded-xl border border-amber-200 dark:border-slate-700 text-xs font-bold text-slate-700 dark:text-slate-300 flex items-center gap-2 shadow-sm">
                        <span className="w-2 h-2 rounded-full bg-indigo-500"></span>
                        Total Pool: {entries.length}
                      </div>
                      <div className="bg-amber-500 text-white px-3.5 py-1.5 rounded-xl text-xs font-bold flex items-center gap-2 shadow-md">
                        <SafeIcon icon={FiUsers} />
                        Eligible: {getEligibleEntries().length}
                      </div>
                    </div>
                  </div>

                  {/* Draw Configuration Controls Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                    
                    {/* Filter Entry Pool */}
                    <div className="md:col-span-5 space-y-2">
                      <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
                        <SafeIcon icon={FiFilter} /> Eligible Participant Filter
                      </label>
                      <select
                        value={settings?.megaDrawFilter || 'all'}
                        onChange={(e) => handleSettingsUpdate('megaDrawFilter', e.target.value)}
                        className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 text-slate-800 dark:text-white rounded-xl px-3.5 py-2.5 text-xs font-semibold outline-none focus:ring-2 focus:ring-amber-500 transition-all"
                      >
                        <option value="all">All Registrations ({entries.length} entries)</option>
                        <option value="unique">Unique Mobile Numbers Only (Deduplicated)</option>
                        <option value="scratch">Scratch Card Players Only ({scratchEntries.length})</option>
                        <option value="spin">Spin Wheel Players Only ({spinEntries.length})</option>
                        <option value="peel">Peel & Reveal Players Only ({peelEntries.length})</option>
                        <option value="slot">Slot Machine Players Only ({slotEntries.length})</option>
                        <option value="flip">Mystery Flip Players Only ({flipEntries.length})</option>
                      </select>
                      <p className="text-[11px] text-slate-500">Select "Unique" to ensure no person can win twice using the same mobile number.</p>
                    </div>

                    {/* Winner Count Controller */}
                    <div className="md:col-span-7 space-y-2">
                      <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                        Number of Winners to Draw
                      </label>
                      <div className="flex items-center gap-2">
                        <div className="flex bg-white dark:bg-slate-900 rounded-xl p-1 border border-slate-300 dark:border-slate-700 flex-1">
                          {[1, 2, 3, 5, 10].map(num => (
                            <button 
                              key={num}
                              type="button"
                              onClick={() => {
                                setWinnerCount(num);
                                setCustomWinnerCount('');
                              }}
                              className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all ${
                                winnerCount === num && !customWinnerCount
                                  ? 'bg-amber-500 text-white shadow-sm'
                                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                              }`}
                            >
                              {num}
                            </button>
                          ))}
                        </div>
                        <input
                          type="number"
                          min="1"
                          max={getEligibleEntries().length || 100}
                          placeholder="Custom"
                          value={customWinnerCount}
                          onChange={(e) => {
                            setCustomWinnerCount(e.target.value);
                            if (e.target.value) setWinnerCount(Number(e.target.value));
                          }}
                          className="w-24 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-amber-500"
                        />
                      </div>
                    </div>

                  </div>

                  {/* Mega Draw Prize Tiers Customizer */}
                  <div className="bg-white/60 dark:bg-slate-900/60 p-4 rounded-xl border border-amber-200/80 dark:border-slate-700/80">
                    <span className="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                      <SafeIcon icon={FiAward} className="text-amber-500" /> Mega Draw Grand Prize Tiers (Assigned by Rank)
                    </span>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                      {['1st Prize (Grand Winner)', '2nd Prize (Runner Up)', '3rd Prize (2nd Runner Up)', 'Consolation Prize'].map((label, idx) => {
                        const currentTiers = settings?.megaDrawPrizes || ["1st Prize - Mega Bumper", "2nd Prize - Premium Gift", "3rd Prize - Exclusive Hamper", "Consolation Prize"];
                        return (
                          <div key={idx}>
                            <label className="block text-[10px] font-bold text-slate-500 mb-1">{label}</label>
                            <input
                              type="text"
                              value={currentTiers[idx] || ''}
                              onChange={(e) => {
                                const updated = [...currentTiers];
                                updated[idx] = e.target.value;
                                handleSettingsUpdate('megaDrawPrizes', updated);
                              }}
                              className="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-1.5 text-xs text-slate-800 dark:text-white outline-none focus:ring-1 focus:ring-amber-500"
                              placeholder={`Prize for Rank #${idx + 1}`}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Execution Action Bar */}
                  <div className="flex flex-col sm:flex-row gap-3 pt-2">
                    <button 
                      onClick={pickRandomWinners}
                      disabled={isSpinning || getEligibleEntries().length === 0}
                      className="flex-1 py-3.5 rounded-xl bg-gradient-to-r from-amber-500 via-orange-500 to-amber-600 hover:from-amber-600 hover:to-orange-700 text-white font-black text-sm shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2 transform active:scale-[0.98]"
                    >
                      <SafeIcon icon={FiAward} /> {isSpinning ? 'Simulating Draw Drumroll...' : `Execute Draw (${winnerCount} Winner${winnerCount > 1 ? 's' : ''})`}
                    </button>

                    {winners.length > 0 && !isSpinning && (
                      <>
                        <button 
                          onClick={settings?.resultsPublished ? resetDisplay : publishResults}
                          className={`flex-1 py-3.5 rounded-xl border font-bold text-sm transition-all flex items-center justify-center gap-2 ${
                            settings?.resultsPublished 
                              ? 'bg-red-50 dark:bg-red-500/10 border-red-200 dark:border-red-500/30 text-red-600 dark:text-red-400 hover:bg-red-100' 
                              : 'bg-emerald-500 text-white border-emerald-600 hover:bg-emerald-600 shadow-md'
                          }`}
                        >
                          <SafeIcon icon={FiMonitor} /> {settings?.resultsPublished ? 'Unpublish from Display' : 'Publish to Live Projection Screen'}
                        </button>

                        <button 
                          onClick={printWinnerSheet}
                          className="px-4 py-3.5 rounded-xl bg-slate-800 hover:bg-slate-900 text-white font-bold text-sm transition-all flex items-center justify-center gap-2 shadow-md"
                          title="Print or export winners certificate sheet"
                        >
                          <SafeIcon icon={FiPrinter} /> Export Sheet
                        </button>
                      </>
                    )}
                  </div>

                  {/* Selected Winners Live Grid */}
                  <div className="bg-white/70 dark:bg-slate-950/70 rounded-2xl border border-amber-200/80 dark:border-slate-800 p-5 min-h-[220px]">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2">
                        <SafeIcon icon={FiAward} className="text-amber-500" />
                        Selected Winners Preview {winners.length > 0 && `(${winners.length})`}
                      </h3>
                      {winners.length > 0 && (
                        <span className="text-[11px] text-slate-400">
                          Click <SafeIcon icon={FiRefreshCw} className="inline mx-0.5" /> to re-roll an individual slot if absent
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <AnimatePresence mode="popLayout">
                        {winners.length === 0 && !isSpinning ? (
                          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="col-span-full text-center text-slate-400 dark:text-slate-600 py-10">
                            No winners drawn yet. Choose your count and click "Execute Draw" above.
                          </motion.div>
                        ) : (
                          winners.map((winner, idx) => {
                            const assignedPrize = winner.megaPrize || (settings?.megaDrawPrizes && settings.megaDrawPrizes[idx]) || winner.reward || 'Mega Reward';
                            return (
                              <motion.div 
                                key={winner?.id ? `${winner.id}-${idx}` : idx}
                                initial={{ opacity: 0, y: 15 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.95 }}
                                className="flex items-center gap-3 bg-white dark:bg-slate-800 p-3.5 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm relative overflow-hidden"
                              >
                                <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-xs font-black shadow-sm flex-shrink-0 ${
                                  idx === 0 
                                    ? 'bg-gradient-to-br from-amber-400 to-orange-500 text-white' 
                                    : 'bg-slate-100 dark:bg-slate-700 text-slate-800 dark:text-slate-200'
                                }`}>
                                  #{idx + 1}
                                </div>

                                <div className="flex-1 overflow-hidden">
                                  <div className="font-bold text-slate-900 dark:text-white text-sm truncate flex items-center gap-1.5">
                                    {winner?.name || '---'}
                                    {idx === 0 && <span className="text-[9px] bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300 font-extrabold px-1.5 py-0.5 rounded">GRAND</span>}
                                  </div>
                                  <div className="text-xs text-slate-500 dark:text-slate-400 truncate flex items-center gap-2 mt-0.5">
                                    <span>{winner?.mobile || '---'}</span>
                                    {winner?.receiptNumber && <span className="font-mono text-[10px] bg-slate-100 dark:bg-slate-900 px-1 rounded">{winner.receiptNumber}</span>}
                                  </div>
                                  <div className="text-xs font-bold text-emerald-600 dark:text-emerald-400 truncate mt-1">
                                    {assignedPrize}
                                  </div>
                                </div>

                                {/* Slot Actions: Re-roll & WhatsApp */}
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  <button
                                    type="button"
                                    onClick={() => reRollWinner(idx)}
                                    className="p-2 rounded-lg text-slate-400 hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-500/10 transition-colors"
                                    title="Re-roll only this winner slot"
                                  >
                                    <SafeIcon icon={FiRefreshCw} className="text-sm" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => sendWhatsApp({ ...winner, reward: assignedPrize })}
                                    className="p-2 rounded-lg text-slate-400 hover:text-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-500/10 transition-colors"
                                    title="Notify winner on WhatsApp"
                                  >
                                    <SafeIcon icon={FiMessageCircle} className="text-sm" />
                                  </button>
                                </div>
                              </motion.div>
                            );
                          })
                        )}
                      </AnimatePresence>
                    </div>
                  </div>

                </div>
              </div>

            </motion.div>
          )}

          {/* TAB: SECURITY & FRAUD */}
          {activeTab === 'security' && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
              <div className="glass-panel p-8 rounded-2xl bg-white dark:bg-slate-800/40 border-slate-200 dark:border-slate-700">
                <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2 flex items-center gap-2">
                  <SafeIcon icon={FiShield} className="text-indigo-500 dark:text-indigo-400" /> Fraud Detection & Restrictions
                </h2>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-8">
                  Prevent unauthorized participants by requiring a valid serial number. When enabled, users must enter a valid, unused serial number to play the game.
                </p>

                <div className="flex flex-col sm:flex-row sm:items-center justify-between p-5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl mb-6 gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <strong className="text-slate-800 dark:text-white text-base">Require Serial / Token Verification</strong>
                      {settings?.requireSerialNumber ? (
                        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black bg-emerald-500 text-white uppercase tracking-wider">
                          Active & Enforced
                        </span>
                      ) : (
                        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                          Open Access
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      When enabled, event visitors must provide an authorized, unused token or serial code to play any game mode.
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                      {settings?.requireSerialNumber ? 'Gate Enabled' : 'Gate Disabled'}
                    </span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        className="sr-only peer"
                        checked={settings?.requireSerialNumber || false}
                        onChange={e => handleSettingsUpdate('requireSerialNumber', e.target.checked)}
                      />
                      <div className="w-14 h-7 bg-slate-300 dark:bg-slate-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-indigo-600"></div>
                    </label>
                  </div>
                </div>

                {/* Professional Token & Serial Management Suite (Excel/CSV upload, batch generator, sample downloads, live directory) */}
                <TokenSerialManager settings={settings} onUpdateSettings={handleSettingsUpdate} />

                {/* NEW FEATURE: Master Credentials Manager */}
                <div className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-6 mt-8">
                  <h3 className="text-sm font-bold text-slate-800 dark:text-white mb-4 flex items-center gap-2">
                    <SafeIcon icon={FiUser} className="text-indigo-500" /> Admin Credentials Management
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-5">Change the username and password used to access this Admin Portal.</p>
                  
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1 relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <SafeIcon icon={FiUser} className="text-slate-400 text-sm" />
                      </div>
                      <input 
                        type="text" 
                        value={credForm.username} 
                        onChange={e => setCredForm({...credForm, username: e.target.value})} 
                        placeholder="New Username" 
                        className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg outline-none focus:ring-2 focus:ring-indigo-500 text-slate-800 dark:text-white text-sm" 
                      />
                    </div>
                    <div className="flex-1 relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <SafeIcon icon={FiLock} className="text-slate-400 text-sm" />
                      </div>
                      <input 
                        type="text" 
                        value={credForm.password} 
                        onChange={e => setCredForm({...credForm, password: e.target.value})} 
                        placeholder="New Password" 
                        className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg outline-none focus:ring-2 focus:ring-indigo-500 text-slate-800 dark:text-white text-sm" 
                      />
                    </div>
                    <button 
                      onClick={handleUpdateCredentials} 
                      className="px-6 py-2.5 bg-slate-800 hover:bg-slate-900 dark:bg-indigo-600 dark:hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors text-sm whitespace-nowrap"
                    >
                      Update Login
                    </button>
                  </div>
                </div>

              </div>
            </motion.div>
          )}

          {/* TAB: LIVE MODE & STATION ROUTING & STAFF ALLOCATION */}
          {(activeTab === 'station_routing' || activeTab === 'staff_stations') && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
              <AdminTabErrorBoundary moduleName="Station Routing & Staff Management">
                <StationRoutingManager />
              </AdminTabErrorBoundary>
            </motion.div>
          )}

          {/* TAB: TRANSPARENT GLASS POT TOKEN MEGA DRAW (3rd -> 2nd -> 1st) */}
          {activeTab === 'glass_token_draw' && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
              {/* Home Page Live Draw Display Options & Stage Switcher */}
              <div className="glass-panel rounded-2xl p-6 bg-white dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 shadow-md">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 border-b border-slate-200 dark:border-slate-700">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">🏺</span>
                      <h2 className="text-lg font-black text-slate-800 dark:text-white">
                        Home Page Live Draw Display Controller
                      </h2>
                      <span className={`text-[11px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full border ${
                        (settings?.homeDisplayMode === 'live_pot_draw')
                          ? 'bg-amber-500/20 text-amber-700 dark:text-amber-400 border-amber-500/40 animate-pulse'
                          : (settings?.homeDisplayMode === 'both')
                          ? 'bg-indigo-500/20 text-indigo-700 dark:text-indigo-400 border-indigo-500/40'
                          : 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border-emerald-500/40'
                      }`}>
                        {(settings?.homeDisplayMode === 'live_pot_draw') ? '🔴 Live Ceremony (QR Code Hidden)' : (settings?.homeDisplayMode === 'both') ? '🔄 Dual Mode' : '📱 Visitor QR Code Active'}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      Choose what the public audience sees on the Home Page (<code className="text-indigo-600 dark:text-indigo-400 font-mono">#/</code>). When conducting the draw, hide all QR codes and display only the transparent glass pot.
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    <a
                      href="#/"
                      target="_blank"
                      rel="noreferrer"
                      className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 shadow-sm"
                    >
                      <SafeIcon icon={FiExternalLink} />
                      <span>Preview Home Page</span>
                    </a>
                    <a
                      href="#/transparent-draw"
                      target="_blank"
                      rel="noreferrer"
                      className="px-3.5 py-2 bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/40 text-amber-800 dark:text-amber-300 rounded-xl text-xs font-black transition-all flex items-center gap-1.5 shadow-sm"
                    >
                      <SafeIcon icon={FiMonitor} />
                      <span>Big Screen Projector Stage</span>
                    </a>
                  </div>
                </div>

                {/* 3 Display Mode Selection Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
                  {/* Mode 1: Live Ceremony Only (Hides QR Code) */}
                  <div
                    onClick={() => handleSettingsUpdate('homeDisplayMode', 'live_pot_draw')}
                    className={`cursor-pointer rounded-2xl p-4 border-2 transition-all relative ${
                      settings?.homeDisplayMode === 'live_pot_draw'
                        ? 'border-amber-500 bg-amber-500/10 dark:bg-amber-500/20 shadow-lg ring-2 ring-amber-500/20'
                        : 'border-slate-200 dark:border-slate-700 hover:border-amber-300 dark:hover:border-slate-600 bg-slate-50/50 dark:bg-slate-800/40'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">🏺</span>
                        <h3 className="text-sm font-black text-slate-800 dark:text-white">Live Pot Draw Only</h3>
                      </div>
                      {settings?.homeDisplayMode === 'live_pot_draw' && (
                        <span className="w-5 h-5 rounded-full bg-amber-500 text-slate-950 flex items-center justify-center text-xs font-black">✓</span>
                      )}
                    </div>
                    <p className="text-xs text-slate-600 dark:text-slate-300 mt-2 font-medium">
                      <strong className="text-amber-600 dark:text-amber-400">Hides QR Code and everything else.</strong> Displays ONLY the transparent glass pot and ceremonial winner podium on the public home page.
                    </p>
                    <div className="mt-3 flex items-center gap-1 text-[11px] font-bold text-amber-700 dark:text-amber-300">
                      <span>🎯 Recommended during ceremonial draw</span>
                    </div>
                  </div>

                  {/* Mode 2: Standard Visitor QR Participation */}
                  <div
                    onClick={() => handleSettingsUpdate('homeDisplayMode', 'qr_participate')}
                    className={`cursor-pointer rounded-2xl p-4 border-2 transition-all relative ${
                      settings?.homeDisplayMode === 'qr_participate' || !settings?.homeDisplayMode
                        ? 'border-emerald-500 bg-emerald-500/10 dark:bg-emerald-500/20 shadow-lg ring-2 ring-emerald-500/20'
                        : 'border-slate-200 dark:border-slate-700 hover:border-emerald-300 dark:hover:border-slate-600 bg-slate-50/50 dark:bg-slate-800/40'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">📱</span>
                        <h3 className="text-sm font-black text-slate-800 dark:text-white">Visitor QR Code Mode</h3>
                      </div>
                      {(settings?.homeDisplayMode === 'qr_participate' || !settings?.homeDisplayMode) && (
                        <span className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center text-xs font-black">✓</span>
                      )}
                    </div>
                    <p className="text-xs text-slate-600 dark:text-slate-300 mt-2 font-medium">
                      Standard participation screen with prominent QR code for visitors to scan and play games, prize pool showcases, and countdown timer.
                    </p>
                    <div className="mt-3 flex items-center gap-1 text-[11px] font-bold text-emerald-700 dark:text-emerald-300">
                      <span>🎟️ Standard event participation mode</span>
                    </div>
                  </div>

                  {/* Mode 3: Dual Mode */}
                  <div
                    onClick={() => handleSettingsUpdate('homeDisplayMode', 'both')}
                    className={`cursor-pointer rounded-2xl p-4 border-2 transition-all relative ${
                      settings?.homeDisplayMode === 'both'
                        ? 'border-indigo-500 bg-indigo-500/10 dark:bg-indigo-500/20 shadow-lg ring-2 ring-indigo-500/20'
                        : 'border-slate-200 dark:border-slate-700 hover:border-indigo-300 dark:hover:border-slate-600 bg-slate-50/50 dark:bg-slate-800/40'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">🔄</span>
                        <h3 className="text-sm font-black text-slate-800 dark:text-white">Dual Showcase Mode</h3>
                      </div>
                      {settings?.homeDisplayMode === 'both' && (
                        <span className="w-5 h-5 rounded-full bg-indigo-500 text-white flex items-center justify-center text-xs font-black">✓</span>
                      )}
                    </div>
                    <p className="text-xs text-slate-600 dark:text-slate-300 mt-2 font-medium">
                      Displays both the circulating transparent glass pot showcase and the visitor QR code participation hero side-by-side.
                    </p>
                    <div className="mt-3 flex items-center gap-1 text-[11px] font-bold text-indigo-700 dark:text-indigo-300">
                      <span>✨ Hybrid display format</span>
                    </div>
                  </div>
                </div>

                {/* Instant Action Toggles */}
                <div className="mt-5 pt-4 border-t border-slate-200 dark:border-slate-700 flex flex-wrap items-center justify-between gap-3">
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    💡 <em>Switch to <strong>"Live Pot Draw Only"</strong> to instantly clear the Home page of QR codes and focus the audience on the drawing pot.</em>
                  </div>
                  <div className="flex items-center gap-2">
                    {settings?.homeDisplayMode !== 'live_pot_draw' ? (
                      <button
                        type="button"
                        onClick={() => handleSettingsUpdate('homeDisplayMode', 'live_pot_draw')}
                        className="px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-slate-950 font-black rounded-xl text-xs transition-all shadow-md flex items-center gap-1.5 cursor-pointer"
                      >
                        <span>⚡ Switch Home Page to Live Pot Draw Only (Hide QR Code)</span>
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleSettingsUpdate('homeDisplayMode', 'qr_participate')}
                        className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-black rounded-xl text-xs transition-all shadow-md flex items-center gap-1.5 cursor-pointer"
                      >
                        <span>📱 Restore Visitor QR Code on Home Page</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Admin Draw Controller: Full draw triggers, vortex agitation, claw extraction */}
              <AdminTabErrorBoundary moduleName="Glass Pot Token Draw">
                <GlassPotTokenDraw isAdmin={true} isStageMode={false} />
              </AdminTabErrorBoundary>
            </motion.div>
          )}

          {/* TAB: ANALYTICS */}
          {activeTab === 'analytics' && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
              <AdminAnalytics entries={entries} />
            </motion.div>
          )}

          {/* TAB: OFFICIAL/ANNOUNCED WINNERS (7-Day Setup) */}
          {activeTab === 'official_winners' && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="glass-panel rounded-2xl p-6 bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 flex flex-col h-[700px]">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-xl font-semibold text-slate-800 dark:text-white flex items-center gap-2">
                    <SafeIcon icon={FiAward} className="text-amber-500 dark:text-amber-400" /> Announced Winners Directory
                  </h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Historically announced winners (auto-expires after 7 days)</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-500 text-xs px-3 py-1.5 rounded-full font-medium flex items-center gap-2 border border-amber-200 dark:border-amber-500/20">
                    <SafeIcon icon={FiStar} /> Total: {officialWinners.length}
                  </span>
                  {officialWinners.length > 0 && (
                    <button
                      type="button"
                      onClick={handleClearOfficialWinners}
                      className="text-xs px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-600 dark:text-red-400 rounded-full font-medium border border-red-500/20 transition-colors flex items-center gap-1.5 cursor-pointer"
                      title="Clear all announced winners from directory"
                    >
                      <SafeIcon icon={FiTrash2} /> Clear Directory
                    </button>
                  )}
                </div>
              </div>
              
              <div className="overflow-y-auto flex-1 pr-2 custom-scrollbar">
                <table className="w-full text-left text-sm">
                  <thead className="sticky top-0 bg-white dark:bg-slate-800 z-10 shadow-sm dark:shadow-md">
                    <tr className="border-b border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400">
                      <th className="py-3 font-medium">Winner Name</th>
                      <th className="py-3 font-medium">Contact Details</th>
                      <th className="py-3 font-medium">Reward</th>
                      <th className="py-3 font-medium">Announced On</th>
                      <th className="py-3 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
                    {officialWinners.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="py-10 text-center text-slate-400 dark:text-slate-500">
                          No announced winners in the last 7 days. Execute a draw and publish results to see them here.
                        </td>
                      </tr>
                    ) : officialWinners.map((winner) => (
                      <tr key={winner.announcedId} className="hover:bg-slate-50 dark:hover:bg-slate-700/20 transition-colors">
                        <td className="py-4 font-bold text-slate-800 dark:text-white">{winner.name}</td>
                        <td className="py-4 text-slate-600 dark:text-slate-300">
                          {winner.mobile}
                        </td>
                        <td className="py-4">
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 text-xs font-bold border border-amber-200 dark:border-amber-500/20">
                            {winner.reward}
                          </span>
                        </td>
                        <td className="py-4 text-slate-500 dark:text-slate-400 text-xs">
                          {new Date(winner.announcedAt).toLocaleString()}
                        </td>
                        <td className="py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button onClick={() => sendWhatsApp(winner)} className="p-1.5 text-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-500/10 rounded-md transition-colors" title="Notify via WhatsApp">
                              <SafeIcon icon={FiMessageCircle} className="text-lg" />
                            </button>
                            <button onClick={() => setEditingWinner(winner)} className="p-1.5 text-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 rounded-md transition-colors" title="Edit Details">
                              <SafeIcon icon={FiEdit2} className="text-lg" />
                            </button>
                            <button onClick={() => handleDeleteOfficialWinner(winner.announcedId)} className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-md transition-colors" title="Delete Winner">
                              <SafeIcon icon={FiTrash2} className="text-lg" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          )}

          {/* TAB: INDIVIDUAL GAME ENTRIES */}
          {(activeTab === 'scratch_entries' || activeTab === 'spin_entries' || activeTab === 'peel_entries' || activeTab === 'slot_entries' || activeTab === 'flip_entries') && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="glass-panel rounded-2xl p-6 bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 flex flex-col h-[700px]">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-slate-800 dark:text-white flex items-center gap-2">
                  {activeTab === 'scratch_entries' && <><SafeIcon icon={FiGift} className="text-orange-500 dark:text-orange-400" /> Scratch Card Participants</>}
                  {activeTab === 'spin_entries' && <><SafeIcon icon={FiTarget} className="text-blue-500 dark:text-blue-400" /> Spin Wheel Participants</>}
                  {activeTab === 'peel_entries' && <><SafeIcon icon={FiLayers} className="text-purple-500 dark:text-purple-400" /> Peel & Reveal Participants</>}
                  {activeTab === 'slot_entries' && <><SafeIcon icon={FiColumns} className="text-red-500 dark:text-red-400" /> Slot Machine Participants</>}
                  {activeTab === 'flip_entries' && <><SafeIcon icon={FiAward} className="text-emerald-500 dark:text-emerald-400" /> Mystery Flip Card Participants</>}
                </h2>
                <span className="bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-xs px-3 py-1.5 rounded-full font-medium flex items-center gap-2 border border-slate-200 dark:border-transparent">
                  <SafeIcon icon={FiActivity} /> Total: {
                    activeTab === 'scratch_entries' ? scratchEntries.length : 
                    activeTab === 'spin_entries' ? spinEntries.length : 
                    activeTab === 'peel_entries' ? peelEntries.length : 
                    activeTab === 'slot_entries' ? slotEntries.length :
                    flipEntries.length
                  }
                </span>
              </div>
              
              <div className="overflow-y-auto flex-1 pr-2 custom-scrollbar">
                <table className="w-full text-left text-sm">
                  <thead className="sticky top-0 bg-white dark:bg-slate-800 z-10 shadow-sm dark:shadow-md">
                    <tr className="border-b border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400">
                      <th className="py-3 font-medium">Name</th>
                      <th className="py-3 font-medium">Contact</th>
                      <th className="py-3 font-medium">Identifier</th>
                      <th className="py-3 font-medium">Date & Time</th>
                      <th className="py-3 font-medium">Reward Won</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
                    {(() => {
                      const displayEntries = activeTab === 'scratch_entries' ? scratchEntries : 
                                            activeTab === 'spin_entries' ? spinEntries : 
                                            activeTab === 'peel_entries' ? peelEntries : 
                                            activeTab === 'slot_entries' ? slotEntries : 
                                            flipEntries;
                      
                      if (displayEntries.length === 0) {
                        return (
                          <tr>
                            <td colSpan="5" className="py-10 text-center text-slate-400 dark:text-slate-500">
                              No entries found for this campaign yet.
                            </td>
                          </tr>
                        );
                      }

                      return displayEntries.map((entry) => (
                        <tr key={entry.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/20 transition-colors">
                          <td className="py-4 font-medium text-slate-800 dark:text-white">{entry.name}</td>
                          <td className="py-4 text-slate-600 dark:text-slate-300">
                            <div>{entry.mobile}</div>
                          </td>
                          <td className="py-4 text-slate-600 dark:text-slate-300">
                            <div className="text-[11px] font-mono font-medium bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded inline-block">
                              {entry.receiptNumber || 'N/A'}
                            </div>
                          </td>
                          <td className="py-4 text-slate-500 dark:text-slate-400 text-xs">
                            {new Date(entry.createdAt).toLocaleString()}
                          </td>
                          <td className="py-4">
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-xs font-medium border border-emerald-200 dark:border-emerald-500/20">
                              {entry.reward}
                            </span>
                          </td>
                        </tr>
                      ));
                    })()}
                  </tbody>
                </table>
              </div>
            </motion.div>
          )}

          {/* TAB: SCHOOL QUIZ & PROJECTOR ARENA (NEW) */}
          {activeTab === 'school_quiz' && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
              
              {/* Modular Navigation Bar */}
              {renderSchoolModuleNav()}

              {/* Top Banner & Quick Projector Launchers */}
              <div className="glass-panel p-6 md:p-8 rounded-2xl bg-gradient-to-r from-amber-500/10 via-yellow-500/5 to-indigo-500/10 dark:from-slate-800/90 dark:to-slate-900 border border-amber-300 dark:border-amber-500/30 shadow-xl relative overflow-hidden">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                  <div>
                    <span className="px-3 py-1 rounded-full bg-amber-500 text-slate-950 text-xs font-black uppercase tracking-wider inline-flex items-center gap-1.5 mb-2 shadow-sm">
                      <SafeIcon icon={FiZap} /> School & Event Module
                    </span>
                    <h2 className="text-2xl md:text-3xl font-black text-slate-900 dark:text-white flex items-center gap-2">
                      <SafeIcon icon={FiBookOpen} className="text-amber-500" /> School Quiz & Games Arena
                    </h2>
                    <p className="text-xs md:text-sm text-slate-600 dark:text-slate-300 mt-1 max-w-2xl">
                      Host interactive live screen quiz competitions on classroom projectors and run fun carnival balloon pop games at school stalls.
                    </p>
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    <a
                      href={`${baseUrl}#/live-quiz`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-5 py-3 rounded-xl bg-gradient-to-r from-amber-400 to-yellow-400 hover:from-amber-500 hover:to-yellow-500 text-slate-950 font-black text-xs uppercase tracking-wider shadow-lg flex items-center gap-2 transition-transform active:scale-95"
                    >
                      <SafeIcon icon={FiMonitor} /> Launch Projector Quiz Screen
                    </a>

                    <a
                      href={`${baseUrl}#/balloon`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-5 py-3 rounded-xl bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white font-black text-xs uppercase tracking-wider shadow-lg flex items-center gap-2 transition-transform active:scale-95"
                    >
                      <span>🎈</span> Launch Balloon Stall Kiosk
                    </a>

                    <button
                      type="button"
                      onClick={() => {
                        setQuizModalTab('upload');
                        setShowAddQuizModal(true);
                      }}
                      className="px-5 py-3 rounded-xl bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white font-black text-xs uppercase tracking-wider shadow-lg flex items-center gap-2 transition-transform active:scale-95 border border-indigo-400/30 cursor-pointer"
                    >
                      <span>📊</span> Question Bank & Excel Upload ({(quizQuestions || []).length})
                    </button>
                  </div>
                </div>
              </div>

              {/* Category Filter Pills for Quick Focusing */}
              <div className="p-2 rounded-2xl bg-white dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700/80 shadow-xs flex flex-wrap items-center gap-2">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider pl-2 pr-1">Filter Categories:</span>
                {[
                  { id: 'all', label: 'All 14 Games', icon: '⚡' },
                  { id: 'projector', label: 'Auditorium Projector Stage (3)', icon: '📽️' },
                  { id: 'stall', label: 'Event Stall Tablets (3)', icon: '🎪' },
                  { id: 'junior', label: 'Junior Champions Ages 3-7 (2)', icon: '🧸' },
                  { id: 'stem', label: 'Space & STEM Lab (6)', icon: '🚀' }
                ].map(f => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => setSchoolGamesFilter(f.id)}
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                      schoolGamesFilter === f.id
                        ? 'bg-amber-500 text-slate-950 font-black shadow-sm'
                        : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
                    }`}
                  >
                    <span>{f.icon}</span>
                    <span>{f.label}</span>
                  </button>
                ))}
              </div>

              {/* CATEGORY 1: LIVE AUDITORIUM & PROJECTOR DISPLAYS */}
              {['all', 'projector'].includes(schoolGamesFilter) && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">📽️</span>
                    <h3 className="text-base font-black text-slate-800 dark:text-white uppercase tracking-wider">
                      Live Auditorium & Projector Displays (Audience & Classroom)
                    </h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Kiosk 1: Classroom / Auditorium Quiz */}
                  <div className="glass-panel p-5 rounded-2xl bg-white dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 flex flex-col justify-between shadow-sm">
                    <div className="flex items-start gap-4">
                      <div className="bg-white p-2 rounded-xl border border-slate-200 shadow-md flex-shrink-0">
                        <QRCode value={`${baseUrl}#/live-quiz`} size={70} fgColor="#0f172a" level="L" />
                      </div>
                      <div>
                        <span className="text-[10px] font-black uppercase text-indigo-500 tracking-wider">Projector Display 1</span>
                        <h4 className="text-sm font-bold text-slate-900 dark:text-white mt-0.5">Live Screen Quiz & Guessing</h4>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                          15s timer, "Pehchano Kaun" unblur, Day/Night mode & sounds.
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-4 pt-3 border-t border-slate-100 dark:border-slate-700/60">
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(`${baseUrl}#/live-quiz`);
                          alert("Projector Quiz link copied!");
                        }}
                        className="flex-1 py-2 text-xs font-bold rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                      >
                        Copy Link
                      </button>
                      <a
                        href={`${baseUrl}#/live-quiz`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-2 rounded-xl bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 text-xs font-bold border border-indigo-500/20 flex items-center"
                      >
                        <SafeIcon icon={FiExternalLink} />
                      </a>
                    </div>
                  </div>

                  {/* Kiosk 2: Classroom Word Scramble */}
                  <div className="glass-panel p-5 rounded-2xl bg-white dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 flex flex-col justify-between shadow-sm">
                    <div className="flex items-start gap-4">
                      <div className="bg-white p-2 rounded-xl border border-slate-200 shadow-md flex-shrink-0">
                        <QRCode value={`${baseUrl}#/live-scramble`} size={70} fgColor="#0f172a" level="L" />
                      </div>
                      <div>
                        <span className="text-[10px] font-black uppercase text-emerald-500 tracking-wider">Projector Display 2</span>
                        <h4 className="text-sm font-bold text-slate-900 dark:text-white mt-0.5">Speed Word Scramble</h4>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                          Unscramble Indian heritage words with hints and 4-house scoring!
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-4 pt-3 border-t border-slate-100 dark:border-slate-700/60">
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(`${baseUrl}#/live-scramble`);
                          alert("Word Scramble link copied!");
                        }}
                        className="flex-1 py-2 text-xs font-bold rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                      >
                        Copy Link
                      </button>
                      <a
                        href={`${baseUrl}#/live-scramble`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-2 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-xs font-bold border border-emerald-500/20 flex items-center"
                      >
                        <SafeIcon icon={FiExternalLink} />
                      </a>
                    </div>
                  </div>

                  {/* Kiosk 3: Live Emoji Riddle Quest */}
                  <div className="glass-panel p-5 rounded-2xl bg-white dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 flex flex-col justify-between shadow-sm">
                    <div className="flex items-start gap-4">
                      <div className="bg-white p-2 rounded-xl border border-slate-200 shadow-md flex-shrink-0">
                        <QRCode value={`${baseUrl}#/live-emoji`} size={70} fgColor="#0f172a" level="L" />
                      </div>
                      <div>
                        <span className="text-[10px] font-black uppercase text-purple-500 tracking-wider">Projector Display 3 (NEW)</span>
                        <h4 className="text-sm font-bold text-slate-900 dark:text-white mt-0.5">Live Emoji Riddle Quest</h4>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                          Big emoji puzzles for science, space & Indian icons. 25s timer & house points.
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-4 pt-3 border-t border-slate-100 dark:border-slate-700/60">
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(`${baseUrl}#/live-emoji`);
                          alert("Emoji Riddle Quest link copied!");
                        }}
                        className="flex-1 py-2 text-xs font-bold rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                      >
                        Copy Link
                      </button>
                      <a
                        href={`${baseUrl}#/live-emoji`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-2 rounded-xl bg-purple-500/10 text-purple-600 dark:text-purple-400 text-xs font-bold border border-purple-500/20 flex items-center"
                      >
                        <SafeIcon icon={FiExternalLink} />
                      </a>
                    </div>
                  </div>
                </div>
                </div>
              )}

              {/* CATEGORY 2: KIDS EVENT STALL & TABLET GAMES */}
              {['all', 'stall'].includes(schoolGamesFilter) && (
                <div className="space-y-3 pt-3">
                  <div className="flex items-center gap-2">
                  <span className="text-xl">🎪</span>
                  <h3 className="text-base font-black text-slate-800 dark:text-white uppercase tracking-wider">
                    Kids Event Stall & Tablet Kiosks (Ages 6+)
                  </h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Stall 1: Balloon Pop */}
                  <div className="glass-panel p-5 rounded-2xl bg-white dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 flex flex-col justify-between shadow-sm">
                    <div className="flex items-start gap-4">
                      <div className="bg-white p-2 rounded-xl border border-slate-200 shadow-md flex-shrink-0">
                        <QRCode value={`${baseUrl}#/balloon`} size={70} fgColor="#0f172a" level="L" />
                      </div>
                      <div>
                        <span className="text-[10px] font-black uppercase text-pink-500 tracking-wider">Stall Tablet 1</span>
                        <h4 className="text-sm font-bold text-slate-900 dark:text-white mt-0.5">Balloon Pop Carnival</h4>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                          Kids tap floating balloons to burst with realistic pops and win prizes.
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-4 pt-3 border-t border-slate-100 dark:border-slate-700/60">
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(`${baseUrl}#/balloon`);
                          alert("Balloon Carnival link copied!");
                        }}
                        className="flex-1 py-2 text-xs font-bold rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                      >
                        Copy Link
                      </button>
                      <a
                        href={`${baseUrl}#/balloon`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-2 rounded-xl bg-pink-500/10 text-pink-600 dark:text-pink-400 text-xs font-bold border border-pink-500/20 flex items-center"
                      >
                        <SafeIcon icon={FiExternalLink} />
                      </a>
                    </div>
                  </div>

                  {/* Stall 2: Treasure Box */}
                  <div className="glass-panel p-5 rounded-2xl bg-white dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 flex flex-col justify-between shadow-sm">
                    <div className="flex items-start gap-4">
                      <div className="bg-white p-2 rounded-xl border border-slate-200 shadow-md flex-shrink-0">
                        <QRCode value={`${baseUrl}#/treasure`} size={70} fgColor="#0f172a" level="L" />
                      </div>
                      <div>
                        <span className="text-[10px] font-black uppercase text-amber-500 tracking-wider">Stall Tablet 2</span>
                        <h4 className="text-sm font-bold text-slate-900 dark:text-white mt-0.5">Magic Treasure Chest</h4>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                          Kids tap mystery chests to unlock gifts, caps, and badges with sound effects.
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-4 pt-3 border-t border-slate-100 dark:border-slate-700/60">
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(`${baseUrl}#/treasure`);
                          alert("Treasure Box link copied!");
                        }}
                        className="flex-1 py-2 text-xs font-bold rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                      >
                        Copy Link
                      </button>
                      <a
                        href={`${baseUrl}#/treasure`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-2 rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400 text-xs font-bold border border-amber-500/20 flex items-center"
                      >
                        <SafeIcon icon={FiExternalLink} />
                      </a>
                    </div>
                  </div>

                  {/* Stall 3: Candy Piñata Bash */}
                  <div className="glass-panel p-5 rounded-2xl bg-white dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 flex flex-col justify-between shadow-sm">
                    <div className="flex items-start gap-4">
                      <div className="bg-white p-2 rounded-xl border border-slate-200 shadow-md flex-shrink-0">
                        <QRCode value={`${baseUrl}#/pinata`} size={70} fgColor="#0f172a" level="L" />
                      </div>
                      <div>
                        <span className="text-[10px] font-black uppercase text-purple-500 tracking-wider">Stall Tablet 3 (NEW)</span>
                        <h4 className="text-sm font-bold text-slate-900 dark:text-white mt-0.5">Candy Piñata Bash</h4>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                          Interactive 3D swinging piñata. Child taps to bash open with candy explosions.
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-4 pt-3 border-t border-slate-100 dark:border-slate-700/60">
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(`${baseUrl}#/pinata`);
                          alert("Candy Piñata link copied!");
                        }}
                        className="flex-1 py-2 text-xs font-bold rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                      >
                        Copy Link
                      </button>
                      <a
                        href={`${baseUrl}#/pinata`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-2 rounded-xl bg-purple-500/10 text-purple-600 dark:text-purple-400 text-xs font-bold border border-purple-500/20 flex items-center"
                      >
                        <SafeIcon icon={FiExternalLink} />
                      </a>
                    </div>
                  </div>
                </div>
                </div>
              )}

              {/* CATEGORY 3: JUNIOR CHAMPS & TODDLERS (AGES 3-7 YEARS) */}
              {['all', 'junior'].includes(schoolGamesFilter) && (
                <div className="space-y-3 pt-3">
                  <div className="flex items-center gap-2">
                  <span className="text-xl">🧸</span>
                  <h3 className="text-base font-black text-slate-800 dark:text-white uppercase tracking-wider">
                    Junior Champions & Toddlers (Ages 3-7 Years)
                  </h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Junior 1: Jungle Safari Animal Match */}
                  <div className="glass-panel p-5 rounded-2xl bg-white dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 flex flex-col justify-between shadow-sm">
                    <div className="flex items-start gap-4">
                      <div className="bg-white p-2 rounded-xl border border-slate-200 shadow-md flex-shrink-0">
                        <QRCode value={`${baseUrl}#/animal-match`} size={70} fgColor="#0f172a" level="L" />
                      </div>
                      <div>
                        <span className="text-[10px] font-black uppercase text-amber-500 tracking-wider">Junior Wonder 1 (Ages 3-7)</span>
                        <h4 className="text-sm font-bold text-slate-900 dark:text-white mt-0.5">Jungle Safari Animal Match</h4>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                          Big friendly animal cards (Lion, Elephant, Peacock). Gentle sounds, no timer stress, Safari Explorer badges.
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-4 pt-3 border-t border-slate-100 dark:border-slate-700/60">
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(`${baseUrl}#/animal-match`);
                          alert("Animal Safari Match link copied!");
                        }}
                        className="flex-1 py-2 text-xs font-bold rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                      >
                        Copy Link
                      </button>
                      <a
                        href={`${baseUrl}#/animal-match`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-2 rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400 text-xs font-bold border border-amber-500/20 flex items-center"
                      >
                        <SafeIcon icon={FiExternalLink} />
                      </a>
                    </div>
                  </div>

                  {/* Junior 2: Rainbow Bubble & Star Pop */}
                  <div className="glass-panel p-5 rounded-2xl bg-white dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 flex flex-col justify-between shadow-sm">
                    <div className="flex items-start gap-4">
                      <div className="bg-white p-2 rounded-xl border border-slate-200 shadow-md flex-shrink-0">
                        <QRCode value={`${baseUrl}#/rainbow-pop`} size={70} fgColor="#0f172a" level="L" />
                      </div>
                      <div>
                        <span className="text-[10px] font-black uppercase text-sky-500 tracking-wider">Junior Wonder 2 (Ages 3-7)</span>
                        <h4 className="text-sm font-bold text-slate-900 dark:text-white mt-0.5">Rainbow Bubble & Star Pop</h4>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                          Floating bubbles, smiling stars, and fruit pops. Touch to burst with water pops and earn Little Star certificates.
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-4 pt-3 border-t border-slate-100 dark:border-slate-700/60">
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(`${baseUrl}#/rainbow-pop`);
                          alert("Rainbow Bubble Pop link copied!");
                        }}
                        className="flex-1 py-2 text-xs font-bold rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                      >
                        Copy Link
                      </button>
                      <a
                        href={`${baseUrl}#/rainbow-pop`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-2 rounded-xl bg-sky-500/10 text-sky-600 dark:text-sky-400 text-xs font-bold border border-sky-500/20 flex items-center"
                      >
                        <SafeIcon icon={FiExternalLink} />
                      </a>
                    </div>
                  </div>
                </div>
                </div>
              )}

              {/* CATEGORY 4: SPACE & SCIENCE STEM EXPLORATORIUM (NEW) */}
              {['all', 'stem'].includes(schoolGamesFilter) && (
                <div className="space-y-3 pt-3">
                  <div className="flex items-center gap-2">
                  <span className="text-xl">🚀</span>
                  <h3 className="text-base font-black text-slate-800 dark:text-white uppercase tracking-wider">
                    Space & Science STEM Exploratorium (ISRO, Physics & Chemistry)
                  </h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* STEM 1: Mission Chandrayaan */}
                  <div className="glass-panel p-5 rounded-2xl bg-white dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 flex flex-col justify-between shadow-sm">
                    <div className="flex items-start gap-3">
                      <div className="bg-white p-2 rounded-xl border border-slate-200 shadow-md flex-shrink-0">
                        <QRCode value={`${baseUrl}#/chandrayaan`} size={64} fgColor="#0f172a" level="L" />
                      </div>
                      <div>
                        <span className="text-[10px] font-black uppercase text-orange-500 tracking-wider">Projector Display</span>
                        <h4 className="text-xs font-bold text-slate-900 dark:text-white mt-0.5">Mission Chandrayaan</h4>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1 leading-tight">
                          Lunar South Pole descent. Answer physics questions to fire braking thrusters!
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-4 pt-3 border-t border-slate-100 dark:border-slate-700/60">
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(`${baseUrl}#/chandrayaan`);
                          alert("Chandrayaan link copied!");
                        }}
                        className="flex-1 py-1.5 text-xs font-bold rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                      >
                        Copy Link
                      </button>
                      <a
                        href={`${baseUrl}#/chandrayaan`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-2.5 py-1.5 rounded-xl bg-orange-500/10 text-orange-600 dark:text-orange-400 text-xs font-bold border border-orange-500/20 flex items-center"
                      >
                        <SafeIcon icon={FiExternalLink} />
                      </a>
                    </div>
                  </div>

                  {/* STEM 2: Astro-Engineer Rocket Builder */}
                  <div className="glass-panel p-5 rounded-2xl bg-white dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 flex flex-col justify-between shadow-sm">
                    <div className="flex items-start gap-3">
                      <div className="bg-white p-2 rounded-xl border border-slate-200 shadow-md flex-shrink-0">
                        <QRCode value={`${baseUrl}#/rocket-builder`} size={64} fgColor="#0f172a" level="L" />
                      </div>
                      <div>
                        <span className="text-[10px] font-black uppercase text-cyan-500 tracking-wider">Stall Tablet</span>
                        <h4 className="text-xs font-bold text-slate-900 dark:text-white mt-0.5">Astro-Engineer Rocket</h4>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1 leading-tight">
                          Kids customize rocket stages, fuel & payload, then launch into Earth orbit!
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-4 pt-3 border-t border-slate-100 dark:border-slate-700/60">
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(`${baseUrl}#/rocket-builder`);
                          alert("Rocket Builder link copied!");
                        }}
                        className="flex-1 py-1.5 text-xs font-bold rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                      >
                        Copy Link
                      </button>
                      <a
                        href={`${baseUrl}#/rocket-builder`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-2.5 py-1.5 rounded-xl bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 text-xs font-bold border border-cyan-500/20 flex items-center"
                      >
                        <SafeIcon icon={FiExternalLink} />
                      </a>
                    </div>
                  </div>

                  {/* STEM 3: Cosmic Gravity & Weight */}
                  <div className="glass-panel p-5 rounded-2xl bg-white dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 flex flex-col justify-between shadow-sm">
                    <div className="flex items-start gap-3">
                      <div className="bg-white p-2 rounded-xl border border-slate-200 shadow-md flex-shrink-0">
                        <QRCode value={`${baseUrl}#/gravity-lab`} size={64} fgColor="#0f172a" level="L" />
                      </div>
                      <div>
                        <span className="text-[10px] font-black uppercase text-amber-500 tracking-wider">Physics Lab</span>
                        <h4 className="text-xs font-bold text-slate-900 dark:text-white mt-0.5">Cosmic Gravity Explorer</h4>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1 leading-tight">
                          Compare weights on Moon, Mars, Jupiter & Sun with high-jump simulator.
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-4 pt-3 border-t border-slate-100 dark:border-slate-700/60">
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(`${baseUrl}#/gravity-lab`);
                          alert("Gravity Lab link copied!");
                        }}
                        className="flex-1 py-1.5 text-xs font-bold rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                      >
                        Copy Link
                      </button>
                      <a
                        href={`${baseUrl}#/gravity-lab`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-2.5 py-1.5 rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400 text-xs font-bold border border-amber-500/20 flex items-center"
                      >
                        <SafeIcon icon={FiExternalLink} />
                      </a>
                    </div>
                  </div>

                  {/* STEM 4: Little Alchemist Color Lab */}
                  <div className="glass-panel p-5 rounded-2xl bg-white dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 flex flex-col justify-between shadow-sm">
                    <div className="flex items-start gap-3">
                      <div className="bg-white p-2 rounded-xl border border-slate-200 shadow-md flex-shrink-0">
                        <QRCode value={`${baseUrl}#/color-lab`} size={64} fgColor="#0f172a" level="L" />
                      </div>
                      <div>
                        <span className="text-[10px] font-black uppercase text-purple-500 tracking-wider">Junior STEM (Ages 3-7)</span>
                        <h4 className="text-xs font-bold text-slate-900 dark:text-white mt-0.5">Magic Color Chemistry</h4>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1 leading-tight">
                          Toddlers pour test tubes to discover color mixing & potion rewards.
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-4 pt-3 border-t border-slate-100 dark:border-slate-700/60">
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(`${baseUrl}#/color-lab`);
                          alert("Color Lab link copied!");
                        }}
                        className="flex-1 py-1.5 text-xs font-bold rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                      >
                        Copy Link
                      </button>
                      <a
                        href={`${baseUrl}#/color-lab`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-2.5 py-1.5 rounded-xl bg-purple-500/10 text-purple-600 dark:text-purple-400 text-xs font-bold border border-purple-500/20 flex items-center"
                      >
                        <SafeIcon icon={FiExternalLink} />
                      </a>
                    </div>
                  </div>

                  {/* STEM 5: Solar System Planetary Orrery (NEW) */}
                  <div className="glass-panel p-5 rounded-2xl bg-white dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 flex flex-col justify-between shadow-sm">
                    <div className="flex items-start gap-3">
                      <div className="bg-white p-2 rounded-xl border border-slate-200 shadow-md flex-shrink-0">
                        <QRCode value={`${baseUrl}#/solar-system`} size={64} fgColor="#0f172a" level="L" />
                      </div>
                      <div>
                        <span className="text-[10px] font-black uppercase text-amber-500 tracking-wider">Stage & Kiosk STEM</span>
                        <h4 className="text-xs font-bold text-slate-900 dark:text-white mt-0.5">Solar System Planetary Orrery</h4>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1 leading-tight">
                          3D orbital Orrery with 8 planets, ISRO missions (Aditya-L1, Mangalyaan) & quiz.
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-4 pt-3 border-t border-slate-100 dark:border-slate-700/60">
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(`${baseUrl}#/solar-system`);
                          alert("Solar System link copied!");
                        }}
                        className="flex-1 py-1.5 text-xs font-bold rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                      >
                        Copy Link
                      </button>
                      <a
                        href={`${baseUrl}#/solar-system`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-2.5 py-1.5 rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400 text-xs font-bold border border-amber-500/20 flex items-center"
                      >
                        <SafeIcon icon={FiExternalLink} />
                      </a>
                    </div>
                  </div>

                  {/* STEM 6: Dino Fossil Dig & Paleontologist Lab (NEW) */}
                  <div className="glass-panel p-5 rounded-2xl bg-white dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 flex flex-col justify-between shadow-sm">
                    <div className="flex items-start gap-3">
                      <div className="bg-white p-2 rounded-xl border border-slate-200 shadow-md flex-shrink-0">
                        <QRCode value={`${baseUrl}#/dino-dig`} size={64} fgColor="#0f172a" level="L" />
                      </div>
                      <div>
                        <span className="text-[10px] font-black uppercase text-amber-600 tracking-wider">Earth Science STEM</span>
                        <h4 className="text-xs font-bold text-slate-900 dark:text-white mt-0.5">Dino Fossil Dig & Paleontologist</h4>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1 leading-tight">
                          Excavate T-Rex skull, Triceratops frill, amber fossil with chisel & whisk brush.
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-4 pt-3 border-t border-slate-100 dark:border-slate-700/60">
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(`${baseUrl}#/dino-dig`);
                          alert("Dino Dig link copied!");
                        }}
                        className="flex-1 py-1.5 text-xs font-bold rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                      >
                        Copy Link
                      </button>
                      <a
                        href={`${baseUrl}#/dino-dig`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-2.5 py-1.5 rounded-xl bg-amber-600/10 text-amber-700 dark:text-amber-500 text-xs font-bold border border-amber-600/20 flex items-center"
                      >
                        <SafeIcon icon={FiExternalLink} />
                      </a>
                    </div>
                  </div>
                </div>
                </div>
              )}
            </motion.div>
          )}

          {/* TAB: SCHOOL HOUSE CHAMPIONSHIP BAROMETER */}
          {activeTab === 'school_barometer' && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
              {renderSchoolModuleNav()}

              <AdminTabErrorBoundary moduleName="House Championship Barometer">
                <SchoolSportsBarometerManager />
              </AdminTabErrorBoundary>
            </motion.div>
          )}

          {/* TAB: INTER-HOUSE CHAMPIONS ROLL OF HONOR & AWARDS */}
          {activeTab === 'school_champions' && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
              {renderSchoolModuleNav()}

              <div className="glass-panel p-6 md:p-8 rounded-2xl bg-white dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 space-y-6 shadow-sm">
                
                {/* Header & Actions */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 border-b border-slate-200 dark:border-slate-700">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-2xl">🏆</span>
                      <h2 className="text-xl font-black text-slate-900 dark:text-white">
                        Inter-House Champions Roll of Honor
                      </h2>
                      <span className="px-2.5 py-0.5 rounded-full bg-amber-500/20 text-amber-700 dark:text-amber-400 border border-amber-500/30 text-[10px] font-black uppercase">
                        {(interHouseChampions || []).length} Titles
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Official record of announced house trophies, championship titles, and prize distribution logs across all tournament events.
                    </p>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      type="button"
                      onClick={() => setShowManualChampModal(true)}
                      className="px-4 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-slate-950 font-black text-xs shadow-md transition-all flex items-center gap-1.5 cursor-pointer"
                    >
                      <SafeIcon icon={FiPlus} />
                      <span>Announce Champion Manually</span>
                    </button>

                    <a
                      href={`${baseUrl}#/live-house-barometer`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 rounded-xl bg-indigo-50 dark:bg-indigo-950/50 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 font-black text-xs border border-indigo-200 dark:border-indigo-800 transition-all flex items-center gap-1.5"
                    >
                      <span>Launch Projector Stage</span>
                      <SafeIcon icon={FiExternalLink} />
                    </a>
                  </div>
                </div>

                {/* 4 Summary Stats Cards */}
                {(() => {
                  const houseCounts = {};
                  let totalPoints = 0;
                  (interHouseChampions || []).forEach(c => {
                    houseCounts[c.houseName] = (houseCounts[c.houseName] || 0) + 1;
                    totalPoints += Number(c.points) || 0;
                  });
                  let topHouse = 'None yet';
                  let topCount = 0;
                  Object.entries(houseCounts).forEach(([name, count]) => {
                    if (count > topCount) {
                      topCount = count;
                      topHouse = `${name} (${count}x)`;
                    }
                  });

                  return (
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                      <div className="p-4 rounded-xl bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/30">
                        <div className="text-[10px] font-black uppercase text-amber-600 dark:text-amber-400">Total Champions Crowned</div>
                        <div className="text-2xl font-black text-slate-900 dark:text-white mt-1">{(interHouseChampions || []).length}</div>
                        <div className="text-[11px] text-slate-400 mt-0.5">Official Trophies Awarded</div>
                      </div>

                      <div className="p-4 rounded-xl bg-purple-50 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-900/30">
                        <div className="text-[10px] font-black uppercase text-purple-600 dark:text-purple-400">Leading House</div>
                        <div className="text-xl font-black text-purple-700 dark:text-purple-300 mt-1 truncate">{topHouse}</div>
                        <div className="text-[11px] text-slate-400 mt-0.5">Most Championship Honors</div>
                      </div>

                      <div className="p-4 rounded-xl bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900/30">
                        <div className="text-[10px] font-black uppercase text-emerald-600 dark:text-emerald-400">Total Points Awarded</div>
                        <div className="text-2xl font-black text-emerald-700 dark:text-emerald-300 mt-1 font-mono">{totalPoints}</div>
                        <div className="text-[11px] text-slate-400 mt-0.5">Across All Tournaments</div>
                      </div>

                      <div className="p-4 rounded-xl bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900/30">
                        <div className="text-[10px] font-black uppercase text-blue-600 dark:text-blue-400">Latest Winner</div>
                        <div className="text-lg font-black text-blue-700 dark:text-blue-300 mt-1 truncate">
                          {interHouseChampions?.[0]?.houseName || 'Pending'}
                        </div>
                        <div className="text-[11px] text-slate-400 mt-0.5 truncate">
                          {interHouseChampions?.[0]?.awardType || 'Awaiting announcement'}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Filter by House */}
                <div className="flex items-center justify-between gap-3 flex-wrap pt-2">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-xs font-bold text-slate-400 mr-1">Filter House:</span>
                    {['ALL', 'red', 'blue', 'green', 'yellow'].map(hKey => {
                      const label = hKey === 'ALL' ? 'All Houses' : {
                        red: '🦁 Ruby Lions',
                        blue: '🦅 Sapphire Eagles',
                        green: '🐯 Emerald Tigers',
                        yellow: '⚡ Golden Falcons'
                      }[hKey];

                      return (
                        <button
                          key={hKey}
                          type="button"
                          onClick={() => setChampFilterHouse(hKey)}
                          className={`px-3 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                            champFilterHouse === hKey
                              ? 'bg-amber-500 text-slate-950 font-black shadow-sm'
                              : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200'
                          }`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>

                  <span className="text-xs text-slate-400">
                    Showing {(interHouseChampions || []).filter(c => champFilterHouse === 'ALL' || c.houseId === champFilterHouse).length} of {(interHouseChampions || []).length} records
                  </span>
                </div>

                {/* Champions List / Cards */}
                {(() => {
                  const filtered = (interHouseChampions || []).filter(c => champFilterHouse === 'ALL' || c.houseId === champFilterHouse);

                  if (filtered.length === 0) {
                    return (
                      <div className="p-12 text-center rounded-2xl bg-slate-50 dark:bg-slate-900/50 border-2 border-dashed border-slate-200 dark:border-slate-800 space-y-4">
                        <div className="w-16 h-16 mx-auto rounded-full bg-amber-500/10 text-amber-500 flex items-center justify-center text-3xl">
                          🏆
                        </div>
                        <h4 className="text-base font-black text-slate-800 dark:text-white">
                          No Inter-House Champions Recorded Yet
                        </h4>
                        <p className="text-xs text-slate-500 dark:text-slate-400 max-w-md mx-auto">
                          Announce champions when houses reach the points threshold on the Live Projector Barometer Stage, or announce a champion manually using the button below.
                        </p>
                        <div className="flex justify-center gap-3 pt-2">
                          <button
                            type="button"
                            onClick={() => setShowManualChampModal(true)}
                            className="px-5 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-slate-950 font-black text-xs shadow-md transition-all"
                          >
                            + Announce Champion Now
                          </button>
                          <a
                            href={`${baseUrl}#/live-house-barometer`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-5 py-2.5 rounded-xl bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-white font-bold text-xs"
                          >
                            Open Projector Stage
                          </a>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {filtered.map(champ => {
                        const houseColors = {
                          red: 'border-red-500/40 bg-gradient-to-br from-red-500/10 to-transparent',
                          blue: 'border-blue-500/40 bg-gradient-to-br from-blue-500/10 to-transparent',
                          green: 'border-emerald-500/40 bg-gradient-to-br from-emerald-500/10 to-transparent',
                          yellow: 'border-amber-500/40 bg-gradient-to-br from-amber-500/10 to-transparent'
                        }[champ.houseId] || 'border-indigo-500/40 bg-indigo-500/10';

                        return (
                          <div
                            key={champ.id}
                            className={`p-5 rounded-2xl border-2 ${houseColors} shadow-md relative overflow-hidden flex flex-col justify-between`}
                          >
                            <div>
                              {/* Top Bar */}
                              <div className="flex items-start justify-between gap-3 mb-3">
                                <div className="flex items-center gap-2.5">
                                  <span className="text-3xl">{champ.badge || '🏆'}</span>
                                  <div>
                                    <h4 className="text-base font-black text-slate-900 dark:text-white">
                                      {champ.houseName}
                                    </h4>
                                    <div className="flex items-center gap-2 text-[11px] text-slate-500 dark:text-slate-400">
                                      <span className="font-bold text-amber-600 dark:text-amber-400">
                                        {champ.eventSource || 'Inter-House Championship'}
                                      </span>
                                      <span>&bull;</span>
                                      <span>{new Date(champ.timestamp).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                                    </div>
                                  </div>
                                </div>

                                <span className="px-2.5 py-1 rounded-xl bg-slate-900 text-amber-400 font-mono font-black text-xs shadow-sm">
                                  {champ.points} PTS
                                </span>
                              </div>

                              {/* Award Type Banner */}
                              <div className="p-3 rounded-xl bg-white/70 dark:bg-slate-900/70 border border-slate-200 dark:border-slate-700/80 mb-3">
                                <div className="text-[10px] font-black uppercase text-amber-600 dark:text-amber-400 flex items-center gap-1 mb-0.5">
                                  <SafeIcon icon={FiAward} /> Award Bestowed
                                </div>
                                <div className="text-sm font-black text-slate-900 dark:text-white">
                                  {champ.awardType}
                                </div>
                              </div>

                              {/* Prize Distribution Info */}
                              {champ.prizeDistributionInfo && (
                                <div className="text-xs text-slate-600 dark:text-slate-300 bg-black/5 dark:bg-white/5 p-2.5 rounded-lg mb-3">
                                  <span className="font-bold text-slate-700 dark:text-slate-200">🎁 Prize Distribution Info: </span>
                                  <span>{champ.prizeDistributionInfo}</span>
                                </div>
                              )}
                            </div>

                            {/* Card Footer */}
                            <div className="pt-3 border-t border-slate-200/60 dark:border-slate-700/60 flex items-center justify-between text-xs">
                              <span className="text-slate-400 text-[11px]">
                                Announced by: <strong className="text-slate-600 dark:text-slate-300">{champ.announcedBy || 'Referee'}</strong>
                              </span>

                              <button
                                type="button"
                                onClick={() => handleDeleteChampionRecord(champ.id)}
                                className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-500/10 transition-colors"
                                title="Remove from Roll of Honor"
                              >
                                <SafeIcon icon={FiTrash2} />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}

              </div>

              {/* MANUAL CHAMPION ANNOUNCEMENT MODAL */}
              <AnimatePresence>
                {showManualChampModal && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-md flex items-center justify-center p-4 sm:p-6 overflow-y-auto"
                  >
                    <motion.div
                      initial={{ scale: 0.95, y: 15 }}
                      animate={{ scale: 1, y: 0 }}
                      exit={{ scale: 0.95, y: 15 }}
                      className="bg-white dark:bg-slate-900 border-2 border-amber-500/50 rounded-3xl max-w-lg w-full p-6 shadow-2xl relative my-8"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-black text-slate-900 dark:text-white flex items-center gap-2">
                          <span>🏆 Announce Champion Award</span>
                        </h3>
                        <button
                          type="button"
                          onClick={() => setShowManualChampModal(false)}
                          className="p-1.5 rounded-xl text-slate-400 hover:text-slate-700 dark:hover:text-white"
                        >
                          <SafeIcon icon={FiX} className="text-lg" />
                        </button>
                      </div>

                      <form onSubmit={handleManualAnnounceChampion} className="space-y-3.5">
                        {/* House Selector */}
                        <div>
                          <label className="block text-[11px] font-black uppercase text-slate-500 mb-1">
                            Winning House
                          </label>
                          <select
                            value={manualChampForm.houseId}
                            onChange={e => setManualChampForm({ ...manualChampForm, houseId: e.target.value })}
                            className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-xl px-3 py-2 text-xs font-bold text-slate-900 dark:text-white outline-none focus:border-amber-500"
                          >
                            <option value="red">🦁 Ruby Lions (Red House)</option>
                            <option value="blue">🦅 Sapphire Eagles (Blue House)</option>
                            <option value="green">🐯 Emerald Tigers (Green House)</option>
                            <option value="yellow">⚡ Golden Falcons (Yellow House)</option>
                          </select>
                        </div>

                        {/* Award Type */}
                        <div>
                          <label className="block text-[11px] font-black uppercase text-slate-500 mb-1">
                            Award / Trophy Title
                          </label>
                          <select
                            value={manualChampForm.awardType}
                            onChange={e => setManualChampForm({ ...manualChampForm, awardType: e.target.value })}
                            className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-xl px-3 py-2 text-xs font-bold text-slate-900 dark:text-white outline-none focus:border-amber-500"
                          >
                            <option value="🏆 Overall Annual Inter-House Championship Trophy">🏆 Overall Annual Inter-House Championship Trophy</option>
                            <option value="🥇 Sports Day Inter-House Gold Cup">🥇 Sports Day Inter-House Gold Cup</option>
                            <option value="🌟 General Championship Shield">🌟 General Championship Shield</option>
                            <option value="🎯 Inter-House Quiz & Cultural Trophy">🎯 Inter-House Quiz & Cultural Trophy</option>
                            <option value="⚡ Outstanding House Spirit & March Past Trophy">⚡ Outstanding House Spirit & March Past Trophy</option>
                            <option value="🎖️ Academic & STEM Excellence Shield">🎖️ Academic & STEM Excellence Shield</option>
                            <option value="CUSTOM">✏️ Custom Trophy Name...</option>
                          </select>
                          {manualChampForm.awardType === 'CUSTOM' && (
                            <input
                              type="text"
                              placeholder="Type custom trophy title..."
                              value={manualChampForm.customAward}
                              onChange={e => setManualChampForm({ ...manualChampForm, customAward: e.target.value })}
                              className="mt-1.5 w-full bg-slate-50 dark:bg-slate-800 border border-amber-400 rounded-xl px-3 py-2 text-xs font-bold outline-none"
                              autoFocus
                            />
                          )}
                        </div>

                        {/* Points & Source */}
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-[11px] font-black uppercase text-slate-500 mb-1">
                              Points Won
                            </label>
                            <input
                              type="number"
                              value={manualChampForm.points}
                              onChange={e => setManualChampForm({ ...manualChampForm, points: e.target.value })}
                              className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-xl px-3 py-2 text-xs font-bold outline-none"
                            />
                          </div>
                          <div>
                            <label className="block text-[11px] font-black uppercase text-slate-500 mb-1">
                              Tournament Source
                            </label>
                            <input
                              type="text"
                              value={manualChampForm.eventSource}
                              onChange={e => setManualChampForm({ ...manualChampForm, eventSource: e.target.value })}
                              className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-xl px-3 py-2 text-xs font-bold outline-none"
                            />
                          </div>
                        </div>

                        {/* Prize Distribution Info */}
                        <div>
                          <label className="block text-[11px] font-black uppercase text-slate-500 mb-1">
                            Prize Distribution Details
                          </label>
                          <textarea
                            rows={2}
                            value={manualChampForm.prizeDistributionInfo}
                            onChange={e => setManualChampForm({ ...manualChampForm, prizeDistributionInfo: e.target.value })}
                            className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-xl px-3 py-2 text-xs font-medium outline-none"
                          />
                        </div>

                        {/* Announced By */}
                        <div>
                          <label className="block text-[11px] font-black uppercase text-slate-500 mb-1">
                            Announced By
                          </label>
                          <input
                            type="text"
                            value={manualChampForm.announcedBy}
                            onChange={e => setManualChampForm({ ...manualChampForm, announcedBy: e.target.value })}
                            className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-xl px-3 py-2 text-xs font-bold outline-none"
                          />
                        </div>

                        <div className="flex justify-end gap-2 pt-3 border-t border-slate-100 dark:border-slate-800">
                          <button
                            type="button"
                            onClick={() => setShowManualChampModal(false)}
                            className="px-4 py-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-xs font-bold"
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className="px-5 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 text-slate-950 font-black text-xs shadow-md"
                          >
                            Announce & Save Record 🏆
                          </button>
                        </div>
                      </form>
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>

            </motion.div>
          )}

          {/* TAB: STUDENT PHYSICAL AWARDS & REWARDS MANAGER */}
          {activeTab === 'school_awards' && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
              {renderSchoolModuleNav()}

              {/* School Awards & Physical Merchandise Manager (Customizable by School / Event Owner) */}
              <div className="glass-panel p-6 md:p-8 rounded-2xl bg-white dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                      <SafeIcon icon={FiAward} className="text-amber-500" /> Student Physical Awards, Caps & Merchandise Manager
                    </h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      Customize physical reward items and tokens available to present to students based on school inventory, sponsor gifts, or principal preferences.
                    </p>
                  </div>

                  <div className="flex items-center gap-2 self-start sm:self-auto">
                    <span className="text-xs font-bold text-slate-400">
                      {(schoolAwards || []).length} Active Rewards
                    </span>
                    <button
                      type="button"
                      onClick={handleResetSchoolAwards}
                      className="px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 text-xs font-bold hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors flex items-center gap-1.5 cursor-pointer"
                      title="Reset back to default school awards"
                    >
                      <SafeIcon icon={FiRefreshCw} /> Reset Defaults
                    </button>
                  </div>
                </div>

                {/* Add Custom Award Form */}
                <form onSubmit={handleAddSchoolAward} className="p-4 rounded-xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700/80 space-y-3">
                  <div className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider flex items-center gap-2">
                    <SafeIcon icon={FiPlus} className="text-indigo-500" /> Add Custom Physical Award / Token
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-12 gap-2.5">
                    {/* Emoji Selector */}
                    <div className="sm:col-span-2">
                      <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">Badge Icon</label>
                      <select
                        value={awardEmojiInput}
                        onChange={e => setAwardEmojiInput(e.target.value)}
                        className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm font-bold focus:outline-none focus:ring-2 focus:ring-indigo-400"
                      >
                        <option value="🎖️">🎖️ Badge</option>
                        <option value="🥇">🥇 1st Gold</option>
                        <option value="🥈">🥈 2nd Silver</option>
                        <option value="🥉">🥉 3rd Bronze</option>
                        <option value="🏆">🏆 Trophy</option>
                        <option value="🧢">🧢 Cap</option>
                        <option value="🎒">🎒 Bag</option>
                        <option value="✏️">✏️ Pencil Box</option>
                        <option value="📜">📜 Certificate</option>
                        <option value="🍫">🍫 Chocolate</option>
                        <option value="🌟">🌟 Star Pin</option>
                        <option value="🎁">🎁 Hamper</option>
                        <option value="🏷️">🏷️ Wristband</option>
                        <option value="🔬">🔬 STEM Medal</option>
                        <option value="💡">💡 Trophy Pin</option>
                      </select>
                    </div>

                    {/* Award Title */}
                    <div className="sm:col-span-5">
                      <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">Award Title</label>
                      <input
                        type="text"
                        value={awardTitleInput}
                        onChange={e => setAwardTitleInput(e.target.value)}
                        placeholder="e.g. Science Whiz Gold Medal"
                        className="w-full px-3.5 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-xs font-bold focus:outline-none focus:ring-2 focus:ring-indigo-400"
                      />
                    </div>

                    {/* Item Description */}
                    <div className="sm:col-span-3">
                      <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">Item Details / Token</label>
                      <input
                        type="text"
                        value={awardItemInput}
                        onChange={e => setAwardItemInput(e.target.value)}
                        placeholder="e.g. Engraved Metal Medal + Ribbon"
                        className="w-full px-3.5 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-xs font-medium focus:outline-none focus:ring-2 focus:ring-indigo-400"
                      />
                    </div>

                    {/* Submit Button */}
                    <div className="sm:col-span-2 flex items-end">
                      <button
                        type="submit"
                        className="w-full py-2 px-3 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs flex items-center justify-center gap-1.5 shadow-md shadow-indigo-500/20 transition-all cursor-pointer"
                      >
                        <SafeIcon icon={FiPlus} /> Add Award
                      </button>
                    </div>
                  </div>

                  {/* QUICK ADD PRESET CHIPS */}
                  <div className="pt-2 border-t border-slate-200 dark:border-slate-800 flex flex-wrap items-center gap-1.5">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mr-1">
                      Quick Add:
                    </span>
                    {[
                      { emoji: "🥇", title: "Gold Medal of Excellence", item: "Custom Engraved Gold Medal with Ribbon" },
                      { emoji: "🥈", title: "Silver Achievement Medal", item: "Polished Silver Medal with Ribbon" },
                      { emoji: "🥉", title: "Bronze Runner-Up Medal", item: "Bronze Metal Medal with Ribbon" },
                      { emoji: "🧢", title: "School Champion Cap", item: "Custom Embroidered Event Cap" },
                      { emoji: "🎒", title: "STEM School Backpack", item: "Durable Branded Student School Bag" },
                      { emoji: "✏️", title: "Color Stationery Kit", item: "Geometry Box, Crayons & Pencil Set" },
                      { emoji: "🏆", title: "Inter-House Rolling Trophy", item: "Grand Shining Trophy for Winning House" },
                      { emoji: "📜", title: "Principal Certificate of Honor", item: "Embossed Merit Certificate" },
                      { emoji: "🍫", title: "Cadbury Celebrations Treat", item: "Delicious Chocolate Treat Box" },
                      { emoji: "🏷️", title: "Silicone Event Wristband", item: "Glow-in-the-dark House Wristband" },
                      { emoji: "🔬", title: "Science Whiz Enamel Pin", item: "Custom Metallic Lapel Badge" }
                    ].map((preset, pIdx) => (
                      <button
                        key={pIdx}
                        type="button"
                        onClick={() => handleQuickAddAward(preset)}
                        className="px-2.5 py-1 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 text-[11px] font-bold hover:bg-indigo-50 dark:hover:bg-slate-700 hover:text-indigo-600 transition-colors flex items-center gap-1 cursor-pointer shadow-xs"
                      >
                        <span>{preset.emoji}</span>
                        <span>{preset.title.split(' ')[0]} {preset.title.split(' ')[1] || ''}</span>
                      </button>
                    ))}
                  </div>
                </form>

                {/* ACTIVE AWARDS GRID */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {(schoolAwards || []).map(badge => (
                    <div 
                      key={badge.id} 
                      className="p-4 rounded-xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700 flex items-start justify-between gap-3 group hover:border-amber-400 transition-colors"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 rounded-xl bg-amber-500/15 text-amber-500 flex items-center justify-center flex-shrink-0 text-2xl font-bold shadow-xs">
                          {badge.emoji || '🎖️'}
                        </div>
                        <div>
                          <h5 className="font-extrabold text-xs text-slate-800 dark:text-white leading-tight">
                            {badge.title}
                          </h5>
                          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 leading-snug">
                            {badge.item}
                          </p>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleDeleteSchoolAward(badge.id)}
                        className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg transition-colors cursor-pointer"
                        title="Remove award item"
                      >
                        <SafeIcon icon={FiTrash2} />
                      </button>
                    </div>
                  ))}

                  {(schoolAwards || []).length === 0 && (
                    <div className="col-span-full py-8 text-center text-slate-400 text-xs border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-xl">
                      No custom awards configured. Use the form above or click Quick Add chips to populate student gifts.
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* TAB: CURATED INDIAN SCHOOL QUESTION BANK & EXCEL MANAGER */}
          {activeTab === 'school_qbank' && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
              {renderSchoolModuleNav()}

              {/* Question Bank Manager */}
              <div className="glass-panel p-6 md:p-8 rounded-2xl bg-white dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                      <SafeIcon icon={FiHelpCircle} className="text-indigo-500" /> Curated Indian School Question Bank
                    </h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      {(quizQuestions || []).length} questions loaded across Science, Indian History, and General Knowledge.
                    </p>
                  </div>
                  
                  <div className="flex flex-wrap items-center gap-2 self-start sm:self-auto">
                    <button
                      type="button"
                      onClick={() => {
                        setQuizModalTab('upload');
                        setShowAddQuizModal(true);
                      }}
                      className="px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold text-xs flex items-center gap-1.5 shadow-sm transition-all cursor-pointer"
                    >
                      <SafeIcon icon={FiUpload} /> Upload Excel / CSV Sheet
                    </button>
                    <button
                      type="button"
                      onClick={downloadQuizExcelSample}
                      className="px-4 py-2 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-300 dark:border-emerald-800 text-emerald-800 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 font-bold text-xs flex items-center gap-1.5 shadow-xs transition-all cursor-pointer"
                      title="Download sample formatted .XLSX Excel template"
                    >
                      <SafeIcon icon={FiDownload} /> Download Sample .XLSX
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setQuizModalTab('manual');
                        setShowAddQuizModal(true);
                      }}
                      className="px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs flex items-center gap-1.5 transition-colors cursor-pointer"
                    >
                      <SafeIcon icon={FiPlus} /> Add Question
                    </button>
                  </div>
                </div>

                {/* Questions List Table */}
                <div className="space-y-3">
                  {(quizQuestions || []).map((q, idx) => (
                    <div key={q.id || idx} className="p-4 rounded-xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700 flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1 space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-0.5 rounded text-[10px] font-black uppercase bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300">
                            {q.category}
                          </span>
                          <span className="text-[10px] font-bold text-slate-400 uppercase">
                            {q.type === 'mystery' ? '🔍 Pehchano Kaun (Mystery)' : '🎯 Trivia (4-Option)'}
                          </span>
                        </div>

                        <h5 className="font-bold text-sm text-slate-800 dark:text-white">
                          {idx + 1}. {q.type === 'mystery' ? `${q.title} — ${q.question}` : q.question}
                        </h5>

                        {q.type === 'trivia' && (
                          <div className="flex flex-wrap gap-2 pt-1">
                            {q.options.map((opt, oIdx) => (
                              <span 
                                key={oIdx} 
                                className={`text-[11px] px-2 py-0.5 rounded-md ${
                                  oIdx === q.correctIndex 
                                    ? 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 font-bold border border-emerald-300 dark:border-emerald-500/30' 
                                    : 'text-slate-500 bg-slate-200/50 dark:bg-slate-800'
                                }`}
                              >
                                {['A', 'B', 'C', 'D'][oIdx]}. {opt}
                              </span>
                            ))}
                          </div>
                        )}

                        {q.type === 'mystery' && (
                          <div className="text-xs text-amber-600 dark:text-amber-400 font-semibold pt-1">
                            Answer: {q.answer}
                          </div>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={() => {
                          if (window.confirm("Delete this question from question bank?")) {
                            const updated = quizQuestions.filter((_, i) => i !== idx);
                            setQuizQuestions(updated);
                            saveQuizQuestions(updated);
                          }
                        }}
                        className="p-2 text-slate-400 hover:text-red-500 transition-colors self-end md:self-center"
                        title="Delete Question"
                      >
                        <SafeIcon icon={FiTrash2} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* TAB: LIVE EMOJI & VISUAL RIDDLE QUEST BUILDER */}
          {activeTab === 'school_riddles' && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
              {renderSchoolModuleNav()}

              {/* Live Emoji & Visual Riddle Quest Manager (Custom Emojis & Images for Schools / Event Owners) */}
              <div className="glass-panel p-6 md:p-8 rounded-2xl bg-white dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                      <span className="text-xl">🧩</span> Emoji & Visual Riddle Quest Manager
                    </h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      Create interactive visual riddles with custom clue emojis and photos/images to test students' knowledge on stage or classroom projectors.
                    </p>
                  </div>

                  <div className="flex items-center gap-2 self-start sm:self-auto">
                    <span className="text-xs font-bold text-slate-400">
                      {(emojiRiddles || []).length} Active Riddles
                    </span>
                    <button
                      type="button"
                      onClick={handleResetEmojiRiddles}
                      className="px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 text-xs font-bold hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors flex items-center gap-1.5 cursor-pointer"
                      title="Reset back to default curated riddles"
                    >
                      <SafeIcon icon={FiRefreshCw} /> Reset Defaults
                    </button>
                    <a
                      href={`${baseUrl}#/live-emoji`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3.5 py-1.5 rounded-lg bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold flex items-center gap-1.5 shadow-sm"
                    >
                      <SafeIcon icon={FiExternalLink} /> Launch Projector Screen
                    </a>
                  </div>
                </div>

                {/* Create Custom Emoji Riddle Form */}
                <form onSubmit={handleAddEmojiRiddle} className="p-4 sm:p-5 rounded-xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700/80 space-y-4">
                  <div className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider flex items-center gap-2">
                    <SafeIcon icon={FiPlus} className="text-purple-500" /> Add Custom Emoji / Visual Riddle
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-12 gap-3">
                    {/* Category */}
                    <div className="sm:col-span-3">
                      <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">Category</label>
                      <select
                        value={newRiddleForm.category}
                        onChange={e => setNewRiddleForm({ ...newRiddleForm, category: e.target.value })}
                        className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-xs font-bold focus:outline-none focus:ring-2 focus:ring-purple-400"
                      >
                        <option value="Science & Space">Science & Space 🚀</option>
                        <option value="Indian Heritage & Wonders">Indian Heritage 🕌</option>
                        <option value="Animals & Wildlife">Animals & Wildlife 🐅</option>
                        <option value="Great Scientists & Inventions">Scientists & Inventions 💡</option>
                        <option value="Indian Sports Legends">Sports Legends 🏏</option>
                        <option value="Solar System & Astronomy">Solar System 🪐</option>
                        <option value="General Knowledge">General Knowledge 🌍</option>
                      </select>
                    </div>

                    {/* Emojis Clue Sequence & Interactive Palette */}
                    <div className="sm:col-span-5 space-y-2">
                      <div className="flex items-center justify-between">
                        <label className="text-[10px] uppercase font-bold text-slate-400 block">
                          Clue Emojis (Click below or type)
                        </label>
                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => {
                              const list = (newRiddleForm.emojis || '').trim().split(/\s+/).filter(Boolean);
                              list.pop();
                              setNewRiddleForm({ ...newRiddleForm, emojis: list.join(' ') });
                            }}
                            className="text-[10px] text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 font-bold px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700 cursor-pointer"
                            title="Remove last emoji"
                          >
                            ⌫ Backspace
                          </button>
                          <button
                            type="button"
                            onClick={() => setNewRiddleForm({ ...newRiddleForm, emojis: '' })}
                            className="text-[10px] text-rose-500 hover:underline font-bold cursor-pointer"
                          >
                            Clear All
                          </button>
                        </div>
                      </div>

                      <input
                        type="text"
                        value={newRiddleForm.emojis}
                        onChange={e => setNewRiddleForm({ ...newRiddleForm, emojis: e.target.value })}
                        placeholder="e.g. 🚀 🌕 🇮🇳 🛰️"
                        className="w-full px-3.5 py-2 rounded-xl border border-purple-300 dark:border-purple-600/50 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-lg tracking-widest font-mono focus:outline-none focus:ring-2 focus:ring-purple-400 shadow-inner"
                      />

                      {/* Interactive Emoji Palette */}
                      <div className="p-2.5 rounded-xl bg-purple-500/5 dark:bg-purple-900/10 border border-purple-200/60 dark:border-purple-800/40 space-y-2">
                        {/* Category switcher */}
                        <div className="flex items-center gap-1 text-[10px] font-bold overflow-x-auto pb-1 scrollbar-none">
                          {[
                            { id: 'space', label: '🚀 Space' },
                            { id: 'heritage', label: '🕌 Heritage' },
                            { id: 'animals', label: '🐅 Animals' },
                            { id: 'sports', label: '🏏 Sports' },
                            { id: 'mystery', label: '🧩 Mystery' }
                          ].map(cat => (
                            <button
                              key={cat.id}
                              type="button"
                              onClick={() => setActiveEmojiCategory(cat.id)}
                              className={`px-2 py-0.5 rounded-md transition-all cursor-pointer whitespace-nowrap ${
                                activeEmojiCategory === cat.id
                                  ? 'bg-purple-600 text-white shadow-xs font-black'
                                  : 'text-slate-600 dark:text-slate-400 hover:bg-purple-100 dark:hover:bg-slate-700'
                              }`}
                            >
                              {cat.label}
                            </button>
                          ))}
                        </div>

                        {/* Clickable Emoji Grid */}
                        <div className="flex flex-wrap gap-1.5 pt-1">
                          {(
                            activeEmojiCategory === 'heritage'
                              ? ['🇮🇳', '🕌', '🛕', '🏰', '👑', '🐘', '🐅', '🦚', '🪔', '🕉️', '🧘', '🏹', '📜', '🏛️']
                              : activeEmojiCategory === 'animals'
                              ? ['🦁', '🐯', '🐘', '🦏', '🐆', '🐼', '🦅', '🐬', '🐋', '🦈', '🦖', '🦕', '🌴', '🌸']
                              : activeEmojiCategory === 'sports'
                              ? ['🏏', '⚽', '🏸', '🎾', '🥊', '🏹', '🏆', '🥇', '🥈', '🥉', '🎯', '🛹', '🚴', '🧗']
                              : activeEmojiCategory === 'mystery'
                              ? ['🔍', '🕵️', '❓', '❗', '🔑', '🗝️', '💎', '🎁', '📦', '🗺️', '🧭', '⏳', '⏰', '🔔']
                              : ['🚀', '🌕', '🪐', '🛸', '🛰️', '🔭', '🌍', '☀️', '⭐', '🧬', '🔬', '🧪', '⚡', '💡']
                          ).map((em, eIdx) => (
                            <button
                              key={eIdx}
                              type="button"
                              onClick={() => {
                                const current = (newRiddleForm.emojis || '').trim();
                                setNewRiddleForm({
                                  ...newRiddleForm,
                                  emojis: current ? `${current} ${em}` : em
                                });
                              }}
                              className="w-8 h-8 rounded-lg bg-white dark:bg-slate-800 border border-purple-200 dark:border-purple-700 text-lg flex items-center justify-center hover:scale-125 hover:bg-purple-100 dark:hover:bg-purple-900/40 active:scale-95 transition-all cursor-pointer shadow-xs"
                              title={`Insert ${em}`}
                            >
                              {em}
                            </button>
                          ))}
                        </div>

                        <p className="text-[10px] text-slate-400 italic">
                          💡 Click any emoji above to insert. You can also paste custom emojis or press <kbd className="px-1 py-0.5 rounded bg-slate-200 dark:bg-slate-700 text-[9px] font-mono">Win + .</kbd> on your keyboard.
                        </p>
                      </div>
                    </div>

                    {/* Answer Title */}
                    <div className="sm:col-span-4">
                      <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">Correct Answer Name</label>
                      <input
                        type="text"
                        required
                        value={newRiddleForm.answer}
                        onChange={e => setNewRiddleForm({ ...newRiddleForm, answer: e.target.value })}
                        placeholder="e.g. Chandrayaan-3 Mission"
                        className="w-full px-3.5 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-xs font-bold focus:outline-none focus:ring-2 focus:ring-purple-400"
                      />
                    </div>

                    {/* Custom Image: URL or Local File Upload */}
                    <div className="sm:col-span-6">
                      <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">
                        Optional Image Clue (URL or Upload File)
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={newRiddleForm.image}
                          onChange={e => setNewRiddleForm({ ...newRiddleForm, image: e.target.value })}
                          placeholder="https://example.com/photo.jpg or upload below"
                          className="flex-1 px-3.5 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-xs focus:outline-none focus:ring-2 focus:ring-purple-400"
                        />
                        <label className="px-3 py-2 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 rounded-xl text-xs font-bold cursor-pointer flex items-center gap-1.5 text-slate-700 dark:text-slate-200">
                          <SafeIcon icon={FiImage} /> Upload
                          <input type="file" accept="image/*" onChange={handleRiddleImageFile} className="hidden" />
                        </label>
                      </div>
                      {newRiddleForm.image && (
                        <div className="mt-2 flex items-center gap-2">
                          <img src={newRiddleForm.image} alt="Preview" className="w-12 h-12 object-cover rounded-lg border border-purple-300" />
                          <button type="button" onClick={() => setNewRiddleForm({ ...newRiddleForm, image: '' })} className="text-[10px] text-red-500 font-bold hover:underline">
                            Remove Image
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Hint / Clue */}
                    <div className="sm:col-span-6">
                      <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">Teacher Hint / Stage Clue</label>
                      <input
                        type="text"
                        value={newRiddleForm.hint}
                        onChange={e => setNewRiddleForm({ ...newRiddleForm, hint: e.target.value })}
                        placeholder="e.g. India became the 1st nation to reach the lunar South Pole."
                        className="w-full px-3.5 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-xs focus:outline-none focus:ring-2 focus:ring-purple-400"
                      />
                    </div>

                    {/* 4 Multiple Choice Options */}
                    <div className="sm:col-span-12 pt-2 border-t border-slate-200 dark:border-slate-800">
                      <span className="text-[10px] uppercase font-bold text-slate-400 block mb-2">
                        Multiple Choice Answer Options (Students tap these to answer)
                      </span>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
                        <div>
                          <label className="text-[10px] font-bold text-slate-500 block mb-0.5">Option A</label>
                          <input
                            type="text"
                            required
                            value={newRiddleForm.optionA}
                            onChange={e => setNewRiddleForm({ ...newRiddleForm, optionA: e.target.value })}
                            placeholder="Option A text"
                            className="w-full px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs font-bold text-slate-900 dark:text-white"
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-bold text-slate-500 block mb-0.5">Option B</label>
                          <input
                            type="text"
                            required
                            value={newRiddleForm.optionB}
                            onChange={e => setNewRiddleForm({ ...newRiddleForm, optionB: e.target.value })}
                            placeholder="Option B text"
                            className="w-full px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs font-bold text-slate-900 dark:text-white"
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-bold text-slate-500 block mb-0.5">Option C</label>
                          <input
                            type="text"
                            value={newRiddleForm.optionC}
                            onChange={e => setNewRiddleForm({ ...newRiddleForm, optionC: e.target.value })}
                            placeholder="Option C text"
                            className="w-full px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs font-bold text-slate-900 dark:text-white"
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-bold text-slate-500 block mb-0.5">Option D</label>
                          <input
                            type="text"
                            value={newRiddleForm.optionD}
                            onChange={e => setNewRiddleForm({ ...newRiddleForm, optionD: e.target.value })}
                            placeholder="Option D text"
                            className="w-full px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs font-bold text-slate-900 dark:text-white"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Correct Option Selector & Fun Fact */}
                    <div className="sm:col-span-4">
                      <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">Which Option is Correct?</label>
                      <select
                        value={newRiddleForm.correctIndex}
                        onChange={e => setNewRiddleForm({ ...newRiddleForm, correctIndex: Number(e.target.value) })}
                        className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs font-black text-emerald-600 dark:text-emerald-400 focus:outline-none focus:ring-2 focus:ring-purple-400"
                      >
                        <option value={0}>Option A is Correct ✅</option>
                        <option value={1}>Option B is Correct ✅</option>
                        <option value={2}>Option C is Correct ✅</option>
                        <option value={3}>Option D is Correct ✅</option>
                      </select>
                    </div>

                    <div className="sm:col-span-5">
                      <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">Educational "Did You Know?" Fact</label>
                      <input
                        type="text"
                        value={newRiddleForm.funFact}
                        onChange={e => setNewRiddleForm({ ...newRiddleForm, funFact: e.target.value })}
                        placeholder="e.g. August 23 is now celebrated as National Space Day in India!"
                        className="w-full px-3.5 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-xs focus:outline-none focus:ring-2 focus:ring-purple-400"
                      />
                    </div>

                    <div className="sm:col-span-3 flex items-end">
                      <button
                        type="submit"
                        className="w-full py-2.5 px-4 rounded-xl bg-purple-600 hover:bg-purple-500 text-white font-bold text-xs flex items-center justify-center gap-1.5 shadow-md shadow-purple-500/20 transition-all cursor-pointer"
                      >
                        <SafeIcon icon={FiPlus} /> Add Emoji Riddle
                      </button>
                    </div>
                  </div>
                </form>

                {/* ACTIVE RIDDLES LIST */}
                <div className="space-y-3">
                  {(emojiRiddles || []).map((riddle, rIdx) => (
                    <div
                      key={riddle.id || rIdx}
                      className="p-4 rounded-xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700 flex flex-col md:flex-row md:items-center justify-between gap-4 group hover:border-purple-300 transition-colors"
                    >
                      <div className="flex items-start gap-3.5">
                        {riddle.image ? (
                          <img
                            src={riddle.image}
                            alt={riddle.answer}
                            className="w-14 h-14 object-cover rounded-xl border border-slate-200 shadow-xs flex-shrink-0"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <div className="w-14 h-14 rounded-xl bg-purple-500/15 text-purple-600 flex items-center justify-center text-2xl flex-shrink-0 shadow-xs">
                            🧩
                          </div>
                        )}

                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-0.5 rounded text-[10px] font-black uppercase bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-300">
                              {riddle.category}
                            </span>
                            <span className="text-sm tracking-widest font-black">
                              {(riddle.emojis || []).join(' ')}
                            </span>
                          </div>

                          <h5 className="font-extrabold text-sm text-slate-800 dark:text-white">
                            {rIdx + 1}. {riddle.answer}
                          </h5>

                          <div className="flex flex-wrap gap-1.5 pt-1">
                            {(riddle.options || []).map((opt, oIdx) => (
                              <span
                                key={oIdx}
                                className={`text-[10px] px-2 py-0.5 rounded-md ${
                                  oIdx === riddle.correctIndex
                                    ? 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 font-bold border border-emerald-300'
                                    : 'text-slate-500 bg-slate-200/50 dark:bg-slate-800'
                                }`}
                              >
                                {['A', 'B', 'C', 'D'][oIdx]}. {opt.text || opt}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleDeleteEmojiRiddle(riddle.id)}
                        className="p-2 text-slate-400 hover:text-red-500 transition-colors self-end md:self-center cursor-pointer"
                        title="Delete Riddle"
                      >
                        <SafeIcon icon={FiTrash2} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

            </motion.div>
          )}

          {/* TAB: BALLOON POP CARNIVAL ENTRIES (NEW) */}
          {activeTab === 'balloon_entries' && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="glass-panel rounded-2xl p-6 bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 flex flex-col h-[700px]">
              <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4 mb-6">
                <div>
                  <h2 className="text-xl font-semibold text-slate-800 dark:text-white flex items-center gap-2">
                    <span>🎈</span> Kids Balloon Pop Carnival Entries
                  </h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Total Participants: {balloonEntries.length}</p>
                </div>
                <button
                  type="button"
                  onClick={() => exportToCSV(balloonEntries, 'balloon_pop_entries')}
                  className="px-4 py-2 bg-pink-500 hover:bg-pink-600 text-white rounded-xl font-medium transition-colors text-xs flex items-center gap-2 self-start sm:self-auto shadow-sm"
                >
                  <SafeIcon icon={FiDownload} /> Export Balloon CSV
                </button>
              </div>

              <div className="flex-1 overflow-auto custom-scrollbar">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-slate-700 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      <th className="pb-3">Student Name</th>
                      <th className="pb-3">Parent Mobile</th>
                      <th className="pb-3">Class / Grade</th>
                      <th className="pb-3">Timestamp</th>
                      <th className="pb-3">Award / Badge Won</th>
                      <th className="pb-3">Claim Coupon</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50 text-sm">
                    {balloonEntries.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center py-8 text-slate-400 text-xs">
                          No student balloon pop entries yet. Launch the balloon carnival kiosk at <a href={`${baseUrl}#/balloon`} target="_blank" className="text-pink-500 underline font-bold">#/balloon</a>.
                        </td>
                      </tr>
                    ) : (
                      balloonEntries.map((e, idx) => (
                        <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-700/20 transition-colors">
                          <td className="py-3.5 font-bold text-slate-800 dark:text-white">{e.name}</td>
                          <td className="py-3.5 text-slate-600 dark:text-slate-300 font-mono text-xs">{e.mobile}</td>
                          <td className="py-3.5 text-slate-600 dark:text-slate-300">
                            <span className="text-xs font-mono bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded">
                              {e.receiptNumber || 'Student'}
                            </span>
                          </td>
                          <td className="py-3.5 text-slate-400 text-xs">{new Date(e.createdAt).toLocaleString()}</td>
                          <td className="py-3.5">
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 text-xs font-bold border border-amber-200 dark:border-amber-500/20">
                              {e.reward}
                            </span>
                          </td>
                          <td className="py-3.5">
                            <code className="text-xs font-mono font-bold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-500/10 px-2 py-1 rounded border border-indigo-200 dark:border-indigo-500/20">
                              {e.couponCode}
                            </code>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </motion.div>
          )}

          {/* TAB: MAGIC TREASURE BOX ENTRIES (NEW) */}
          {activeTab === 'treasure_entries' && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="glass-panel rounded-2xl p-6 bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 flex flex-col h-[700px]">
              <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4 mb-6">
                <div>
                  <h2 className="text-xl font-semibold text-slate-800 dark:text-white flex items-center gap-2">
                    <span>🎁</span> Magic Treasure Box Entries
                  </h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Total Participants: {treasureEntries.length}</p>
                </div>
                <button
                  type="button"
                  onClick={() => exportToCSV(treasureEntries, 'treasure_box_entries')}
                  className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold rounded-xl transition-colors text-xs flex items-center gap-2 self-start sm:self-auto shadow-sm cursor-pointer"
                >
                  <SafeIcon icon={FiDownload} /> Export Treasure CSV
                </button>
              </div>

              <div className="flex-1 overflow-auto custom-scrollbar">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-slate-700 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      <th className="pb-3">Student Name</th>
                      <th className="pb-3">Parent Mobile</th>
                      <th className="pb-3">Class / Grade</th>
                      <th className="pb-3">Timestamp</th>
                      <th className="pb-3">Award / Badge Won</th>
                      <th className="pb-3">Voucher Code</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50 text-sm">
                    {treasureEntries.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center py-8 text-slate-400 text-xs">
                          No treasure box entries yet. Launch the treasure kiosk at <a href={`${baseUrl}#/treasure`} target="_blank" className="text-amber-500 underline font-bold">#/treasure</a>.
                        </td>
                      </tr>
                    ) : (
                      treasureEntries.map((e, idx) => (
                        <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-700/20 transition-colors">
                          <td className="py-3.5 font-bold text-slate-800 dark:text-white">{e.name}</td>
                          <td className="py-3.5 text-slate-600 dark:text-slate-300 font-mono text-xs">{e.mobile}</td>
                          <td className="py-3.5 text-slate-600 dark:text-slate-300">
                            <span className="text-xs font-mono bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded">
                              {e.receiptNumber || 'Student'}
                            </span>
                          </td>
                          <td className="py-3.5 text-slate-400 text-xs">{new Date(e.createdAt).toLocaleString()}</td>
                          <td className="py-3.5">
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 text-xs font-bold border border-amber-200 dark:border-amber-500/20">
                              {e.reward}
                            </span>
                          </td>
                          <td className="py-3.5">
                            <code className="text-xs font-mono font-bold text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/10 px-2 py-1 rounded border border-amber-200 dark:border-amber-500/20">
                              {e.couponCode}
                            </code>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </motion.div>
          )}

          {/* TAB: CANDY PIÑATA BASH ENTRIES */}
          {activeTab === 'pinata_entries' && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="glass-panel rounded-2xl p-6 bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 flex flex-col h-[700px]">
              <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4 mb-6">
                <div>
                  <h2 className="text-xl font-semibold text-slate-800 dark:text-white flex items-center gap-2">
                    <span>🪅</span> Candy Piñata Bash Entries
                  </h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Total Participants: {pinataEntries.length}</p>
                </div>
                <button
                  type="button"
                  onClick={() => exportToCSV(pinataEntries, 'candy_pinata_entries')}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-xl transition-colors text-xs flex items-center gap-2 self-start sm:self-auto shadow-sm cursor-pointer"
                >
                  <SafeIcon icon={FiDownload} /> Export Piñata CSV
                </button>
              </div>

              <div className="flex-1 overflow-auto custom-scrollbar">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-slate-700 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      <th className="pb-3">Student Name</th>
                      <th className="pb-3">Parent Phone</th>
                      <th className="pb-3">Class / Grade</th>
                      <th className="pb-3">Timestamp</th>
                      <th className="pb-3">Prize Won</th>
                      <th className="pb-3">Voucher Code</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50 text-sm">
                    {pinataEntries.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center py-8 text-slate-400 text-xs">
                          No Piñata entries yet. Launch the kiosk at <a href={`${baseUrl}#/pinata`} target="_blank" className="text-purple-500 underline font-bold">#/pinata</a>.
                        </td>
                      </tr>
                    ) : (
                      pinataEntries.map((e, idx) => (
                        <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-700/20 transition-colors">
                          <td className="py-3.5 font-bold text-slate-800 dark:text-white">{e.name}</td>
                          <td className="py-3.5 text-slate-600 dark:text-slate-300 font-mono text-xs">{e.mobile}</td>
                          <td className="py-3.5 text-slate-600 dark:text-slate-300">
                            <span className="text-xs font-mono bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded">
                              {e.classGrade || 'Junior'}
                            </span>
                          </td>
                          <td className="py-3.5 text-slate-400 text-xs">{new Date(e.createdAt).toLocaleString()}</td>
                          <td className="py-3.5">
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-purple-50 dark:bg-purple-500/10 text-purple-700 dark:text-purple-400 text-xs font-bold border border-purple-200 dark:border-purple-500/20">
                              {e.reward}
                            </span>
                          </td>
                          <td className="py-3.5">
                            <code className="text-xs font-mono font-bold text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-500/10 px-2 py-1 rounded border border-purple-200 dark:border-purple-500/20">
                              {e.couponCode}
                            </code>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </motion.div>
          )}

          {/* TAB: JUNIOR CHAMPS ENTRIES (AGES 3-7) */}
          {activeTab === 'junior_entries' && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="glass-panel rounded-2xl p-6 bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 flex flex-col h-[700px]">
              <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4 mb-6">
                <div>
                  <h2 className="text-xl font-semibold text-slate-800 dark:text-white flex items-center gap-2">
                    <span>🧸</span> Junior Champs Entries (Ages 3-7)
                  </h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Total Participants: {juniorEntries.length} (Safari Match & Rainbow Bubble Pop)</p>
                </div>
                <button
                  type="button"
                  onClick={() => exportToCSV(juniorEntries, 'junior_champs_entries')}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl transition-colors text-xs flex items-center gap-2 self-start sm:self-auto shadow-sm cursor-pointer"
                >
                  <SafeIcon icon={FiDownload} /> Export Junior CSV
                </button>
              </div>

              <div className="flex-1 overflow-auto custom-scrollbar">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-slate-700 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      <th className="pb-3">Little Champ</th>
                      <th className="pb-3">Game Type</th>
                      <th className="pb-3">Age / Category</th>
                      <th className="pb-3">Timestamp</th>
                      <th className="pb-3">Award Won</th>
                      <th className="pb-3">Gift Pass Code</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50 text-sm">
                    {juniorEntries.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center py-8 text-slate-400 text-xs">
                          No junior entries yet. Try <a href={`${baseUrl}#/animal-match`} target="_blank" className="text-emerald-500 underline font-bold">#/animal-match</a> or <a href={`${baseUrl}#/rainbow-pop`} target="_blank" className="text-sky-500 underline font-bold">#/rainbow-pop</a>.
                        </td>
                      </tr>
                    ) : (
                      juniorEntries.map((e, idx) => (
                        <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-700/20 transition-colors">
                          <td className="py-3.5 font-bold text-slate-800 dark:text-white">{e.name}</td>
                          <td className="py-3.5">
                            <span className="text-xs px-2 py-0.5 rounded-full font-bold bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                              {e.gameType}
                            </span>
                          </td>
                          <td className="py-3.5 text-slate-500 text-xs">{e.mobile || 'Ages 3-7'}</td>
                          <td className="py-3.5 text-slate-400 text-xs">{new Date(e.createdAt).toLocaleString()}</td>
                          <td className="py-3.5">
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 text-xs font-bold border border-emerald-200 dark:border-emerald-500/20">
                              {e.reward}
                            </span>
                          </td>
                          <td className="py-3.5">
                            <code className="text-xs font-mono font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/10 px-2 py-1 rounded border border-emerald-200 dark:border-emerald-500/20">
                              {e.couponCode}
                            </code>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </motion.div>
          )}

          {/* TAB: SPACE & STEM LAB ENTRIES */}
          {activeTab === 'stem_entries' && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="glass-panel rounded-2xl p-6 bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 flex flex-col h-[700px]">
              <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4 mb-6">
                <div>
                  <h2 className="text-xl font-semibold text-slate-800 dark:text-white flex items-center gap-2">
                    <span>🚀</span> Space & STEM Lab Entries
                  </h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Total Participants: {stemEntries.length} (Rocket Builder, Gravity Lab & Color Chemistry)</p>
                </div>
                <button
                  type="button"
                  onClick={() => exportToCSV(stemEntries, 'stem_lab_entries')}
                  className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-slate-950 font-bold rounded-xl transition-colors text-xs flex items-center gap-2 self-start sm:self-auto shadow-sm cursor-pointer"
                >
                  <SafeIcon icon={FiDownload} /> Export STEM CSV
                </button>
              </div>

              <div className="flex-1 overflow-auto custom-scrollbar">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-slate-700 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      <th className="pb-3">Student Cadet</th>
                      <th className="pb-3">STEM Domain</th>
                      <th className="pb-3">Class / Level</th>
                      <th className="pb-3">Timestamp</th>
                      <th className="pb-3">Science Award Won</th>
                      <th className="pb-3">Certificate Pass</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50 text-sm">
                    {stemEntries.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center py-8 text-slate-400 text-xs">
                          No STEM entries yet. Try launching <a href={`${baseUrl}#/rocket-builder`} target="_blank" className="text-cyan-500 underline font-bold">#/rocket-builder</a> or <a href={`${baseUrl}#/gravity-lab`} target="_blank" className="text-amber-500 underline font-bold">#/gravity-lab</a>.
                        </td>
                      </tr>
                    ) : (
                      stemEntries.map((e, idx) => (
                        <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-700/20 transition-colors">
                          <td className="py-3.5 font-bold text-slate-800 dark:text-white">{e.name}</td>
                          <td className="py-3.5">
                            <span className="text-xs px-2 py-0.5 rounded-full font-bold bg-slate-100 dark:bg-slate-800 text-cyan-700 dark:text-cyan-300">
                              {e.gameType}
                            </span>
                          </td>
                          <td className="py-3.5 text-slate-500 text-xs">{e.classGrade || 'STEM Cadet'}</td>
                          <td className="py-3.5 text-slate-400 text-xs">{new Date(e.createdAt).toLocaleString()}</td>
                          <td className="py-3.5">
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-cyan-50 dark:bg-cyan-500/10 text-cyan-700 dark:text-cyan-400 text-xs font-bold border border-cyan-200 dark:border-cyan-500/20">
                              {e.reward}
                            </span>
                          </td>
                          <td className="py-3.5">
                            <code className="text-xs font-mono font-bold text-cyan-600 dark:text-cyan-400 bg-cyan-50 dark:bg-cyan-500/10 px-2 py-1 rounded border border-cyan-200 dark:border-cyan-500/20">
                              {e.couponCode}
                            </code>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </motion.div>
          )}

        </div>
      </div>

      {/* Interactive Game Engine Modal Preview */}
      <AnimatePresence>
        {previewGameMode && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md"
          >
            <motion.div 
              initial={{ scale: 0.95, y: 15 }} 
              animate={{ scale: 1, y: 0 }} 
              exit={{ scale: 0.95, y: 15 }}
              className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-3xl p-5 sm:p-6 max-w-lg w-full shadow-2xl relative text-center max-h-[92vh] flex flex-col"
            >
              <button 
                type="button"
                onClick={() => setPreviewGameMode(null)} 
                className="absolute top-4 right-4 w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-300 flex items-center justify-center transition-colors z-20"
              >
                <SafeIcon icon={FiX} />
              </button>

              <div className="shrink-0 mb-3 pr-8">
                <h3 className="text-base sm:text-lg font-bold text-slate-900 dark:text-white capitalize flex items-center justify-center gap-2">
                  <SafeIcon icon={FiEye} className="text-indigo-500" />
                  {previewGameMode === 'scratch' && 'Scratch & Win Engine Preview'}
                  {previewGameMode === 'spin' && 'Spin the Wheel Engine Preview'}
                  {previewGameMode === 'peel' && 'Peel & Reveal Engine Preview'}
                  {previewGameMode === 'slot' && 'Slot Machine Engine Preview'}
                  {previewGameMode === 'flip' && 'Mystery Flip Card Engine Preview'}
                </h3>
                <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                  Interactive preview powered by the live <strong className="text-indigo-600 dark:text-indigo-400">Dynamic Reward Pool</strong>.
                </p>
              </div>

              {/* Dynamic Reward Pool Control Bar for Preview Testing */}
              <div className="shrink-0 mb-3 p-2.5 rounded-2xl bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700/80 text-left">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1.5 mb-2">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-ping"></span>
                    <span className="text-[10px] font-bold text-slate-700 dark:text-slate-200 uppercase tracking-wider">
                      Dynamic Pool: {settings?.prizes?.length || 0} Prizes Active
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const activePrizes = settings?.prizes?.length 
                        ? settings.prizes 
                        : ["₹50 Discount", "Free Premium Gift", "Better Luck Next Time", "Free Product Sample", "10% Cashback"];
                      const newP = activePrizes[Math.floor(Math.random() * activePrizes.length)];
                      setPreviewSelectedPrize(newP);
                      setPreviewResult('');
                      setPreviewResetKey(Date.now());
                    }}
                    className="text-[10px] font-bold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 flex items-center gap-1 self-start sm:self-auto"
                  >
                    🎲 Re-Roll Random Test Prize
                  </button>
                </div>

                <div className="flex flex-col sm:flex-row items-center gap-2">
                  <span className="text-[10px] text-slate-500 shrink-0 font-medium">Test Specific Prize:</span>
                  <select
                    value={previewSelectedPrize}
                    onChange={(e) => {
                      setPreviewSelectedPrize(e.target.value);
                      setPreviewResult('');
                      setPreviewResetKey(Date.now());
                    }}
                    className="flex-1 w-full text-xs bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-1 text-slate-800 dark:text-slate-200 outline-none focus:ring-1 focus:ring-indigo-500"
                  >
                    {(settings?.prizes?.length ? settings.prizes : ["₹50 Discount", "Free Premium Gift", "Better Luck Next Time"]).map((pz, idx) => (
                      <option key={idx} value={pz}>
                        {pz} ({((1 / (settings?.prizes?.length || 1)) * 100).toFixed(0)}% chance)
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              {/* Scrollable Game Canvas Container */}
              <div className="flex-1 overflow-y-auto overflow-x-hidden py-1 px-1 flex flex-col justify-center items-center min-h-0">
                {previewGameMode === 'scratch' && (
                  <ScratchToRevealCard 
                    key={previewResetKey}
                    compact={true}
                    reward={previewSelectedPrize || settings?.prizes?.[0] || "₹50 Discount"} 
                    onRevealed={() => setPreviewResult(`🎉 Card scratched! Dynamic Prize Revealed: ${previewSelectedPrize || settings?.prizes?.[0]}`)} 
                  />
                )}
                {previewGameMode === 'spin' && (
                  <SpinWheel 
                    key={previewResetKey}
                    prizes={settings?.prizes?.length ? settings.prizes : ["₹50 Discount", "Free Gift", "Try Again", "Sample", "10% Cashback"]} 
                    onFinished={(p) => setPreviewResult(`🎉 Wheel landed on: ${p}`)} 
                  />
                )}
                {previewGameMode === 'peel' && (
                  <PeelToRevealCard 
                    key={previewResetKey}
                    compact={true}
                    reward={previewSelectedPrize || settings?.prizes?.[0] || "₹50 Discount"} 
                    onRevealed={() => setPreviewResult(`🎉 Sticker peeled! Dynamic Prize Revealed: ${previewSelectedPrize || settings?.prizes?.[0]}`)} 
                  />
                )}
                {previewGameMode === 'slot' && (
                  <SlotMachine 
                    key={previewResetKey}
                    compact={true}
                    prizes={settings?.prizes?.length ? settings.prizes : ["₹50 Discount", "Free Gift", "Try Again", "Sample", "10% Cashback"]} 
                    targetPrize={previewSelectedPrize || settings?.prizes?.[0] || "₹50 Discount"} 
                    onFinished={(p) => setPreviewResult(`🎉 Jackpot reels locked! Won: ${p}`)} 
                  />
                )}
                {previewGameMode === 'flip' && (
                  <FlipToRevealCard 
                    key={previewResetKey}
                    compact={true}
                    reward={previewSelectedPrize || settings?.prizes?.[0] || "₹50 Discount"} 
                    onRevealed={() => setPreviewResult(`🎉 Card flipped! Dynamic Prize Revealed: ${previewSelectedPrize || settings?.prizes?.[0]}`)} 
                  />
                )}

                {previewResult && (
                  <motion.div 
                    initial={{ opacity: 0, y: 6, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    className="mt-3 px-3 py-2 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-300 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-400 font-bold text-xs flex items-center justify-center gap-1.5 shadow-sm w-full max-w-sm"
                  >
                    <SafeIcon icon={FiAward} className="text-sm text-emerald-500" />
                    <span>{previewResult}</span>
                  </motion.div>
                )}
              </div>
              
              {/* Pinned Modal Footer */}
              <div className="shrink-0 mt-3 pt-2.5 border-t border-slate-100 dark:border-slate-800 flex justify-end">
                <button 
                  type="button"
                  onClick={() => {
                    setPreviewGameMode(null);
                    setPreviewResult('');
                  }} 
                  className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold transition-colors shadow-sm"
                >
                  Close Preview
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Quiz & Science Question Manager Modal (Excel & CSV Upload / Sample Template) */}
      <QuizQuestionManagerModal
        isOpen={showAddQuizModal}
        initialTab={quizModalTab}
        onClose={() => setShowAddQuizModal(false)}
        onQuestionsUpdated={() => setQuizQuestions(getQuizQuestions())}
      />

      <style dangerouslySetInnerHTML={{__html: `
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: rgba(30, 41, 59, 0.05); border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(71, 85, 105, 0.3); border-radius: 10px; }
        .dark .custom-scrollbar::-webkit-scrollbar-track { background: rgba(30, 41, 59, 0.5); }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(71, 85, 105, 0.8); }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(100, 116, 139, 1); }
      `}} />
    </div>
  );
};

export default AdminDashboard;